/** Unique identifiers (UUIDv4 strings). */
export type Id = string;

export type UserRole = "author" | "editor" | "producer" | "admin";

/**
 * How an episode is presented in the UI.
 * - timing: scene list with individual + total durations
 * - outline: scene titles + descriptions (no dialogue/script)
 * - script: two-column manuscript (billedside + lydside)
 * - crew: per-scene credits / ophavsmænd
 * - programtext: named TV-guide / listing texts for the episode
 * - sources: manuscript + a third notes column (Noter / faktatjek / kilder)
 * - status: scene production status checklist
 * - home: legacy alias for status
 */
export type EpisodeView =
  | "home"
  | "timing"
  | "outline"
  | "script"
  | "crew"
  | "programtext"
  | "sources"
  | "status";

/** Columns in the script manuscript (not used in outline/timing). */
export type ScriptColumn = "visual" | "audio";

/** Default job titles offered when assigning scene crew credits. */
export const DEFAULT_CREW_JOB_TITLES: readonly string[] = [
  "Foto",
  "Lyd",
  "Lys",
  "Klipper",
  "Redaktionschef",
  "Producent",
  "Tilrettelæggelse",
  "Produktion",
] as const;

/** Default production-status steps shown on the status bar. */
export const DEFAULT_STATUS_STEPS: readonly { label: string }[] = [
  { label: "Research" },
  { label: "Manus" },
  { label: "Optagelse" },
  { label: "Nedklip" },
  { label: "Sceneklip" },
  { label: "Internt gennemsyn" },
  { label: "Rettelser" },
  { label: "Eksternt gennemsyn" },
  { label: "Rettelser" },
  { label: "Lyd" },
  { label: "Grade" },
] as const;

/** Previous built-in status list; migrated on load when a production still has it. */
const LEGACY_DEFAULT_STATUS_STEP_LABELS = [
  "Research",
  "Manus",
  "Optagelse",
  "Nedklip",
  "Postmanus",
  "Internt gennemsyn",
  "Eksternt gennemsyn",
  "Lyd",
  "Grade",
] as const;

export interface LockState {
  lockOwnerId: Id | null;
  lockOwnerName: string | null;
  lockExpiresAt: string | null;
}

/** One side of a linked cue row. Content may be empty. */
export interface ColumnContent extends LockState {
  content: string;
  revision: number;
  /**
   * Cast / character attribution for this cell:
   * dialogue on audio, or who performs the action on visual.
   */
  characterName?: string;
}

/**
 * Linked manuscript row: billedside + lydside.
 * Default: one visual cell per audio cell (linje-for-linje).
 * Override: visualRowSpan > 1 lets billedside cover several following audio rows.
 * Override: audioRowSpan > 1 lets lydside cover several following visual rows.
 * A cue should not have both spans > 1 at once.
 */
export interface Cue {
  id: Id;
  sceneId: Id;
  sortOrder: number;
  /** Billedside */
  visual: ColumnContent;
  /** Lydside */
  audio: ColumnContent;
  /**
   * How many cue rows this visual covers (1 = normal 1:1 pair).
   * Following cues in the span are audio-only in the script view.
   */
  visualRowSpan: number;
  /**
   * How many cue rows this audio covers (1 = normal 1:1 pair).
   * Following cues in the span are visual-only in the script view.
   */
  audioRowSpan: number;
  /**
   * When true, a horizontal rule is printed below this cue's row.
   * Default false — no line between every row on print.
   */
  printBorderBelow?: boolean;
  /**
   * Fact-check / source notes for this cue (Kilder/faktatjek view).
   * Plain text; URLs are linkified in the UI.
   */
  sourceNote?: string;
  /**
   * Named note-column texts for this cue (Noter tab).
   * Keys are `NoteColumnDef.id`. Legacy `sourceNote` is the first column.
   */
  notes?: Record<Id, string>;
  /**
   * Camera assignment for this setup (KAM 1, KAM 2, …).
   * Shown when camera-notes display is enabled.
   */
  cameraNumber?: number;
  /**
   * Optional note on framing / movement / zoom for this camera setup.
   */
  cameraNote?: string;
  /**
   * Optional position note for this setup (shown in column 2 when enabled).
   */
  positionNote?: string;
  updatedAt: string;
  updatedById?: Id;
}

/** One credited person under a job title on a scene. */
export interface SceneCrewPerson {
  id: Id;
  name: string;
  /** Optional detail (e.g. track title under Musikcredits). */
  detail?: string;
}

/** Crew / credits line on a scene (one job title, one or more people). */
export interface SceneCrewRole {
  id: Id;
  jobTitle: string;
  people: SceneCrewPerson[];
  sortOrder: number;
}

/** Configurable status-bar step for the production. */
export interface StatusStepDef {
  id: Id;
  label: string;
  sortOrder: number;
}

/** Named third-column heading on the Noter tab (production-wide). */
export interface NoteColumnDef {
  id: Id;
  title: string;
  sortOrder: number;
}

export interface Scene {
  id: Id;
  episodeId: Id;
  number: ItemNumber;
  title: string;
  description: string;
  durationSeconds: number;
  sortOrder: number;
  cueIds: Id[];
  /** Background color for the scene heading row in script view (CSS color). */
  headerBackground?: string;
  /**
   * Optional scene heading parts, shown beside the scene number.
   * Joined with " - " for display (e.g. "INT - KØKKEN - DAG - note").
   * `heading` is the cached joined string for print/export/lists.
   */
  heading?: string;
  headingKind?: "int" | "ext";
  headingLocation?: string;
  headingTime?: string;
  headingNote?: string;
  /** Ophavsmænd / crew credits for this scene. */
  crewRoles?: SceneCrewRole[];
  /**
   * Optional per-scene status steps. When omitted, the production-wide
   * `statusSteps` list is used.
   */
  statusSteps?: StatusStepDef[];
  /** Ids of status steps marked done for this scene. */
  statusCompletedIds?: Id[];
  /**
   * When set, this scene is an archived snapshot of the live scene with
   * this id (lives under the archive season).
   */
  archiveOfSceneId?: Id;
  /** ISO timestamp when the archive snapshot was created. */
  archivedAt?: string;
  /**
   * Optional display name for this archive occurrence (not the scene title).
   * Cleared on restore; `title` stays the real scene name.
   */
  archiveLabel?: string;
  /**
   * When set, this scene is a submitted (afleveret) snapshot of the live
   * program scene with this id (lives under the submitted season).
   */
  submittedOfSceneId?: Id;
  /** ISO timestamp when the scene was submitted. */
  submittedAt?: string;
  /** User id of the submitter (tilrettelægger). */
  submittedByUserId?: Id;
  /** Display name of the submitter at submit time. */
  submittedByName?: string;
  /**
   * ISO timestamp when the scene entered papirkurven.
   * Used for the {@link VAULT_RETENTION_DAYS}-day auto-delete.
   */
  trashedAt?: string;
  /**
   * Scene-level notes for the Noter tab (one text per note column).
   * Keys are `NoteColumnDef.id`. Shown above the cue rows for the active column.
   */
  notes?: Record<Id, string>;
  updatedAt: string;
}

/** Named cast member for the production (medvirkende). */
export interface CastMember {
  id: Id;
  name: string;
  /** Optional text color for this character's dialogue. */
  color?: string;
  /** When true, show the name above each of their lines. Default true. */
  showNameAboveLines: boolean;
  sortOrder: number;
}

/** Sequential 1…n, or user-owned labels (letters and dots allowed). */
export type SceneNumberingMode = "sequential" | "custom";

/** Scene/episode number: sequential "1" or custom "1a" / "2.1". */
export type ItemNumber = string | number;

const ITEM_NUMBER_RE =
  /^[0-9A-Za-z]+(?:\.[0-9A-Za-z]+)*(?: \/ [0-9A-Za-z]+(?:\.[0-9A-Za-z]+)*)*$/;

export function displayItemNumber(n: ItemNumber | null | undefined): string {
  return n == null ? "" : String(n);
}

/** Accept digits, letters, dots and slash (e.g. 12, 1a, 2.1, 3 / 4). */
export function parseItemNumber(raw: string | number): string | null {
  const t = String(raw).trim().replace(/\s*\/\s*/g, " / ");
  if (!ITEM_NUMBER_RE.test(t)) return null;
  return t;
}

/** Join two scene titles or custom numbers with " / ". */
export function joinMergedLabels(
  first: string | number,
  second: string | number,
  empty = "",
): string {
  const a = String(first).trim() || empty;
  const b = String(second).trim() || empty;
  if (a && b) return `${a} / ${b}`;
  return a || b;
}

export function itemNumberFollowsIndex(
  n: ItemNumber | null | undefined,
  index: number,
): boolean {
  return String(n ?? "") === String(index + 1);
}

export function nextNumericItemNumber(
  values: Array<ItemNumber | null | undefined>,
): number {
  let max = 0;
  for (const v of values) {
    if (v == null) continue;
    const s = String(v);
    if (/^\d+$/.test(s)) max = Math.max(max, Number(s));
  }
  return max + 1;
}

export function formatEpisodeDisplayTitle(
  episode: Pick<Episode, "number" | "title">,
  showNumber: boolean,
): string {
  const title = episode.title.trim() || "Unavngiven episode";
  if (!showNumber) return title;
  return `Episode ${episode.number}: ${title}`;
}

/** Named listing / TV-guide text belonging to an episode. */
export interface ProgramText {
  id: Id;
  title: string;
  body: string;
  sortOrder: number;
}

export interface Episode {
  id: Id;
  productionId: Id;
  seasonId: Id | null;
  number: ItemNumber;
  title: string;
  sortOrder: number;
  sceneIds: Id[];
  /**
   * How scene numbers behave for this episode.
   * - sequential: 1…n by order (user may renumber on move)
   * - custom: numbers stick to scenes; user edits them manually
   * Default when missing: sequential.
   */
  sceneNumberingMode?: SceneNumberingMode;
  /** Credits that apply to the whole episode (not a single scene). */
  crewRoles?: SceneCrewRole[];
  /** Named program texts (programtekster) for listings and press. */
  programTexts?: ProgramText[];
  /**
   * Production note-column ids hidden for this episode only
   * («Fjern kolonne» → kun denne episode).
   */
  hiddenNoteColumnIds?: Id[];
  /**
   * Checklist items completed for the whole episode (not per scene),
   * e.g. Lyd / Grade. Empty/missing → episode % is scene points only.
   */
  statusSteps?: StatusStepDef[];
  /** Ids of episode-level status steps marked done. */
  statusCompletedIds?: Id[];
  /**
   * For trash/archive shells: the live episode this shell mirrors.
   * When a whole episode is moved to trash, equals this episode’s own id.
   */
  originEpisodeId?: Id | null;
  /** Title snapshot of the origin episode (if later renamed/purged). */
  originEpisodeTitle?: string;
  updatedAt: string;
}

/** Normal seasons vs hidden papirkurv / arkiv / afleverede containers. */
export type SeasonRole = "normal" | "trash" | "archive" | "submitted";

export interface Season {
  id: Id;
  productionId: Id;
  number: number;
  title: string;
  sortOrder: number;
  episodeIds: Id[];
  /**
   * Credits that apply to every episode in the season (“hele sæsonen”).
   * Mirrored onto each episode’s `crewRoles` when set via setCrewPersonOnSeason.
   */
  crewRoles?: SceneCrewRole[];
  /** Hidden containers are never listed as real seasons. Default: normal. */
  role?: SeasonRole;
  updatedAt: string;
}

export interface Production {
  id: Id;
  title: string;
  createdAt: string;
  updatedAt: string;
  seasonIds: Id[];
  episodeIds: Id[];
  /**
   * How episode numbers behave for this production.
   * - sequential: 1…n by order within each season (or the production list)
   * - custom: numbers stick to episodes; user edits them manually
   * Default when missing: sequential.
   */
  episodeNumberingMode?: SceneNumberingMode;
  /**
   * When true, the UI offers seasons under this production.
   * When false, treat as a single (implicit) season unless the production
   * already has non-default season structure.
   */
  useSeasons?: boolean;
  /** Cast / medvirkende for the production. */
  castMembers: CastMember[];
  /**
   * Ordered status-bar steps for Status view (shared across scenes).
   * When missing, DEFAULT_STATUS_STEPS are applied on normalize.
   */
  statusSteps?: StatusStepDef[];
  /**
   * Extra crew job titles added on this production. Shown together with
   * DEFAULT_CREW_JOB_TITLES when assigning credits.
   */
  crewJobTitles?: string[];
  /**
   * Credits that apply to every episode when seasons are not in use
   * (implicit “sæson 1”). Mirrored onto each episode’s `crewRoles` via
   * setCrewPersonOnEpisodePeers.
   */
  crewRoles?: SceneCrewRole[];
  /**
   * Headings for the third column on the Noter tab.
   * Switch between several (Noter, Faktatjek, Kilder, …).
   */
  noteColumns?: NoteColumnDef[];
  /** Hidden papirkurv season (not in seasonIds). */
  trashSeasonId?: Id | null;
  /** Hidden arkiv season (not in seasonIds). */
  archiveSeasonId?: Id | null;
  /** Hidden afleverede-scener season (not in seasonIds). */
  submittedSeasonId?: Id | null;
}

export interface ScriptDocument {
  production: Production;
  seasons: Record<Id, Season>;
  episodes: Record<Id, Episode>;
  scenes: Record<Id, Scene>;
  cues: Record<Id, Cue>;
  /** Manuscript comments (cell- or word-level). */
  comments: Record<Id, Comment>;
  /**
   * Cue ids removed on purpose (`deletedAt` ISO). Survives sync/merge so a
   * stale snapshot or concurrent upsert cannot resurrect the row. Cleared
   * when a new cue reuses the id (should not happen) or on undo to a
   * pre-delete snapshot.
   */
  deletedCueIds?: Record<Id, string>;
}

export interface PresenceUser {
  userId: Id;
  displayName: string;
  color: string;
  episodeId?: Id;
  sceneId?: Id;
  cueId?: Id;
  column?: ScriptColumn;
  lastSeenAt: string;
}

export interface Comment {
  id: Id;
  cueId: Id;
  /** Which column the comment targets. Required for new comments. */
  column?: ScriptColumn;
  /**
   * Character offsets in the cell's plain text (same as TipTap getText
   * with newline block separators). When omitted, the comment is cell-level.
   */
  from?: number;
  to?: number;
  /** Snapshot of the commented span when created. */
  quote?: string;
  authorId: Id;
  authorName: string;
  body: string;
  createdAt: string;
  resolved: boolean;
}

export const LOCK_TTL_MS = 30_000;
export const LOCK_HEARTBEAT_MS = 10_000;

export function createId(): Id {
  return crypto.randomUUID();
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function emptyLock(): LockState {
  return {
    lockOwnerId: null,
    lockOwnerName: null,
    lockExpiresAt: null,
  };
}

export function emptyColumn(characterName?: string): ColumnContent {
  return {
    content: "",
    revision: 1,
    characterName,
    ...emptyLock(),
  };
}

export function getCueSide(cue: Cue, column: ScriptColumn): ColumnContent {
  return column === "visual" ? cue.visual : cue.audio;
}

export function isLockHeldBy(
  side: ColumnContent,
  userId: Id,
  at = Date.now(),
): boolean {
  if (!side.lockOwnerId || !side.lockExpiresAt) return false;
  if (side.lockOwnerId !== userId) return false;
  return new Date(side.lockExpiresAt).getTime() > at;
}

export function isLockAvailable(
  side: ColumnContent,
  userId: Id,
  at = Date.now(),
): boolean {
  if (!side.lockOwnerId || !side.lockExpiresAt) return true;
  if (new Date(side.lockExpiresAt).getTime() <= at) return true;
  return side.lockOwnerId === userId;
}

export function formatDuration(
  totalSeconds: number,
  opts: {
    showHours?: boolean;
    showFrames?: boolean;
    fps?: number;
  } = {},
): string {
  const fps = Math.max(1, opts.fps ?? 25);
  const showFrames = Boolean(opts.showFrames);
  const total = Math.max(0, totalSeconds);
  const whole = Math.floor(total);
  const frames = showFrames
    ? Math.min(fps - 1, Math.round((total - whole) * fps))
    : 0;
  const s = whole;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const framePart = showFrames
    ? `:${String(frames).padStart(2, "0")}`
    : "";

  if (opts.showHours === false) {
    const totalMin = Math.floor(s / 60);
    return `${totalMin}:${String(sec).padStart(2, "0")}${framePart}`;
  }
  if (opts.showHours === true || h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}${framePart}`;
  }
  return `${m}:${String(sec).padStart(2, "0")}${framePart}`;
}

export type DurationParts = {
  hours: number;
  minutes: number;
  seconds: number;
  frames: number;
};

export function splitDuration(
  totalSeconds: number,
  fps = 25,
): DurationParts {
  const rate = Math.max(1, fps);
  const total = Math.max(0, totalSeconds);
  const whole = Math.floor(total + 1e-9);
  const frac = total - whole;
  const frames = Math.min(rate - 1, Math.max(0, Math.round(frac * rate)));
  return {
    hours: Math.floor(whole / 3600),
    minutes: Math.floor((whole % 3600) / 60),
    seconds: whole % 60,
    frames,
  };
}

/** Build duration in seconds (fractional when frames are used). */
export function joinDuration(
  parts: DurationParts,
  fps = 25,
  includeFrames = false,
): number {
  const rate = Math.max(1, fps);
  const h = Math.max(0, Math.floor(parts.hours) || 0);
  const m = Math.max(0, Math.floor(parts.minutes) || 0);
  const s = Math.max(0, Math.floor(parts.seconds) || 0);
  const f = includeFrames
    ? Math.max(0, Math.min(rate - 1, Math.floor(parts.frames) || 0))
    : 0;
  return h * 3600 + m * 60 + s + f / rate;
}

export function totalSceneDuration(scenes: Scene[]): number {
  return scenes.reduce((sum, scene) => sum + (scene.durationSeconds || 0), 0);
}

export function getEpisodeScenes(
  doc: ScriptDocument,
  episodeId: Id,
): Scene[] {
  const episode = doc.episodes[episodeId];
  if (!episode) return [];
  const inVault = !isNormalEpisode(doc, episode);
  // Require scene.episodeId to match — hides half-moved trash zombies that
  // still linger in a live episode.sceneIds after a bad sync merge.
  // Vault shells (papirkurv/arkiv) must still list scenes with trashedAt.
  return episode.sceneIds
    .map((id) => doc.scenes[id])
    .filter((s): s is Scene => {
      if (!s || s.episodeId !== episodeId) return false;
      if (!inVault && s.trashedAt) return false;
      return true;
    });
}

export function getSceneCues(doc: ScriptDocument, sceneId: Id): Cue[] {
  const scene = doc.scenes[sceneId];
  if (!scene) return [];
  return scene.cueIds
    .map((id) => doc.cues[id])
    .filter((c): c is Cue => Boolean(c) && c.sceneId === sceneId);
}

/** Merge deletion tombstones; keep the latest `deletedAt` per cue id. */
export function mergeDeletedCueMaps(
  a?: Record<Id, string>,
  b?: Record<Id, string>,
): Record<Id, string> | undefined {
  const out: Record<Id, string> = { ...(a ?? {}) };
  for (const [id, at] of Object.entries(b ?? {})) {
    const prev = out[id];
    if (!prev || at > prev) out[id] = at;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function withCueDeleted(doc: ScriptDocument, cueId: Id, at: string): ScriptDocument {
  return {
    ...doc,
    deletedCueIds: { ...(doc.deletedCueIds ?? {}), [cueId]: at },
  };
}

function withoutCueDeleted(
  doc: ScriptDocument,
  cueId: Id,
): ScriptDocument {
  if (!doc.deletedCueIds?.[cueId]) return doc;
  const next = { ...doc.deletedCueIds };
  delete next[cueId];
  return {
    ...doc,
    deletedCueIds: Object.keys(next).length > 0 ? next : undefined,
  };
}

/**
 * Each cue may belong to exactly one scene (`cue.sceneId`). Drop stray
 * memberships (e.g. after a bad merge re-listed split-away rows on the
 * original scene) and ensure the owner scene lists the cue.
 */
/** Drop tombstoned cue bodies and fix scene↔cue ownership. */
export function applyCueTombstones(doc: ScriptDocument): ScriptDocument {
  const deletedCueIds = doc.deletedCueIds;
  if (!deletedCueIds || Object.keys(deletedCueIds).length === 0) {
    return reconcileCueSceneMembership(doc);
  }
  const cues = { ...doc.cues };
  for (const [id, at] of Object.entries(deletedCueIds)) {
    const cue = cues[id];
    if (cue && at >= cue.updatedAt) delete cues[id];
  }
  return reconcileCueSceneMembership({ ...doc, cues, deletedCueIds });
}

/**
 * Fold remote deletion tombstones into a newer local doc without taking
 * remote's stale scene/cue membership.
 */
export function absorbRemoteCueTombstones(
  local: ScriptDocument,
  remote: ScriptDocument,
): ScriptDocument {
  return applyCueTombstones({
    ...local,
    deletedCueIds: mergeDeletedCueMaps(
      local.deletedCueIds,
      remote.deletedCueIds,
    ),
  });
}

/**
 * True when `local` (after merge or with tombstones) must be written back so a
 * stale server/snapshot cannot keep resurrecting deleted or mis-owned cues.
 */
export function documentNeedsSyncRepublish(
  local: ScriptDocument,
  remote: ScriptDocument,
): boolean {
  const localDel = local.deletedCueIds ?? {};
  const remoteDel = remote.deletedCueIds ?? {};
  for (const [id, at] of Object.entries(localDel)) {
    if (remoteDel[id] !== at) return true;
    if (remote.cues[id]) return true;
  }
  for (const id of Object.keys(remote.cues)) {
    if (!local.cues[id]) return true;
  }
  for (const [id, cue] of Object.entries(local.cues)) {
    const remoteCue = remote.cues[id];
    if (!remoteCue) return true;
    if (remoteCue.sceneId !== cue.sceneId) return true;
  }
  for (const [sceneId, scene] of Object.entries(local.scenes)) {
    const remoteScene = remote.scenes[sceneId];
    if (!remoteScene) continue;
    if (scene.cueIds.join("\0") !== remoteScene.cueIds.join("\0")) return true;
    if (scene.episodeId !== remoteScene.episodeId) return true;
    if (Boolean(scene.trashedAt) !== Boolean(remoteScene.trashedAt)) return true;
  }
  for (const [epId, ep] of Object.entries(local.episodes)) {
    const remoteEp = remote.episodes[epId];
    if (!remoteEp) return true;
    if (ep.sceneIds.join("\0") !== remoteEp.sceneIds.join("\0")) return true;
    if ((ep.seasonId ?? null) !== (remoteEp.seasonId ?? null)) return true;
  }
  for (const epId of Object.keys(remote.episodes)) {
    if (!local.episodes[epId]) return true;
  }
  return false;
}

/**
 * Ensure each scene appears in `episodes[scene.episodeId].sceneIds` and
 * nowhere else — order follows scene.sortOrder.
 */
function reconcileEpisodeSceneMembership(
  episodes: Record<Id, Episode>,
  scenes: Record<Id, Scene>,
): Record<Id, Episode> {
  const claimed = new Map<Id, Id[]>();
  for (const scene of Object.values(scenes)) {
    if (!episodes[scene.episodeId]) continue;
    const list = claimed.get(scene.episodeId);
    if (list) list.push(scene.id);
    else claimed.set(scene.episodeId, [scene.id]);
  }

  let changed = false;
  const next: Record<Id, Episode> = { ...episodes };
  for (const [epId, ep] of Object.entries(next)) {
    const belonging = claimed.get(epId) ?? [];
    belonging.sort(
      (a, b) => (scenes[a]?.sortOrder ?? 0) - (scenes[b]?.sortOrder ?? 0),
    );
    const same =
      belonging.length === ep.sceneIds.length &&
      belonging.every((id, i) => id === ep.sceneIds[i]);
    if (same) continue;
    changed = true;
    next[epId] = { ...ep, sceneIds: belonging };
  }
  return changed ? next : episodes;
}

function mergeSeasonRecords(
  local: ScriptDocument,
  remote: ScriptDocument,
): Record<Id, Season> {
  const seasons: Record<Id, Season> = { ...remote.seasons };
  for (const [id, localSeason] of Object.entries(local.seasons)) {
    const remoteSeason = seasons[id];
    if (!remoteSeason) {
      seasons[id] = localSeason;
      continue;
    }
    if (localSeason.updatedAt >= remoteSeason.updatedAt) {
      seasons[id] = localSeason;
    }
  }
  return seasons;
}

function mergeEpisodeRecords(
  local: ScriptDocument,
  remote: ScriptDocument,
): Record<Id, Episode> {
  const episodes: Record<Id, Episode> = { ...remote.episodes };
  for (const [id, localEp] of Object.entries(local.episodes)) {
    const remoteEp = episodes[id];
    if (!remoteEp) {
      episodes[id] = localEp;
      continue;
    }
    if (localEp.updatedAt >= remoteEp.updatedAt) {
      episodes[id] = localEp;
    }
  }
  return episodes;
}

export function reconcileCueSceneMembership(
  doc: ScriptDocument,
): ScriptDocument {
  const scenes: ScriptDocument["scenes"] = { ...doc.scenes };
  let changed = false;

  for (const [sceneId, scene] of Object.entries(scenes)) {
    const nextIds = scene.cueIds.filter((id) => {
      const cue = doc.cues[id];
      return Boolean(cue && cue.sceneId === sceneId);
    });
    if (
      nextIds.length !== scene.cueIds.length ||
      nextIds.some((id, i) => id !== scene.cueIds[i])
    ) {
      scenes[sceneId] = { ...scene, cueIds: nextIds };
      changed = true;
    }
  }

  for (const cue of Object.values(doc.cues)) {
    const owner = scenes[cue.sceneId];
    if (!owner) continue;
    if (owner.cueIds.includes(cue.id)) continue;
    scenes[cue.sceneId] = {
      ...owner,
      cueIds: [...owner.cueIds, cue.id],
    };
    changed = true;
  }

  return changed ? { ...doc, scenes } : doc;
}

export function isSpecialSeasonId(
  doc: ScriptDocument,
  seasonId: Id | null | undefined,
): boolean {
  if (!seasonId) return false;
  if (seasonId === doc.production.trashSeasonId) return true;
  if (seasonId === doc.production.archiveSeasonId) return true;
  if (seasonId === doc.production.submittedSeasonId) return true;
  const role = doc.seasons[seasonId]?.role;
  return role === "trash" || role === "archive" || role === "submitted";
}

export function isNormalEpisode(
  doc: ScriptDocument,
  episode: Pick<Episode, "seasonId"> | null | undefined,
): boolean {
  if (!episode) return false;
  return !isSpecialSeasonId(doc, episode.seasonId);
}

/** Trash/archive/submitted shell episode, or null for a normal episode. */
export function getEpisodeVaultKind(
  doc: ScriptDocument,
  episode: Pick<Episode, "seasonId"> | null | undefined,
): "trash" | "archive" | "submitted" | null {
  if (!episode?.seasonId) return null;
  const role = seasonRoleOf(doc, episode.seasonId);
  return role === "trash" || role === "archive" || role === "submitted"
    ? role
    : null;
}

export function listEpisodes(doc: ScriptDocument): Episode[] {
  const seen = new Set<Id>();
  const out: Episode[] = [];
  const push = (id: Id) => {
    if (seen.has(id)) return;
    const episode = doc.episodes[id];
    if (!episode || !isNormalEpisode(doc, episode)) return;
    seen.add(id);
    out.push(episode);
  };
  const prodIds = doc.production.episodeIds ?? [];
  if (!doc.production.useSeasons && prodIds.length > 0) {
    const listed = new Set(prodIds);
    const everyNormalListed = Object.values(doc.episodes)
      .filter((ep) => isNormalEpisode(doc, ep))
      .every((ep) => listed.has(ep.id));
    if (everyNormalListed) {
      for (const id of prodIds) push(id);
      return out;
    }
  }
  for (const seasonId of doc.production.seasonIds) {
    const season = doc.seasons[seasonId];
    if (
      !season ||
      season.role === "trash" ||
      season.role === "archive" ||
      season.role === "submitted"
    ) {
      continue;
    }
    for (const id of season.episodeIds) push(id);
  }
  for (const id of prodIds) push(id);
  return out;
}

function createStarterCues(
  sceneId: Id,
  ts: string,
): { cues: Cue[]; cueIds: Id[] } {
  const cueId = createId();
  const cue: Cue = {
    id: cueId,
    sceneId,
    sortOrder: 0,
    visual: {
      content: "",
      revision: 1,
      ...emptyLock(),
    },
    audio: {
      content: "",
      revision: 1,
      ...emptyLock(),
    },
    visualRowSpan: 1,
    audioRowSpan: 1,
    updatedAt: ts,
  };
  return { cues: [cue], cueIds: [cueId] };
}

export interface CreateProductionOptions {
  withSeason?: boolean;
}

export function createEmptyProduction(
  title: string,
  options: CreateProductionOptions = {},
): ScriptDocument {
  const productionId = createId();
  const episodeId = createId();
  const sceneId = createId();
  const ts = nowIso();
  const { cues, cueIds } = createStarterCues(sceneId, ts);

  const scene: Scene = {
    id: sceneId,
    episodeId,
    number: 1,
    title: "Åbning",
    description: "",
    durationSeconds: 90,
    sortOrder: 0,
    cueIds,
    updatedAt: ts,
  };

  let seasonId: Id | null = null;
  const seasons: Record<Id, Season> = {};
  let seasonIds: Id[] = [];
  let productionEpisodeIds: Id[] = [];

  if (options.withSeason) {
    seasonId = createId();
    seasons[seasonId] = {
      id: seasonId,
      productionId,
      number: 1,
      title: "Sæson 1",
      sortOrder: 0,
      episodeIds: [episodeId],
      updatedAt: ts,
    };
    seasonIds = [seasonId];
  } else {
    productionEpisodeIds = [episodeId];
  }

  const episode: Episode = {
    id: episodeId,
    productionId,
    seasonId,
    number: 1,
    title: "Unavngiven episode",
    sortOrder: 0,
    sceneIds: [sceneId],
    sceneNumberingMode: "sequential",
    updatedAt: ts,
  };

  const production: Production = {
    id: productionId,
    title,
    createdAt: ts,
    updatedAt: ts,
    seasonIds,
    episodeIds: productionEpisodeIds,
    useSeasons: Boolean(options.withSeason),
    episodeNumberingMode: "sequential",
    castMembers: [],
    statusSteps: createDefaultStatusSteps(),
    noteColumns: createDefaultNoteColumns(),
  };

  const cuesRecord: Record<Id, Cue> = {};
  for (const cue of cues) cuesRecord[cue.id] = cue;

  return {
    production,
    seasons,
    episodes: { [episodeId]: episode },
    scenes: { [sceneId]: scene },
    cues: cuesRecord,
    comments: {},
  };
}

export const createEmptyProject = createEmptyProduction;

export function addSeason(
  doc: ScriptDocument,
  title = "Ny sæson",
): { doc: ScriptDocument; seasonId: Id } {
  const seasonId = createId();
  const ts = nowIso();
  const number = doc.production.seasonIds.length + 1;
  const season: Season = {
    id: seasonId,
    productionId: doc.production.id,
    number,
    title,
    sortOrder: doc.production.seasonIds.length,
    episodeIds: [],
    updatedAt: ts,
  };
  return {
    seasonId,
    doc: {
      ...doc,
      production: {
        ...doc.production,
        seasonIds: [...doc.production.seasonIds, seasonId],
        updatedAt: ts,
      },
      seasons: { ...doc.seasons, [seasonId]: season },
    },
  };
}

function nextUnusedEpisodeTitle(doc: ScriptDocument): string {
  const used = new Set(
    Object.values(doc.episodes).map((ep) => ep.title.trim().toLowerCase()),
  );
  const base = "Unavngiven episode";
  if (!used.has(base.toLowerCase())) return base;
  let n = 2;
  let title = `${base} (${n})`;
  while (used.has(title.toLowerCase())) {
    n += 1;
    title = `${base} (${n})`;
  }
  return title;
}

function episodeNumberingIsCustom(doc: ScriptDocument): boolean {
  return doc.production.episodeNumberingMode === "custom";
}

function episodeGroupIds(doc: ScriptDocument, episode: Episode): Id[] {
  return episodeListContaining(doc, episode.id);
}

function episodeListContaining(doc: ScriptDocument, episodeId: Id): Id[] {
  if (doc.production.useSeasons) {
    const episode = doc.episodes[episodeId];
    if (
      episode?.seasonId &&
      doc.seasons[episode.seasonId]?.episodeIds.includes(episodeId)
    ) {
      return doc.seasons[episode.seasonId]!.episodeIds;
    }
    for (const seasonId of doc.production.seasonIds) {
      const season = doc.seasons[seasonId];
      if (season?.episodeIds.includes(episodeId)) return season.episodeIds;
    }
  }
  if (doc.production.episodeIds.includes(episodeId)) {
    return doc.production.episodeIds;
  }
  for (const seasonId of doc.production.seasonIds) {
    const season = doc.seasons[seasonId];
    if (season?.episodeIds.includes(episodeId)) return season.episodeIds;
  }
  return listEpisodes(doc).map((ep) => ep.id);
}

function renumberEpisodeGroup(
  episodes: Record<Id, Episode>,
  ids: Id[],
  ts: string,
): Record<Id, Episode> {
  const next = { ...episodes };
  ids.forEach((id, index) => {
    const ep = next[id];
    if (!ep) return;
    next[id] = {
      ...ep,
      number: index + 1,
      sortOrder: index,
      updatedAt: ts,
    };
  });
  return next;
}

export function addEpisode(
  doc: ScriptDocument,
  opts: { seasonId?: Id | null; title?: string } = {},
): { doc: ScriptDocument; episodeId: Id } {
  const seasonId = opts.seasonId ?? null;
  const episodeId = createId();
  const sceneId = createId();
  const ts = nowIso();
  const { cues, cueIds } = createStarterCues(sceneId, ts);

  let number: ItemNumber = 1;
  if (seasonId) {
    const season = doc.seasons[seasonId];
    if (!season) throw new Error(`Season ${seasonId} not found`);
    number = episodeNumberingIsCustom(doc)
      ? nextNumericItemNumber(season.episodeIds.map((id) => doc.episodes[id]?.number))
      : season.episodeIds.length + 1;
  } else {
    number = episodeNumberingIsCustom(doc)
      ? nextNumericItemNumber(
          doc.production.episodeIds.map((id) => doc.episodes[id]?.number),
        )
      : doc.production.episodeIds.length + 1;
  }

  const scene: Scene = {
    id: sceneId,
    episodeId,
    number: 1,
    title: "Åbning",
    description: "",
    durationSeconds: 90,
    sortOrder: 0,
    cueIds,
    updatedAt: ts,
  };

  const episode: Episode = {
    id: episodeId,
    productionId: doc.production.id,
    seasonId,
    number,
    title: opts.title ?? nextUnusedEpisodeTitle(doc),
    sortOrder: number - 1,
    sceneIds: [sceneId],
    sceneNumberingMode: "sequential",
    crewRoles: seasonId
      ? copyCrewRoles(doc.seasons[seasonId]?.crewRoles)
      : copyCrewRoles(doc.production.crewRoles),
    updatedAt: ts,
  };

  const cuesRecord = { ...doc.cues };
  for (const cue of cues) cuesRecord[cue.id] = cue;

  let nextSeasons = doc.seasons;
  let nextProduction = { ...doc.production, updatedAt: ts };

  if (seasonId) {
    const season = doc.seasons[seasonId]!;
    nextSeasons = {
      ...doc.seasons,
      [seasonId]: {
        ...season,
        episodeIds: [...season.episodeIds, episodeId],
        updatedAt: ts,
      },
    };
  } else {
    nextProduction = {
      ...nextProduction,
      episodeIds: [...doc.production.episodeIds, episodeId],
    };
  }

  return {
    episodeId,
    doc: {
      ...doc,
      production: nextProduction,
      seasons: nextSeasons,
      episodes: { ...doc.episodes, [episodeId]: episode },
      scenes: { ...doc.scenes, [sceneId]: scene },
      cues: cuesRecord,
    },
  };
}

/**
 * Remove an episode and its scenes, cues and comments.
 * Keeps at least one episode in the production.
 */
export function removeEpisode(
  doc: ScriptDocument,
  episodeId: Id,
): ScriptDocument {
  const episode = doc.episodes[episodeId];
  if (!episode) throw new Error(`Episode ${episodeId} not found`);
  if (listEpisodes(doc).length <= 1) return doc;

  const ts = nowIso();
  const scenes = { ...doc.scenes };
  const cues = { ...doc.cues };
  const removedCueIds = new Set<Id>();
  for (const sceneId of episode.sceneIds) {
    const scene = scenes[sceneId];
    if (scene) {
      for (const cueId of scene.cueIds) {
        removedCueIds.add(cueId);
        delete cues[cueId];
      }
    }
    delete scenes[sceneId];
  }

  const episodes = { ...doc.episodes };
  delete episodes[episodeId];

  const seasons: Record<Id, Season> = {};
  for (const [seasonId, season] of Object.entries(doc.seasons)) {
    if (!season.episodeIds.includes(episodeId)) {
      seasons[seasonId] = season;
      continue;
    }
    seasons[seasonId] = {
      ...season,
      episodeIds: season.episodeIds.filter((id) => id !== episodeId),
      updatedAt: ts,
    };
  }

  const comments = Object.fromEntries(
    Object.entries(doc.comments ?? {}).filter(
      ([, comment]) => !removedCueIds.has(comment.cueId),
    ),
  );

  const nextProduction = {
    ...doc.production,
    episodeIds: doc.production.episodeIds.filter((id) => id !== episodeId),
    updatedAt: ts,
  };

  let nextEpisodes = episodes;
  if (!episodeNumberingIsCustom(doc)) {
    const groupIds =
      episode.seasonId && seasons[episode.seasonId]
        ? seasons[episode.seasonId]!.episodeIds
        : nextProduction.episodeIds;
    nextEpisodes = renumberEpisodeGroup(nextEpisodes, groupIds, ts);
  }

  return {
    ...doc,
    production: nextProduction,
    seasons,
    episodes: nextEpisodes,
    scenes,
    cues,
    comments,
  };
}

export const MAX_ARCHIVED_SCENE_VERSIONS = 3;

/** Scenes in papirkurv/arkiv are permanently removed after this many days. */
export const VAULT_RETENTION_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function vaultRetentionStartedAt(
  scene: Pick<Scene, "trashedAt" | "archivedAt" | "updatedAt">,
  kind: "trash" | "archive",
): string {
  if (kind === "archive") {
    return scene.archivedAt || scene.updatedAt;
  }
  return scene.trashedAt || scene.updatedAt;
}

export function vaultExpiresAtIso(
  startedAt: string,
  retentionDays = VAULT_RETENTION_DAYS,
): string {
  const start = Date.parse(startedAt);
  if (Number.isNaN(start)) {
    return new Date(Date.now() + retentionDays * MS_PER_DAY).toISOString();
  }
  return new Date(start + retentionDays * MS_PER_DAY).toISOString();
}

export function vaultDaysRemaining(
  startedAt: string,
  now = Date.now(),
  retentionDays = VAULT_RETENTION_DAYS,
): number {
  const expires = Date.parse(vaultExpiresAtIso(startedAt, retentionDays));
  if (Number.isNaN(expires)) return retentionDays;
  return Math.max(0, Math.ceil((expires - now) / MS_PER_DAY));
}

export function isVaultItemExpired(
  startedAt: string,
  now = Date.now(),
  retentionDays = VAULT_RETENTION_DAYS,
): boolean {
  const expires = Date.parse(vaultExpiresAtIso(startedAt, retentionDays));
  if (Number.isNaN(expires)) return false;
  return expires <= now;
}

export function hasExpiredVaultItems(
  doc: ScriptDocument,
  now = Date.now(),
): boolean {
  for (const ep of listTrashEpisodes(doc)) {
    for (const sceneId of ep.sceneIds) {
      const scene = doc.scenes[sceneId];
      if (!scene) continue;
      if (
        isVaultItemExpired(
          vaultRetentionStartedAt(scene, "trash"),
          now,
        )
      ) {
        return true;
      }
    }
  }
  for (const ep of listArchiveEpisodes(doc)) {
    for (const sceneId of ep.sceneIds) {
      const scene = doc.scenes[sceneId];
      if (!scene) continue;
      if (
        isVaultItemExpired(
          vaultRetentionStartedAt(scene, "archive"),
          now,
        )
      ) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Permanently remove trash/archive scenes older than
 * {@link VAULT_RETENTION_DAYS}. Returns the same reference when unchanged.
 */
export function purgeExpiredVaultItems(
  doc: ScriptDocument,
  now = Date.now(),
): ScriptDocument {
  const expired: Id[] = [];
  for (const ep of listTrashEpisodes(doc)) {
    for (const sceneId of ep.sceneIds) {
      const scene = doc.scenes[sceneId];
      if (!scene) continue;
      if (
        isVaultItemExpired(
          vaultRetentionStartedAt(scene, "trash"),
          now,
        )
      ) {
        expired.push(sceneId);
      }
    }
  }
  for (const ep of listArchiveEpisodes(doc)) {
    for (const sceneId of ep.sceneIds) {
      const scene = doc.scenes[sceneId];
      if (!scene) continue;
      if (
        isVaultItemExpired(
          vaultRetentionStartedAt(scene, "archive"),
          now,
        )
      ) {
        expired.push(sceneId);
      }
    }
  }
  if (expired.length === 0) return doc;
  let next = doc;
  for (const sceneId of expired) {
    if (next.scenes[sceneId]) {
      next = permanentlyDeleteScene(next, sceneId);
    }
  }
  return next;
}

/** Permanently delete every episode/scene currently in the trash. */
export function emptyTrash(doc: ScriptDocument): ScriptDocument {
  let next = doc;
  for (const ep of listTrashEpisodes(doc)) {
    if (next.episodes[ep.id]) {
      next = permanentlyDeleteEpisode(next, ep.id);
    }
  }
  return next;
}

/** Permanently delete every archived scene/episode shell. */
export function emptyArchive(doc: ScriptDocument): ScriptDocument {
  let next = doc;
  for (const ep of listArchiveEpisodes(doc)) {
    if (next.episodes[ep.id]) {
      next = permanentlyDeleteEpisode(next, ep.id);
    }
  }
  return next;
}

function stampTrashedAt(
  doc: ScriptDocument,
  sceneIds: Id[],
  ts: string,
): ScriptDocument {
  let changed = false;
  const scenes = { ...doc.scenes };
  for (const id of sceneIds) {
    const scene = scenes[id];
    if (!scene || scene.trashedAt) continue;
    scenes[id] = { ...scene, trashedAt: ts, updatedAt: ts };
    changed = true;
  }
  if (!changed) return doc;
  return { ...doc, scenes, production: { ...doc.production, updatedAt: ts } };
}

function clearTrashedAt(
  doc: ScriptDocument,
  sceneIds: Id[],
  ts: string,
): ScriptDocument {
  let changed = false;
  const scenes = { ...doc.scenes };
  for (const id of sceneIds) {
    const scene = scenes[id];
    if (!scene || !scene.trashedAt) continue;
    scenes[id] = { ...scene, trashedAt: undefined, updatedAt: ts };
    changed = true;
  }
  if (!changed) return doc;
  return { ...doc, scenes, production: { ...doc.production, updatedAt: ts } };
}

function seasonRoleOf(
  doc: ScriptDocument,
  seasonId: Id | null | undefined,
): SeasonRole {
  if (!seasonId) return "normal";
  if (seasonId === doc.production.trashSeasonId) return "trash";
  if (seasonId === doc.production.archiveSeasonId) return "archive";
  if (seasonId === doc.production.submittedSeasonId) return "submitted";
  const role = doc.seasons[seasonId]?.role;
  return role === "trash" || role === "archive" || role === "submitted"
    ? role
    : "normal";
}

function ensureSpecialSeason(
  doc: ScriptDocument,
  role: "trash" | "archive" | "submitted",
  title: string,
  field: "trashSeasonId" | "archiveSeasonId" | "submittedSeasonId",
): ScriptDocument {
  const existingId = doc.production[field];
  if (existingId && doc.seasons[existingId]) {
    const season = doc.seasons[existingId]!;
    if (season.role === role) return doc;
    return {
      ...doc,
      seasons: {
        ...doc.seasons,
        [existingId]: { ...season, role, title },
      },
    };
  }
  const ts = nowIso();
  const seasonId = createId();
  const season: Season = {
    id: seasonId,
    productionId: doc.production.id,
    number: 0,
    title,
    sortOrder: -1,
    episodeIds: [],
    role,
    updatedAt: ts,
  };
  return {
    ...doc,
    production: {
      ...doc.production,
      [field]: seasonId,
      updatedAt: ts,
    },
    seasons: { ...doc.seasons, [seasonId]: season },
  };
}

export function ensureTrashSeason(doc: ScriptDocument): ScriptDocument {
  return ensureSpecialSeason(doc, "trash", "Papirkurv", "trashSeasonId");
}

export function ensureArchiveSeason(doc: ScriptDocument): ScriptDocument {
  return ensureSpecialSeason(doc, "archive", "Arkiv", "archiveSeasonId");
}

export function ensureSubmittedSeason(doc: ScriptDocument): ScriptDocument {
  return ensureSpecialSeason(
    doc,
    "submitted",
    "Afleverede scener",
    "submittedSeasonId",
  );
}

export function listTrashEpisodes(doc: ScriptDocument): Episode[] {
  const seasonId = doc.production.trashSeasonId;
  if (!seasonId) return [];
  const season = doc.seasons[seasonId];
  if (!season) return [];
  return season.episodeIds
    .map((id) => doc.episodes[id])
    .filter((ep): ep is Episode => Boolean(ep));
}

export function listArchiveEpisodes(doc: ScriptDocument): Episode[] {
  const seasonId = doc.production.archiveSeasonId;
  if (!seasonId) return [];
  const season = doc.seasons[seasonId];
  if (!season) return [];
  return season.episodeIds
    .map((id) => doc.episodes[id])
    .filter((ep): ep is Episode => Boolean(ep));
}

export function listSubmittedEpisodes(doc: ScriptDocument): Episode[] {
  const seasonId = doc.production.submittedSeasonId;
  if (!seasonId) return [];
  const season = doc.seasons[seasonId];
  if (!season) return [];
  return season.episodeIds
    .map((id) => doc.episodes[id])
    .filter((ep): ep is Episode => Boolean(ep));
}

function findShellEpisode(
  doc: ScriptDocument,
  specialSeasonId: Id,
  originEpisodeId: Id,
): Episode | null {
  const season = doc.seasons[specialSeasonId];
  if (!season) return null;
  for (const id of season.episodeIds) {
    const ep = doc.episodes[id];
    if (ep?.originEpisodeId === originEpisodeId) return ep;
  }
  return null;
}

function createShellEpisode(
  doc: ScriptDocument,
  specialSeasonId: Id,
  origin: Episode,
): { doc: ScriptDocument; episodeId: Id } {
  const ts = nowIso();
  const episodeId = createId();
  const season = doc.seasons[specialSeasonId];
  if (!season) throw new Error("Special season missing");
  const episode: Episode = {
    id: episodeId,
    productionId: doc.production.id,
    seasonId: specialSeasonId,
    number: origin.number,
    title: origin.title.trim() || "Unavngiven episode",
    sortOrder: season.episodeIds.length,
    sceneIds: [],
    sceneNumberingMode: origin.sceneNumberingMode ?? "sequential",
    originEpisodeId: origin.id,
    originEpisodeTitle: origin.title.trim() || "Unavngiven episode",
    updatedAt: ts,
  };
  return {
    episodeId,
    doc: {
      ...doc,
      production: { ...doc.production, updatedAt: ts },
      seasons: {
        ...doc.seasons,
        [specialSeasonId]: {
          ...season,
          episodeIds: [...season.episodeIds, episodeId],
          updatedAt: ts,
        },
      },
      episodes: { ...doc.episodes, [episodeId]: episode },
    },
  };
}

function detachEpisodeFromNormalLists(
  doc: ScriptDocument,
  episodeId: Id,
  ts: string,
): ScriptDocument {
  const episode = doc.episodes[episodeId];
  if (!episode) return doc;
  const seasons: Record<Id, Season> = { ...doc.seasons };
  if (episode.seasonId && seasons[episode.seasonId]) {
    const season = seasons[episode.seasonId]!;
    if (!isSpecialSeasonId(doc, episode.seasonId)) {
      seasons[episode.seasonId] = {
        ...season,
        episodeIds: season.episodeIds.filter((id) => id !== episodeId),
        updatedAt: ts,
      };
    }
  }
  for (const seasonId of doc.production.seasonIds) {
    const season = seasons[seasonId];
    if (!season?.episodeIds.includes(episodeId)) continue;
    seasons[seasonId] = {
      ...season,
      episodeIds: season.episodeIds.filter((id) => id !== episodeId),
      updatedAt: ts,
    };
  }
  return {
    ...doc,
    production: {
      ...doc.production,
      episodeIds: doc.production.episodeIds.filter((id) => id !== episodeId),
      updatedAt: ts,
    },
    seasons,
  };
}

/** Soft-delete: move a whole normal episode into papirkurven. */
export function moveEpisodeToTrash(
  doc: ScriptDocument,
  episodeId: Id,
): ScriptDocument {
  const episode = doc.episodes[episodeId];
  if (!episode) throw new Error(`Episode ${episodeId} not found`);
  if (!isNormalEpisode(doc, episode)) return doc;
  if (listEpisodes(doc).length <= 1) return doc;

  let next = ensureTrashSeason(doc);
  const trashSeasonId = next.production.trashSeasonId!;
  const ts = nowIso();

  // Merge any prior trash shell (from individual scene deletes) into this episode.
  const shell = findShellEpisode(next, trashSeasonId, episodeId);
  if (shell && shell.id !== episodeId) {
    const scenes = { ...next.scenes };
    const mergedSceneIds = [...episode.sceneIds];
    for (const sceneId of shell.sceneIds) {
      const scene = scenes[sceneId];
      if (!scene) continue;
      scenes[sceneId] = { ...scene, episodeId, updatedAt: ts };
      mergedSceneIds.push(sceneId);
    }
    const trashSeason = next.seasons[trashSeasonId]!;
    next = {
      ...next,
      seasons: {
        ...next.seasons,
        [trashSeasonId]: {
          ...trashSeason,
          episodeIds: trashSeason.episodeIds.filter((id) => id !== shell.id),
          updatedAt: ts,
        },
      },
      episodes: (() => {
        const episodes = { ...next.episodes };
        delete episodes[shell.id];
        episodes[episodeId] = {
          ...episode,
          sceneIds: mergedSceneIds,
          updatedAt: ts,
        };
        return episodes;
      })(),
      scenes,
    };
  }

  const current = next.episodes[episodeId]!;
  next = detachEpisodeFromNormalLists(next, episodeId, ts);
  const trashSeason = next.seasons[trashSeasonId]!;
  const alreadyInTrash = trashSeason.episodeIds.includes(episodeId);
  next = {
    ...next,
    seasons: {
      ...next.seasons,
      [trashSeasonId]: {
        ...trashSeason,
        episodeIds: alreadyInTrash
          ? trashSeason.episodeIds
          : [...trashSeason.episodeIds, episodeId],
        updatedAt: ts,
      },
    },
    episodes: {
      ...next.episodes,
      [episodeId]: {
        ...current,
        seasonId: trashSeasonId,
        originEpisodeId: episodeId,
        originEpisodeTitle: current.title.trim() || "Unavngiven episode",
        updatedAt: ts,
      },
    },
  };

  if (!episodeNumberingIsCustom(next)) {
    const groupIds =
      episode.seasonId &&
      next.seasons[episode.seasonId] &&
      !isSpecialSeasonId(next, episode.seasonId)
        ? next.seasons[episode.seasonId]!.episodeIds
        : next.production.episodeIds;
    next = {
      ...next,
      episodes: renumberEpisodeGroup(next.episodes, groupIds, ts),
    };
  }
  const trashed = next.episodes[episodeId];
  if (trashed) {
    next = stampTrashedAt(next, trashed.sceneIds, ts);
  }
  return next;
}

/** Soft-delete: move a scene into papirkurven (last scene → whole episode). */
export function moveSceneToTrash(
  doc: ScriptDocument,
  sceneId: Id,
): ScriptDocument {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);

  // Prefer a normal episode that still lists this scene (repairs half-moved
  // state where scene.episodeId already points at a trash shell).
  const listedIn = Object.values(doc.episodes).find(
    (ep) => isNormalEpisode(doc, ep) && ep.sceneIds.includes(sceneId),
  );
  const byOwner = doc.episodes[scene.episodeId];
  const episode =
    listedIn ??
    (byOwner && isNormalEpisode(doc, byOwner) ? byOwner : null);

  if (!episode) {
    // Already only under a vault shell — just ensure the trash stamp.
    if (byOwner && !isNormalEpisode(doc, byOwner)) {
      return stampTrashedAt(doc, [sceneId], nowIso());
    }
    throw new Error(`Episode for scene ${sceneId} not found`);
  }

  const liveSceneIds = episode.sceneIds.filter((id) => {
    const s = doc.scenes[id];
    return Boolean(s) && (id === sceneId || s!.episodeId === episode.id);
  });
  if (liveSceneIds.length <= 1) {
    return moveEpisodeToTrash(doc, episode.id);
  }

  let next = ensureTrashSeason(doc);
  const trashSeasonId = next.production.trashSeasonId!;
  let shell = findShellEpisode(next, trashSeasonId, episode.id);
  if (!shell) {
    const created = createShellEpisode(next, trashSeasonId, episode);
    next = created.doc;
    shell = next.episodes[created.episodeId]!;
  }

  // Point the scene at the live episode before moveSceneToEpisode, in case a
  // previous failed merge left episodeId on the shell while sceneIds lingered.
  if (next.scenes[sceneId]?.episodeId !== episode.id) {
    const liveEp = next.episodes[episode.id]!;
    next = {
      ...next,
      scenes: {
        ...next.scenes,
        [sceneId]: {
          ...next.scenes[sceneId]!,
          episodeId: episode.id,
          trashedAt: undefined,
        },
      },
      episodes: {
        ...next.episodes,
        [episode.id]: {
          ...liveEp,
          sceneIds: liveEp.sceneIds.includes(sceneId)
            ? liveEp.sceneIds
            : [...liveEp.sceneIds, sceneId],
        },
      },
    };
  }

  const ts = nowIso();
  next = moveSceneToEpisode(next, sceneId, shell.id);
  // Strip from any other normal episode lists (duplicate membership).
  const episodes = { ...next.episodes };
  let stripped = false;
  for (const [id, ep] of Object.entries(episodes)) {
    if (id === shell.id || !isNormalEpisode(next, ep)) continue;
    if (!ep.sceneIds.includes(sceneId)) continue;
    episodes[id] = {
      ...ep,
      sceneIds: ep.sceneIds.filter((sid) => sid !== sceneId),
      updatedAt: ts,
    };
    stripped = true;
  }
  if (stripped) next = { ...next, episodes };
  return stampTrashedAt(next, [sceneId], ts);
}

export function listArchivedVersions(
  doc: ScriptDocument,
  liveSceneId: Id,
): Scene[] {
  return Object.values(doc.scenes)
    .filter((s) => s.archiveOfSceneId === liveSceneId)
    .sort((a, b) => (a.archivedAt ?? "").localeCompare(b.archivedAt ?? ""));
}

/** User-facing label for an archived occurrence (custom name or version+date). */
export function formatArchiveOccurrenceLabel(
  scene: Pick<Scene, "archiveLabel" | "archivedAt" | "title">,
  versionIndex: number,
): string {
  const custom = scene.archiveLabel?.trim();
  if (custom) return custom;
  const when = scene.archivedAt
    ? (() => {
        try {
          return new Date(scene.archivedAt).toLocaleString("da-DK", {
            dateStyle: "medium",
            timeStyle: "short",
          });
        } catch {
          return scene.archivedAt;
        }
      })()
    : "ukendt tidspunkt";
  return `Version ${versionIndex} — ${when}`;
}

export type ArchiveSceneMode = "new" | "replaceOne" | "replaceAll";

/**
 * Snapshot a live scene into the archive.
 * At most {@link MAX_ARCHIVED_SCENE_VERSIONS} versions per live scene.
 */
export function archiveScene(
  doc: ScriptDocument,
  liveSceneId: Id,
  options: {
    mode: ArchiveSceneMode;
    /** Required when mode is replaceOne. */
    replaceArchivedSceneId?: Id;
    /** Optional name for this archive occurrence (not the scene title). */
    label?: string;
  },
): ScriptDocument {
  const live = doc.scenes[liveSceneId];
  if (!live) throw new Error(`Scene ${liveSceneId} not found`);
  const originEpisode = doc.episodes[live.episodeId];
  if (!originEpisode || !isNormalEpisode(doc, originEpisode)) {
    throw new Error("Can only archive scenes from normal episodes");
  }

  let next = ensureArchiveSeason(doc);
  const archiveSeasonId = next.production.archiveSeasonId!;
  const existing = listArchivedVersions(next, liveSceneId);

  if (options.mode === "replaceAll") {
    for (const snap of existing) {
      next = permanentlyDeleteScene(next, snap.id);
    }
  } else if (options.mode === "replaceOne") {
    const targetId = options.replaceArchivedSceneId;
    if (!targetId || !existing.some((s) => s.id === targetId)) {
      throw new Error("replaceArchivedSceneId required");
    }
    next = permanentlyDeleteScene(next, targetId);
  } else if (existing.length >= MAX_ARCHIVED_SCENE_VERSIONS) {
    throw new Error("Archive limit reached");
  }

  let shell = findShellEpisode(next, archiveSeasonId, originEpisode.id);
  if (!shell) {
    const created = createShellEpisode(next, archiveSeasonId, originEpisode);
    next = created.doc;
    shell = next.episodes[created.episodeId]!;
  }

  const { doc: withClone, sceneId: archivedId } = insertClonedScene(
    next,
    next.scenes[liveSceneId]!,
    shell.id,
    {
      afterSceneId: shell.sceneIds[shell.sceneIds.length - 1] ?? null,
      title: live.title.trim() || "Unavngiven scene",
    },
  );
  const ts = nowIso();
  const archived = withClone.scenes[archivedId]!;
  const label = options.label?.trim() || undefined;
  return {
    ...withClone,
    scenes: {
      ...withClone.scenes,
      [archivedId]: {
        ...archived,
        archiveOfSceneId: liveSceneId,
        archivedAt: ts,
        archiveLabel: label,
        title: live.title.trim() || "Unavngiven scene",
        updatedAt: ts,
      },
    },
  };
}

/** Hard-delete a scene (trash purge / archive version purge). */
export function permanentlyDeleteScene(
  doc: ScriptDocument,
  sceneId: Id,
): ScriptDocument {
  const scene = doc.scenes[sceneId];
  if (!scene) return doc;
  const episode = doc.episodes[scene.episodeId];
  if (!episode) return doc;
  if (episode.sceneIds.length <= 1) {
    // Last scene in a special shell → drop the shell episode entirely.
    if (!isNormalEpisode(doc, episode)) {
      return permanentlyDeleteEpisode(doc, episode.id);
    }
    return doc;
  }
  return removeScene(doc, episode.id, sceneId, {
    renumber: episode.sceneNumberingMode !== "custom",
  });
}

/** Hard-delete an episode (trash purge). */
export function permanentlyDeleteEpisode(
  doc: ScriptDocument,
  episodeId: Id,
): ScriptDocument {
  const episode = doc.episodes[episodeId];
  if (!episode) return doc;
  if (isNormalEpisode(doc, episode)) {
    return removeEpisode(doc, episodeId);
  }

  const ts = nowIso();
  const scenes = { ...doc.scenes };
  const cues = { ...doc.cues };
  const removedCueIds = new Set<Id>();
  for (const sceneId of episode.sceneIds) {
    const scene = scenes[sceneId];
    if (scene) {
      for (const cueId of scene.cueIds) {
        removedCueIds.add(cueId);
        delete cues[cueId];
      }
    }
    delete scenes[sceneId];
  }
  const episodes = { ...doc.episodes };
  delete episodes[episodeId];
  const seasons: Record<Id, Season> = { ...doc.seasons };
  if (episode.seasonId && seasons[episode.seasonId]) {
    const season = seasons[episode.seasonId]!;
    seasons[episode.seasonId] = {
      ...season,
      episodeIds: season.episodeIds.filter((id) => id !== episodeId),
      updatedAt: ts,
    };
  }
  const comments = Object.fromEntries(
    Object.entries(doc.comments ?? {}).filter(
      ([, comment]) => !removedCueIds.has(comment.cueId),
    ),
  );
  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    seasons,
    episodes,
    scenes,
    cues,
    comments,
  };
}

/**
 * Replace a live scene’s content with an archived snapshot, or insert the
 * archive as an extra scene while keeping the live one.
 *
 * `replace`: copies the current live scene into papirkurven first, then
 * overwrites the live scene with the archived content.
 * `keep`: inserts a new scene from the archive after the live scene.
 */
export type RestoreArchiveMode = "replace" | "keep";

/** Clone a live scene into papirkurven without removing it from its episode. */
export function copySceneIntoTrash(
  doc: ScriptDocument,
  sceneId: Id,
): ScriptDocument {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);
  const episode = doc.episodes[scene.episodeId];
  if (!episode) throw new Error(`Episode ${scene.episodeId} not found`);
  if (!isNormalEpisode(doc, episode)) {
    throw new Error("Can only copy normal scenes into trash");
  }

  let next = ensureTrashSeason(doc);
  const trashSeasonId = next.production.trashSeasonId!;
  let shell = findShellEpisode(next, trashSeasonId, episode.id);
  if (!shell) {
    const created = createShellEpisode(next, trashSeasonId, episode);
    next = created.doc;
    shell = next.episodes[created.episodeId]!;
  }
  const ts = nowIso();
  const { doc: withClone, sceneId: trashSceneId } = insertClonedScene(
    next,
    scene,
    shell.id,
    {
      afterSceneId: shell.sceneIds[shell.sceneIds.length - 1] ?? null,
      title: scene.title.trim() || "Unavngiven scene",
    },
  );
  return stampTrashedAt(withClone, [trashSceneId], ts);
}

export function restoreSceneFromArchive(
  doc: ScriptDocument,
  archivedSceneId: Id,
  options: { mode: RestoreArchiveMode } = { mode: "replace" },
): ScriptDocument {
  const archived = doc.scenes[archivedSceneId];
  if (!archived?.archiveOfSceneId) {
    throw new Error("Not an archived scene");
  }
  const liveId = archived.archiveOfSceneId;
  const live = doc.scenes[liveId];
  if (!live) throw new Error("Live scene missing");
  const liveEpisode = doc.episodes[live.episodeId];
  if (!liveEpisode || !isNormalEpisode(doc, liveEpisode)) {
    throw new Error("Live scene is not in a normal episode");
  }

  if (options.mode === "keep") {
    const { doc: withClone, sceneId: newId } = insertClonedScene(
      doc,
      archived,
      live.episodeId,
      {
        afterSceneId: liveId,
        title: archived.title.trim() || live.title.trim() || "Unavngiven scene",
      },
    );
    const ts = nowIso();
    const created = withClone.scenes[newId]!;
    return {
      ...withClone,
      scenes: {
        ...withClone.scenes,
        [newId]: {
          ...created,
          archiveOfSceneId: undefined,
          archivedAt: undefined,
          archiveLabel: undefined,
          submittedOfSceneId: undefined,
          submittedAt: undefined,
          submittedByUserId: undefined,
          submittedByName: undefined,
          trashedAt: undefined,
          updatedAt: ts,
        },
      },
    };
  }

  // replace: current live version → papirkurv, then overwrite live with archive
  let next = copySceneIntoTrash(doc, liveId);
  const ts = nowIso();
  const currentLive = next.scenes[liveId]!;
  const cues = { ...next.cues };
  const removedCueIds = new Set(currentLive.cueIds);
  for (const cueId of removedCueIds) delete cues[cueId];

  const restoreTitle =
    archived.title.trim() || currentLive.title.trim() || "Unavngiven scene";
  const cloned = cloneSceneContent(
    next,
    next.scenes[archivedSceneId]!,
    liveId,
    currentLive.episodeId,
    ts,
    restoreTitle,
    currentLive.number,
  );
  Object.assign(cues, cloned.cues);

  const comments = {
    ...Object.fromEntries(
      Object.entries(next.comments ?? {}).filter(
        ([, comment]) => !removedCueIds.has(comment.cueId),
      ),
    ),
    ...commentsFromCueMap(
      Object.fromEntries(
        Object.entries(next.comments ?? {}).filter(([, c]) =>
          archived.cueIds.includes(c.cueId),
        ),
      ),
      cloned.cueIdMap,
    ),
  };

  return {
    ...next,
    production: { ...next.production, updatedAt: ts },
    scenes: {
      ...next.scenes,
      [liveId]: {
        ...cloned.scene,
        id: liveId,
        episodeId: currentLive.episodeId,
        number: currentLive.number,
        sortOrder: currentLive.sortOrder,
        title: restoreTitle,
        archiveOfSceneId: undefined,
        archivedAt: undefined,
        archiveLabel: undefined,
        submittedOfSceneId: undefined,
        submittedAt: undefined,
        submittedByUserId: undefined,
        submittedByName: undefined,
        trashedAt: undefined,
        updatedAt: ts,
      },
    },
    cues,
    comments,
  };
}

/** Lock / submit bookkeeping stored alongside a personal director fork. */
export type DirectorSubmitState = {
  /** When true, tilrettelægger cannot edit until they unlock. */
  locked: boolean;
  /** Scene id in the shared «Afleverede scener» vault, if still present. */
  submittedSceneId?: Id | null;
  /**
   * True when that submission was inserted into program manus (vault entry gone).
   * Tilrettelægger must submit again after further edits.
   */
  submittedConsumed?: boolean;
};

/** Payload stored in director_scene_forks / personal archives. */
export type DirectorScenePayload = {
  scene: Scene;
  cues: Record<Id, Cue>;
  comments?: Record<Id, Comment>;
  submitState?: DirectorSubmitState;
};

/**
 * Copy a personal fork into the shared «Afleverede scener» vault.
 * Does not modify the program manus.
 * Returns the updated document and the new submitted scene id.
 */
export function submitDirectorScene(
  doc: ScriptDocument,
  originEpisodeId: Id,
  liveSceneId: Id,
  payload: DirectorScenePayload,
  submitter: { userId: Id; displayName: string },
): { doc: ScriptDocument; submittedSceneId: Id } {
  const originEpisode = doc.episodes[originEpisodeId];
  if (!originEpisode || !isNormalEpisode(doc, originEpisode)) {
    throw new Error("Origin episode missing");
  }
  const live = doc.scenes[liveSceneId];
  if (!live || live.episodeId !== originEpisodeId) {
    throw new Error("Live scene missing");
  }

  let next = ensureSubmittedSeason(doc);
  const submittedSeasonId = next.production.submittedSeasonId!;
  let shell = findShellEpisode(next, submittedSeasonId, originEpisode.id);
  if (!shell) {
    const created = createShellEpisode(next, submittedSeasonId, originEpisode);
    next = created.doc;
    shell = next.episodes[created.episodeId]!;
  }

  const ts = nowIso();
  const sourceScene: Scene = {
    ...payload.scene,
    id: payload.scene.id || createId(),
    episodeId: shell.id,
    title: live.title.trim() || payload.scene.title.trim() || "Unavngiven scene",
    number: live.number,
    archiveOfSceneId: undefined,
    archivedAt: undefined,
    archiveLabel: undefined,
    submittedOfSceneId: undefined,
    submittedAt: undefined,
    submittedByUserId: undefined,
    submittedByName: undefined,
    trashedAt: undefined,
    updatedAt: ts,
  };
  const tempCueIds = Object.keys(payload.cues).filter((id) => !next.cues[id]);
  const tempCommentIds = Object.keys(payload.comments ?? {}).filter(
    (id) => !(next.comments ?? {})[id],
  );
  // Temporarily attach cues so insertClonedScene can copy them.
  const withSource: ScriptDocument = {
    ...next,
    scenes: { ...next.scenes, [sourceScene.id]: sourceScene },
    cues: { ...next.cues, ...payload.cues },
    comments: {
      ...(next.comments ?? {}),
      ...(payload.comments ?? {}),
    },
  };
  const { doc: withClone, sceneId: submittedId } = insertClonedScene(
    withSource,
    sourceScene,
    shell.id,
    {
      afterSceneId: shell.sceneIds[shell.sceneIds.length - 1] ?? null,
      title: sourceScene.title,
    },
  );
  // Drop temporary source scene / orphan cues if they were not already in the doc.
  const scenes = { ...withClone.scenes };
  if (!next.scenes[sourceScene.id]) {
    delete scenes[sourceScene.id];
  }
  const cues = { ...withClone.cues };
  for (const id of tempCueIds) delete cues[id];
  const comments = { ...(withClone.comments ?? {}) };
  for (const id of tempCommentIds) delete comments[id];
  const submitted = scenes[submittedId]!;
  scenes[submittedId] = {
    ...submitted,
    submittedOfSceneId: liveSceneId,
    submittedAt: ts,
    submittedByUserId: submitter.userId,
    submittedByName: submitter.displayName.trim() || "Tilrettelægger",
    archiveOfSceneId: undefined,
    archivedAt: undefined,
    archiveLabel: undefined,
    trashedAt: undefined,
    updatedAt: ts,
  };
  return {
    doc: {
      ...withClone,
      scenes,
      cues,
      comments,
      production: { ...withClone.production, updatedAt: ts },
    },
    submittedSceneId: submittedId,
  };
}

export type InsertSubmittedMode = "replace" | "keep";

/**
 * Insert a submitted scene into the program manus (replace live or keep both),
 * then remove it from «Afleverede scener».
 */
export function insertSubmittedIntoProgram(
  doc: ScriptDocument,
  submittedSceneId: Id,
  options: { mode: InsertSubmittedMode } = { mode: "replace" },
): ScriptDocument {
  const submitted = doc.scenes[submittedSceneId];
  if (!submitted?.submittedOfSceneId) {
    throw new Error("Not a submitted scene");
  }
  const liveId = submitted.submittedOfSceneId;
  const live = doc.scenes[liveId];
  if (!live) throw new Error("Live scene missing");
  const liveEpisode = doc.episodes[live.episodeId];
  if (!liveEpisode || !isNormalEpisode(doc, liveEpisode)) {
    throw new Error("Live scene is not in a normal episode");
  }

  let next: ScriptDocument;
  if (options.mode === "keep") {
    const { doc: withClone, sceneId: newId } = insertClonedScene(
      doc,
      submitted,
      live.episodeId,
      {
        afterSceneId: liveId,
        title:
          submitted.title.trim() || live.title.trim() || "Unavngiven scene",
      },
    );
    const ts = nowIso();
    const created = withClone.scenes[newId]!;
    next = {
      ...withClone,
      scenes: {
        ...withClone.scenes,
        [newId]: {
          ...created,
          archiveOfSceneId: undefined,
          archivedAt: undefined,
          archiveLabel: undefined,
          submittedOfSceneId: undefined,
          submittedAt: undefined,
          submittedByUserId: undefined,
          submittedByName: undefined,
          trashedAt: undefined,
          updatedAt: ts,
        },
      },
    };
  } else {
    next = copySceneIntoTrash(doc, liveId);
    const ts = nowIso();
    const currentLive = next.scenes[liveId]!;
    const cues = { ...next.cues };
    const removedCueIds = new Set(currentLive.cueIds);
    for (const cueId of removedCueIds) delete cues[cueId];

    const restoreTitle =
      submitted.title.trim() || currentLive.title.trim() || "Unavngiven scene";
    const cloned = cloneSceneContent(
      next,
      next.scenes[submittedSceneId]!,
      liveId,
      currentLive.episodeId,
      ts,
      restoreTitle,
      currentLive.number,
    );
    Object.assign(cues, cloned.cues);

    const comments = {
      ...Object.fromEntries(
        Object.entries(next.comments ?? {}).filter(
          ([, comment]) => !removedCueIds.has(comment.cueId),
        ),
      ),
      ...commentsFromCueMap(
        Object.fromEntries(
          Object.entries(next.comments ?? {}).filter(([, c]) =>
            submitted.cueIds.includes(c.cueId),
          ),
        ),
        cloned.cueIdMap,
      ),
    };

    next = {
      ...next,
      production: { ...next.production, updatedAt: ts },
      scenes: {
        ...next.scenes,
        [liveId]: {
          ...cloned.scene,
          id: liveId,
          episodeId: currentLive.episodeId,
          number: currentLive.number,
          sortOrder: currentLive.sortOrder,
          title: restoreTitle,
          archiveOfSceneId: undefined,
          archivedAt: undefined,
          archiveLabel: undefined,
          submittedOfSceneId: undefined,
          submittedAt: undefined,
          submittedByUserId: undefined,
          submittedByName: undefined,
          trashedAt: undefined,
          updatedAt: ts,
        },
      },
      cues,
      comments,
    };
  }

  // Drop from «Afleverede scener» once inserted into program manus.
  return permanentlyDeleteScene(next, submittedSceneId);
}

/** Restore a trashed episode back among normal episodes. */
export function restoreEpisodeFromTrash(
  doc: ScriptDocument,
  trashEpisodeId: Id,
  destSeasonId?: Id | null,
): ScriptDocument {
  const episode = doc.episodes[trashEpisodeId];
  if (!episode || seasonRoleOf(doc, episode.seasonId) !== "trash") {
    throw new Error("Not a trash episode");
  }
  const ts = nowIso();
  const trashSeasonId = doc.production.trashSeasonId;
  let next: ScriptDocument = doc;
  if (trashSeasonId && next.seasons[trashSeasonId]) {
    const trashSeason = next.seasons[trashSeasonId]!;
    next = {
      ...next,
      seasons: {
        ...next.seasons,
        [trashSeasonId]: {
          ...trashSeason,
          episodeIds: trashSeason.episodeIds.filter(
            (id) => id !== trashEpisodeId,
          ),
          updatedAt: ts,
        },
      },
    };
  }

  const useSeasons = Boolean(next.production.useSeasons);
  let seasonId: Id | null = null;
  if (useSeasons) {
    seasonId =
      destSeasonId &&
      next.seasons[destSeasonId] &&
      !isSpecialSeasonId(next, destSeasonId)
        ? destSeasonId
        : (next.production.seasonIds.find(
            (id) => !isSpecialSeasonId(next, id),
          ) ?? null);
  }

  if (seasonId && next.seasons[seasonId]) {
    const season = next.seasons[seasonId]!;
    next = {
      ...next,
      production: { ...next.production, updatedAt: ts },
      seasons: {
        ...next.seasons,
        [seasonId]: {
          ...season,
          episodeIds: [...season.episodeIds, trashEpisodeId],
          updatedAt: ts,
        },
      },
      episodes: {
        ...next.episodes,
        [trashEpisodeId]: {
          ...episode,
          seasonId,
          originEpisodeId: undefined,
          originEpisodeTitle: undefined,
          sortOrder: season.episodeIds.length,
          updatedAt: ts,
        },
      },
    };
  } else {
    next = {
      ...next,
      production: {
        ...next.production,
        episodeIds: [...next.production.episodeIds, trashEpisodeId],
        updatedAt: ts,
      },
      episodes: {
        ...next.episodes,
        [trashEpisodeId]: {
          ...episode,
          seasonId: null,
          originEpisodeId: undefined,
          originEpisodeTitle: undefined,
          sortOrder: next.production.episodeIds.length,
          updatedAt: ts,
        },
      },
    };
  }
  return clearTrashedAt(
    next,
    next.episodes[trashEpisodeId]?.sceneIds ?? [],
    ts,
  );
}

/** Restore one trashed scene into its origin episode (or a chosen episode). */
export function restoreSceneFromTrash(
  doc: ScriptDocument,
  sceneId: Id,
  destEpisodeId?: Id,
): ScriptDocument {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);
  const trashEp = doc.episodes[scene.episodeId];
  if (!trashEp || seasonRoleOf(doc, trashEp.seasonId) !== "trash") {
    throw new Error("Scene is not in trash");
  }
  const targetId =
    destEpisodeId ??
    (trashEp.originEpisodeId &&
    doc.episodes[trashEp.originEpisodeId] &&
    isNormalEpisode(doc, doc.episodes[trashEp.originEpisodeId]!)
      ? trashEp.originEpisodeId
      : null);
  if (!targetId) {
    throw new Error("Destination episode required");
  }
  const moved = moveSceneToEpisode(doc, sceneId, targetId);
  const ts = nowIso();
  let next = clearTrashedAt(moved, [sceneId], ts);
  // Clean empty trash shell
  const shell = next.episodes[trashEp.id];
  if (shell && shell.sceneIds.length === 0) {
    return permanentlyDeleteEpisode(next, shell.id);
  }
  return next;
}

/** Set the visible episode order (one season, or the whole production). */
export function reorderEpisodes(
  doc: ScriptDocument,
  orderedIds: Id[],
): ScriptDocument {
  const known = new Set(Object.keys(doc.episodes));
  const nextIds = orderedIds.filter((id) => known.has(id));
  if (nextIds.length === 0) return doc;

  const ts = nowIso();
  const nextEpisodes = { ...doc.episodes };
  nextIds.forEach((id, index) => {
    const ep = nextEpisodes[id];
    if (ep) nextEpisodes[id] = { ...ep, sortOrder: index, updatedAt: ts };
  });

  const useSeasons = Boolean(doc.production.useSeasons);
  const seasonId = nextEpisodes[nextIds[0]!]?.seasonId ?? null;
  const allSameSeason = Boolean(
    useSeasons &&
      seasonId &&
      doc.seasons[seasonId] &&
      nextIds.every((id) => nextEpisodes[id]?.seasonId === seasonId),
  );

  if (allSameSeason && seasonId) {
    const season = doc.seasons[seasonId]!;
    if (
      season.episodeIds.length === nextIds.length &&
      season.episodeIds.every((id, i) => id === nextIds[i])
    ) {
      return doc;
    }
    return {
      ...doc,
      production: { ...doc.production, updatedAt: ts },
      seasons: {
        ...doc.seasons,
        [seasonId]: { ...season, episodeIds: nextIds, updatedAt: ts },
      },
      episodes: nextEpisodes,
    };
  }

  const current = listEpisodes(doc).map((ep) => ep.id);
  if (
    current.length === nextIds.length &&
    current.every((id, i) => id === nextIds[i])
  ) {
    return doc;
  }

  const nextSeasons = { ...doc.seasons };
  for (const sid of doc.production.seasonIds) {
    const season = nextSeasons[sid];
    if (!season) continue;
    nextSeasons[sid] = {
      ...season,
      episodeIds: nextIds.filter((id) => nextEpisodes[id]?.seasonId === sid),
      updatedAt: ts,
    };
  }

  return {
    ...doc,
    production: { ...doc.production, episodeIds: nextIds, updatedAt: ts },
    seasons: nextSeasons,
    episodes: nextEpisodes,
  };
}

/** Reorder an episode within its season (or the production list). */
export function moveEpisode(
  doc: ScriptDocument,
  episodeId: Id,
  beforeEpisodeId: Id | null,
): ScriptDocument {
  const episode = doc.episodes[episodeId];
  if (!episode) throw new Error(`Episode ${episodeId} not found`);
  if (beforeEpisodeId === episodeId) return doc;

  const ids = episodeListContaining(doc, episodeId);
  if (!ids.includes(episodeId)) return doc;
  const remaining = ids.filter((id) => id !== episodeId);
  let insertAt =
    beforeEpisodeId === null
      ? remaining.length
      : remaining.indexOf(beforeEpisodeId);
  if (insertAt < 0) {
    const visible = listEpisodes(doc).map((ep) => ep.id);
    return reorderEpisodes(
      doc,
      moveIdBefore(visible, episodeId, beforeEpisodeId),
    );
  }
  return reorderEpisodes(doc, [
    ...remaining.slice(0, insertAt),
    episodeId,
    ...remaining.slice(insertAt),
  ]);
}

/**
 * Move an episode into another season in the same production.
 * Appends to the destination season. Renumbers sequential groups.
 */
export function moveEpisodeToSeason(
  doc: ScriptDocument,
  episodeId: Id,
  destSeasonId: Id,
): ScriptDocument {
  const episode = doc.episodes[episodeId];
  const destSeason = doc.seasons[destSeasonId];
  if (!episode) throw new Error(`Episode ${episodeId} not found`);
  if (!destSeason) throw new Error(`Season ${destSeasonId} not found`);
  if (episode.seasonId === destSeasonId) return doc;

  const ts = nowIso();
  const sourceSeasonId = episode.seasonId;
  const seasons: Record<Id, Season> = { ...doc.seasons };

  for (const [sid, season] of Object.entries(seasons)) {
    if (!season.episodeIds.includes(episodeId)) continue;
    seasons[sid] = {
      ...season,
      episodeIds: season.episodeIds.filter((id) => id !== episodeId),
      updatedAt: ts,
    };
  }

  const dest = seasons[destSeasonId]!;
  seasons[destSeasonId] = {
    ...dest,
    episodeIds: [...dest.episodeIds, episodeId],
    updatedAt: ts,
  };

  let nextEpisodes: Record<Id, Episode> = {
    ...doc.episodes,
    [episodeId]: {
      ...episode,
      seasonId: destSeasonId,
      updatedAt: ts,
    },
  };

  if (!episodeNumberingIsCustom(doc)) {
    if (sourceSeasonId && seasons[sourceSeasonId]) {
      nextEpisodes = renumberEpisodeGroup(
        nextEpisodes,
        seasons[sourceSeasonId]!.episodeIds,
        ts,
      );
    }
    nextEpisodes = renumberEpisodeGroup(
      nextEpisodes,
      seasons[destSeasonId]!.episodeIds,
      ts,
    );
  } else {
    const reSort = (ids: Id[]) => {
      ids.forEach((id, index) => {
        const ep = nextEpisodes[id];
        if (!ep) return;
        nextEpisodes[id] = { ...ep, sortOrder: index, updatedAt: ts };
      });
    };
    if (sourceSeasonId && seasons[sourceSeasonId]) {
      reSort(seasons[sourceSeasonId]!.episodeIds);
    }
    reSort(seasons[destSeasonId]!.episodeIds);
  }

  return {
    ...doc,
    production: {
      ...doc.production,
      episodeIds: doc.production.episodeIds.filter((id) => id !== episodeId),
      updatedAt: ts,
    },
    seasons,
    episodes: nextEpisodes,
  };
}

function moveIdBefore(
  ids: Id[],
  episodeId: Id,
  beforeEpisodeId: Id | null,
): Id[] {
  const remaining = ids.filter((id) => id !== episodeId);
  let insertAt =
    beforeEpisodeId === null
      ? remaining.length
      : remaining.indexOf(beforeEpisodeId);
  if (insertAt < 0) insertAt = remaining.length;
  return [
    ...remaining.slice(0, insertAt),
    episodeId,
    ...remaining.slice(insertAt),
  ];
}

function insertIdAfter(ids: Id[], afterId: Id, newId: Id): Id[] {
  const i = ids.indexOf(afterId);
  if (i < 0) return [...ids, newId];
  return [...ids.slice(0, i + 1), newId, ...ids.slice(i + 1)];
}

function copyColumnSide(side: ColumnContent): ColumnContent {
  return {
    content: side.content,
    revision: side.revision,
    characterName: side.characterName,
    lockOwnerId: null,
    lockOwnerName: null,
    lockExpiresAt: null,
  };
}

function cloneCue(cue: Cue, newCueId: Id, newSceneId: Id, ts: string): Cue {
  return {
    ...cue,
    id: newCueId,
    sceneId: newSceneId,
    visual: copyColumnSide(cue.visual),
    audio: copyColumnSide(cue.audio),
    notes: cue.notes ? { ...cue.notes } : undefined,
    updatedAt: ts,
    updatedById: undefined,
  };
}

function nextSceneNumber(doc: ScriptDocument, episode: Episode): number {
  return nextNumericItemNumber(
    episode.sceneIds.map((id) => doc.scenes[id]?.number),
  );
}

function unusedSceneTitle(
  doc: ScriptDocument,
  episodeId: Id,
  wanted: string,
): string {
  const episode = doc.episodes[episodeId];
  const used = new Set(
    (episode?.sceneIds ?? []).map((id) =>
      (doc.scenes[id]?.title ?? "").trim().toLowerCase(),
    ),
  );
  const base = wanted.trim() || "Unavngiven scene";
  if (!used.has(base.toLowerCase())) return base;
  let n = 2;
  let title = `${base} (${n})`;
  while (used.has(title.toLowerCase())) {
    n += 1;
    title = `${base} (${n})`;
  }
  return title;
}

function unusedSceneCopyTitle(
  doc: ScriptDocument,
  episodeId: Id,
  sourceTitle: string,
): string {
  const base = sourceTitle.trim() || "Unavngiven scene";
  return unusedSceneTitle(doc, episodeId, `${base} (kopi)`);
}

function applySceneOrder(
  scenes: Record<Id, Scene>,
  episodeId: Id,
  sceneIds: Id[],
  ts: string,
  renumber: boolean,
) {
  sceneIds.forEach((id, i) => {
    const scene = scenes[id];
    if (!scene) return;
    scenes[id] = {
      ...scene,
      episodeId,
      sortOrder: i,
      ...(renumber ? { number: i + 1 } : {}),
      updatedAt: ts,
    };
  });
}

function clampCueSpans(
  cues: Record<Id, Cue>,
  cueIds: Id[],
  ts: string,
) {
  cueIds.forEach((id, i) => {
    const cue = cues[id];
    if (!cue) return;
    const remain = cueIds.length - i;
    const visualRowSpan = Math.min(Math.max(1, cue.visualRowSpan ?? 1), remain);
    const audioRowSpan = Math.min(Math.max(1, cue.audioRowSpan ?? 1), remain);
    if (
      visualRowSpan !== (cue.visualRowSpan ?? 1) ||
      audioRowSpan !== (cue.audioRowSpan ?? 1)
    ) {
      cues[id] = { ...cue, visualRowSpan, audioRowSpan, updatedAt: ts };
    }
  });
}

function copyCommentsForCues(
  comments: Record<Id, Comment>,
  cueIdMap: Map<Id, Id>,
): Record<Id, Comment> {
  const next = { ...comments };
  for (const comment of Object.values(comments)) {
    const newCueId = cueIdMap.get(comment.cueId);
    if (!newCueId) continue;
    const newId = createId();
    next[newId] = { ...comment, id: newId, cueId: newCueId };
  }
  return next;
}

function cloneSceneContent(
  doc: ScriptDocument,
  scene: Scene,
  newSceneId: Id,
  destEpisodeId: Id,
  ts: string,
  title: string,
  number: ItemNumber,
): { scene: Scene; cues: Record<Id, Cue>; cueIdMap: Map<Id, Id> } {
  const cues: Record<Id, Cue> = {};
  const cueIdMap = new Map<Id, Id>();
  const newCueIds: Id[] = [];
  for (const oldCueId of scene.cueIds) {
    const cue = doc.cues[oldCueId];
    if (!cue) continue;
    const newCueId = createId();
    cueIdMap.set(oldCueId, newCueId);
    newCueIds.push(newCueId);
    cues[newCueId] = cloneCue(cue, newCueId, newSceneId, ts);
  }
  return {
    cueIdMap,
    cues,
    scene: {
      ...scene,
      id: newSceneId,
      episodeId: destEpisodeId,
      title,
      number,
      cueIds: newCueIds,
      crewRoles: copyCrewRoles(scene.crewRoles),
      statusSteps: scene.statusSteps?.map((step) => ({ ...step })),
      statusCompletedIds: scene.statusCompletedIds
        ? [...scene.statusCompletedIds]
        : undefined,
      updatedAt: ts,
    },
  };
}

function insertClonedScene(
  doc: ScriptDocument,
  source: Scene,
  destEpisodeId: Id,
  opts: { afterSceneId: Id | null; title: string },
): { doc: ScriptDocument; sceneId: Id } {
  const dest = doc.episodes[destEpisodeId];
  if (!dest) throw new Error(`Episode ${destEpisodeId} not found`);
  const ts = nowIso();
  const newSceneId = createId();
  const cloned = cloneSceneContent(
    doc,
    source,
    newSceneId,
    destEpisodeId,
    ts,
    opts.title,
    nextSceneNumber(doc, dest),
  );
  const scenes = { ...doc.scenes, [newSceneId]: cloned.scene };
  const cues = { ...doc.cues, ...cloned.cues };
  const comments = copyCommentsForCues(doc.comments ?? {}, cloned.cueIdMap);
  const nextIds = opts.afterSceneId
    ? insertIdAfter(dest.sceneIds, opts.afterSceneId, newSceneId)
    : [...dest.sceneIds, newSceneId];
  applySceneOrder(
    scenes,
    dest.id,
    nextIds,
    ts,
    dest.sceneNumberingMode !== "custom",
  );
  return {
    sceneId: newSceneId,
    doc: {
      ...doc,
      production: { ...doc.production, updatedAt: ts },
      episodes: {
        ...doc.episodes,
        [dest.id]: { ...dest, sceneIds: nextIds, updatedAt: ts },
      },
      scenes,
      cues,
      comments,
    },
  };
}

function copyCrewRoles(roles?: SceneCrewRole[]): SceneCrewRole[] | undefined {
  if (!roles?.length) return roles;
  return roles.map((role) => ({
    ...role,
    id: createId(),
    people: role.people.map((person) => ({ ...person, id: createId() })),
  }));
}

function copyProgramTexts(texts?: ProgramText[]): ProgramText[] | undefined {
  if (!texts?.length) return texts;
  return texts.map((text) => ({ ...text, id: createId() }));
}

function unusedLabeledEpisodeTitle(
  doc: ScriptDocument,
  sourceTitle: string,
  label: string,
): string {
  const base = sourceTitle.trim() || "Unavngiven episode";
  const used = new Set(
    Object.values(doc.episodes).map((ep) => ep.title.trim().toLowerCase()),
  );
  let title = `${base} (${label})`;
  let n = 2;
  while (used.has(title.toLowerCase())) {
    title = `${base} (${label} ${n})`;
    n += 1;
  }
  return title;
}

function unusedCopyTitle(doc: ScriptDocument, sourceTitle: string): string {
  return unusedLabeledEpisodeTitle(doc, sourceTitle, "kopi");
}

function unusedRestoredTitle(doc: ScriptDocument, sourceTitle: string): string {
  return unusedLabeledEpisodeTitle(doc, sourceTitle, "gendannet");
}

/**
 * Duplicate an episode (scenes, cues, notes, credits, program texts, comments)
 * and insert the copy after the original.
 */
export function duplicateEpisode(
  doc: ScriptDocument,
  episodeId: Id,
): { doc: ScriptDocument; episodeId: Id } {
  const source = doc.episodes[episodeId];
  if (!source) throw new Error(`Episode ${episodeId} not found`);
  const sourceEp = source;
  const ts = nowIso();
  const newEpisodeId = createId();
  const cueIdMap = new Map<Id, Id>();

  const scenes: Record<Id, Scene> = { ...doc.scenes };
  const cues: Record<Id, Cue> = { ...doc.cues };
  const newSceneIds: Id[] = [];

  for (const oldSceneId of sourceEp.sceneIds) {
    const scene = doc.scenes[oldSceneId];
    if (!scene) continue;
    const newSceneId = createId();
    const newCueIds: Id[] = [];
    for (const oldCueId of scene.cueIds) {
      const cue = doc.cues[oldCueId];
      if (!cue) continue;
      const newCueId = createId();
      cueIdMap.set(oldCueId, newCueId);
      newCueIds.push(newCueId);
      cues[newCueId] = {
        ...cue,
        id: newCueId,
        sceneId: newSceneId,
        visual: copyColumnSide(cue.visual),
        audio: copyColumnSide(cue.audio),
        notes: cue.notes ? { ...cue.notes } : undefined,
        updatedAt: ts,
        updatedById: undefined,
      };
    }
    newSceneIds.push(newSceneId);
    scenes[newSceneId] = {
      ...scene,
      id: newSceneId,
      episodeId: newEpisodeId,
      cueIds: newCueIds,
      crewRoles: copyCrewRoles(scene.crewRoles),
      statusSteps: scene.statusSteps?.map((s) => ({ ...s })),
      statusCompletedIds: scene.statusCompletedIds
        ? [...scene.statusCompletedIds]
        : undefined,
      updatedAt: ts,
    };
  }

  const comments = { ...(doc.comments ?? {}) };
  for (const comment of Object.values(doc.comments ?? {})) {
    const newCueId = cueIdMap.get(comment.cueId);
    if (!newCueId) continue;
    const newCommentId = createId();
    comments[newCommentId] = { ...comment, id: newCommentId, cueId: newCueId };
  }

  const episode: Episode = {
    ...sourceEp,
    id: newEpisodeId,
    title: unusedCopyTitle(doc, sourceEp.title),
    number: episodeNumberingIsCustom(doc)
      ? nextNumericItemNumber(
          episodeGroupIds(doc, sourceEp).map((id) => doc.episodes[id]?.number),
        )
      : sourceEp.number,
    sceneIds: newSceneIds,
    crewRoles: copyCrewRoles(sourceEp.crewRoles),
    programTexts: copyProgramTexts(sourceEp.programTexts),
    statusSteps: sourceEp.statusSteps?.map((s) => ({ ...s })),
    statusCompletedIds: sourceEp.statusCompletedIds
      ? [...sourceEp.statusCompletedIds]
      : undefined,
    updatedAt: ts,
  };

  let nextSeasons = doc.seasons;
  let nextProduction = { ...doc.production, updatedAt: ts };
  let nextEpisodes = { ...doc.episodes, [newEpisodeId]: episode };

  if (sourceEp.seasonId && doc.seasons[sourceEp.seasonId]) {
    const season = doc.seasons[sourceEp.seasonId]!;
    const episodeIds = insertIdAfter(season.episodeIds, sourceEp.id, newEpisodeId);
    nextSeasons = {
      ...doc.seasons,
      [sourceEp.seasonId]: { ...season, episodeIds, updatedAt: ts },
    };
    nextEpisodes = episodeIds.reduce((acc, id, index) => {
      const ep = acc[id];
      if (!ep) return acc;
      acc[id] = {
        ...ep,
        sortOrder: index,
        updatedAt: ts,
        ...(episodeNumberingIsCustom(doc) ? {} : { number: index + 1 }),
      };
      return acc;
    }, nextEpisodes);
  } else {
    const episodeIds = insertIdAfter(
      doc.production.episodeIds,
      sourceEp.id,
      newEpisodeId,
    );
    nextProduction = { ...nextProduction, episodeIds };
    nextEpisodes = episodeIds.reduce((acc, id, index) => {
      const ep = acc[id];
      if (!ep) return acc;
      acc[id] = {
        ...ep,
        sortOrder: index,
        updatedAt: ts,
        ...(episodeNumberingIsCustom(doc) ? {} : { number: index + 1 }),
      };
      return acc;
    }, nextEpisodes);
  }

  return {
    episodeId: newEpisodeId,
    doc: {
      ...doc,
      production: nextProduction,
      seasons: nextSeasons,
      episodes: nextEpisodes,
      scenes,
      cues,
      comments,
    },
  };
}

/**
 * Copy an episode (scenes, cues, notes, credits, program texts, comments)
 * from one document into another. New IDs throughout.
 */
export function copyEpisodeToDocument(
  sourceDoc: ScriptDocument,
  episodeId: Id,
  destDoc: ScriptDocument,
): { doc: ScriptDocument; episodeId: Id } {
  const source = sourceDoc.episodes[episodeId];
  if (!source) throw new Error(`Episode ${episodeId} not found`);
  const ts = nowIso();
  const newEpisodeId = createId();
  const cueIdMap = new Map<Id, Id>();

  const scenes: Record<Id, Scene> = { ...destDoc.scenes };
  const cues: Record<Id, Cue> = { ...destDoc.cues };
  const newSceneIds: Id[] = [];

  for (const oldSceneId of source.sceneIds) {
    const scene = sourceDoc.scenes[oldSceneId];
    if (!scene) continue;
    const newSceneId = createId();
    const newCueIds: Id[] = [];
    for (const oldCueId of scene.cueIds) {
      const cue = sourceDoc.cues[oldCueId];
      if (!cue) continue;
      const newCueId = createId();
      cueIdMap.set(oldCueId, newCueId);
      newCueIds.push(newCueId);
      cues[newCueId] = {
        ...cue,
        id: newCueId,
        sceneId: newSceneId,
        visual: copyColumnSide(cue.visual),
        audio: copyColumnSide(cue.audio),
        notes: cue.notes ? { ...cue.notes } : undefined,
        updatedAt: ts,
        updatedById: undefined,
      };
    }
    newSceneIds.push(newSceneId);
    scenes[newSceneId] = {
      ...scene,
      id: newSceneId,
      episodeId: newEpisodeId,
      cueIds: newCueIds,
      crewRoles: copyCrewRoles(scene.crewRoles),
      statusSteps: scene.statusSteps?.map((s) => ({ ...s })),
      statusCompletedIds: scene.statusCompletedIds
        ? [...scene.statusCompletedIds]
        : undefined,
      updatedAt: ts,
    };
  }

  const comments = { ...(destDoc.comments ?? {}) };
  for (const comment of Object.values(sourceDoc.comments ?? {})) {
    const newCueId = cueIdMap.get(comment.cueId);
    if (!newCueId) continue;
    const newCommentId = createId();
    comments[newCommentId] = { ...comment, id: newCommentId, cueId: newCueId };
  }

  const destSeasonId: Id | null =
    destDoc.production.useSeasons && destDoc.production.seasonIds[0]
      ? destDoc.production.seasonIds[0]!
      : null;

  const episode: Episode = {
    ...source,
    id: newEpisodeId,
    productionId: destDoc.production.id,
    seasonId: destSeasonId,
    title: unusedCopyTitle(destDoc, source.title),
    number: episodeNumberingIsCustom(destDoc)
      ? nextNumericItemNumber(
          listEpisodes(destDoc).map((ep) => ep.number),
        )
      : listEpisodes(destDoc).length + 1,
    sceneIds: newSceneIds,
    sortOrder: listEpisodes(destDoc).length,
    crewRoles: copyCrewRoles(source.crewRoles),
    programTexts: copyProgramTexts(source.programTexts),
    updatedAt: ts,
  };

  let nextSeasons = destDoc.seasons;
  let nextProduction = { ...destDoc.production, updatedAt: ts };
  let nextEpisodes = { ...destDoc.episodes, [newEpisodeId]: episode };

  if (destSeasonId && destDoc.seasons[destSeasonId]) {
    const season = destDoc.seasons[destSeasonId]!;
    const episodeIds = [...season.episodeIds, newEpisodeId];
    nextSeasons = {
      ...destDoc.seasons,
      [destSeasonId]: { ...season, episodeIds, updatedAt: ts },
    };
    nextEpisodes = episodeIds.reduce((acc, id, index) => {
      const ep = acc[id];
      if (!ep) return acc;
      acc[id] = {
        ...ep,
        sortOrder: index,
        updatedAt: ts,
        ...(episodeNumberingIsCustom(destDoc) ? {} : { number: index + 1 }),
      };
      return acc;
    }, { ...nextEpisodes });
  } else {
    const episodeIds = [...destDoc.production.episodeIds, newEpisodeId];
    nextProduction = { ...nextProduction, episodeIds };
    nextEpisodes = episodeIds.reduce((acc, id, index) => {
      const ep = acc[id];
      if (!ep) return acc;
      acc[id] = {
        ...ep,
        sortOrder: index,
        updatedAt: ts,
        ...(episodeNumberingIsCustom(destDoc) ? {} : { number: index + 1 }),
      };
      return acc;
    }, { ...nextEpisodes });
  }

  return {
    episodeId: newEpisodeId,
    doc: {
      ...destDoc,
      production: nextProduction,
      seasons: nextSeasons,
      episodes: nextEpisodes,
      scenes,
      cues,
      comments,
    },
  };
}

/** File format marker for episode backup JSON (komplet episodemanus). */
export const EPISODE_BACKUP_FORMAT = "minproduktion.episode-backup" as const;
export const EPISODE_BACKUP_VERSION = 1 as const;

/**
 * Self-contained episode snapshot: scenes, cues (incl. notes/camera),
 * comments, program texts, crew, plus production note columns / cast /
 * status steps needed to restore notes and dialogue colors.
 */
export type EpisodeBackup = {
  format: typeof EPISODE_BACKUP_FORMAT;
  version: typeof EPISODE_BACKUP_VERSION;
  exportedAt: string;
  /** Display only — not used as an id when restoring. */
  productionTitle: string;
  episode: Episode;
  scenes: Record<Id, Scene>;
  cues: Record<Id, Cue>;
  comments: Record<Id, Comment>;
  noteColumns: NoteColumnDef[];
  castMembers: CastMember[];
  statusSteps: StatusStepDef[];
  crewJobTitles: string[];
};

function collectEpisodeCueIds(doc: ScriptDocument, episode: Episode): Id[] {
  const ids: Id[] = [];
  for (const sceneId of episode.sceneIds) {
    const scene = doc.scenes[sceneId];
    if (!scene) continue;
    for (const cueId of scene.cueIds) {
      if (doc.cues[cueId]) ids.push(cueId);
    }
  }
  return ids;
}

/** Build a portable backup of one episode (manus, noter, credits, …). */
export function exportEpisodeBackup(
  doc: ScriptDocument,
  episodeId: Id,
): EpisodeBackup {
  const episode = doc.episodes[episodeId];
  if (!episode) throw new Error(`Episode ${episodeId} not found`);
  if (!isNormalEpisode(doc, episode)) {
    throw new Error("Kun almindelige episoder kan sikkerhedskopieres");
  }

  const scenes: Record<Id, Scene> = {};
  for (const sceneId of episode.sceneIds) {
    const scene = doc.scenes[sceneId];
    if (scene) scenes[sceneId] = structuredClone(scene);
  }

  const cues: Record<Id, Cue> = {};
  for (const cueId of collectEpisodeCueIds(doc, episode)) {
    const cue = doc.cues[cueId];
    if (cue) cues[cueId] = structuredClone(cue);
  }

  const cueIdSet = new Set(Object.keys(cues));
  const comments: Record<Id, Comment> = {};
  for (const comment of Object.values(doc.comments ?? {})) {
    if (!cueIdSet.has(comment.cueId)) continue;
    comments[comment.id] = structuredClone(comment);
  }

  return {
    format: EPISODE_BACKUP_FORMAT,
    version: EPISODE_BACKUP_VERSION,
    exportedAt: nowIso(),
    productionTitle: doc.production.title,
    episode: structuredClone(episode),
    scenes,
    cues,
    comments,
    noteColumns: structuredClone(getNoteColumns(doc.production)),
    castMembers: structuredClone(doc.production.castMembers ?? []),
    statusSteps: structuredClone(
      doc.production.statusSteps?.length
        ? doc.production.statusSteps
        : createDefaultStatusSteps(),
    ),
    crewJobTitles: [...(doc.production.crewJobTitles ?? [])],
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/** Validate JSON parsed from an episode backup file. */
export function parseEpisodeBackup(raw: unknown): EpisodeBackup {
  if (!isRecord(raw)) throw new Error("Ugyldig sikkerhedskopi");
  if (raw.format !== EPISODE_BACKUP_FORMAT) {
    throw new Error("Filen er ikke en episodemanus-sikkerhedskopi");
  }
  const version = Number(raw.version);
  if (!Number.isFinite(version) || version < 1 || version > EPISODE_BACKUP_VERSION) {
    throw new Error("Ukendt version af sikkerhedskopien");
  }
  if (!isRecord(raw.episode) || typeof raw.episode.id !== "string") {
    throw new Error("Sikkerhedskopien mangler episode");
  }
  if (!isRecord(raw.scenes) || !isRecord(raw.cues)) {
    throw new Error("Sikkerhedskopien mangler scener eller cues");
  }
  const episode = raw.episode as unknown as Episode;
  if (!Array.isArray(episode.sceneIds)) {
    throw new Error("Sikkerhedskopien har ugyldig sceneliste");
  }
  return {
    format: EPISODE_BACKUP_FORMAT,
    version: EPISODE_BACKUP_VERSION,
    exportedAt:
      typeof raw.exportedAt === "string" ? raw.exportedAt : nowIso(),
    productionTitle:
      typeof raw.productionTitle === "string" ? raw.productionTitle : "",
    episode,
    scenes: raw.scenes as Record<Id, Scene>,
    cues: raw.cues as Record<Id, Cue>,
    comments: isRecord(raw.comments)
      ? (raw.comments as Record<Id, Comment>)
      : {},
    noteColumns: Array.isArray(raw.noteColumns)
      ? (raw.noteColumns as NoteColumnDef[])
      : createDefaultNoteColumns(),
    castMembers: Array.isArray(raw.castMembers)
      ? (raw.castMembers as CastMember[])
      : [],
    statusSteps: Array.isArray(raw.statusSteps)
      ? (raw.statusSteps as StatusStepDef[])
      : createDefaultStatusSteps(),
    crewJobTitles: Array.isArray(raw.crewJobTitles)
      ? raw.crewJobTitles.filter((t): t is string => typeof t === "string")
      : [],
  };
}

function episodeBackupToSourceDocument(backup: EpisodeBackup): ScriptDocument {
  const ts = backup.exportedAt || nowIso();
  const productionId = backup.episode.productionId || createId();
  const episode: Episode = {
    ...backup.episode,
    productionId,
    seasonId: null,
    originEpisodeId: undefined,
    originEpisodeTitle: undefined,
  };
  return {
    production: {
      id: productionId,
      title: backup.productionTitle || "Sikkerhedskopi",
      createdAt: ts,
      updatedAt: ts,
      seasonIds: [],
      episodeIds: [episode.id],
      castMembers: backup.castMembers.map((m, i) => ({ ...m, sortOrder: i })),
      noteColumns: backup.noteColumns,
      statusSteps: backup.statusSteps,
      crewJobTitles:
        backup.crewJobTitles.length > 0 ? [...backup.crewJobTitles] : undefined,
    },
    seasons: {},
    episodes: { [episode.id]: episode },
    scenes: { ...backup.scenes },
    cues: { ...backup.cues },
    comments: { ...backup.comments },
  };
}

function mergeCastMembers(
  local: CastMember[] | undefined,
  incoming: CastMember[],
): CastMember[] {
  const out = (local ?? []).map((m) => ({ ...m }));
  const byName = new Map(
    out.map((m) => [m.name.trim().toLowerCase(), m] as const),
  );
  let sortOrder = out.length;
  for (const member of incoming) {
    const key = member.name.trim().toLowerCase();
    if (!key) continue;
    const existing = byName.get(key);
    if (existing) {
      if (!existing.color && member.color) existing.color = member.color;
      continue;
    }
    const next: CastMember = {
      ...member,
      id: createId(),
      sortOrder: sortOrder++,
    };
    out.push(next);
    byName.set(key, next);
  }
  return out;
}

function mergeCrewJobTitles(
  local: string[] | undefined,
  incoming: string[],
): string[] | undefined {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const title of [...(local ?? []), ...incoming]) {
    const trimmed = title.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out.length > 0 ? out : undefined;
}

function mergeStatusStepDefs(
  local: StatusStepDef[] | undefined,
  incoming: StatusStepDef[],
): StatusStepDef[] {
  const byId = new Map<Id, StatusStepDef>();
  for (const step of local?.length ? local : createDefaultStatusSteps()) {
    if (step?.id) byId.set(step.id, { ...step });
  }
  for (const step of incoming) {
    if (step?.id && !byId.has(step.id)) byId.set(step.id, { ...step });
  }
  return [...byId.values()].map((step, i) => ({ ...step, sortOrder: i }));
}

/**
 * Insert a backed-up episode into `destDoc` as a new episode (new ids).
 * Merges note columns, cast, status steps and crew titles so notes/colors work.
 */
export function importEpisodeBackup(
  destDoc: ScriptDocument,
  backup: EpisodeBackup,
): { doc: ScriptDocument; episodeId: Id } {
  const sourceDoc = normalizeDocument(episodeBackupToSourceDocument(backup));
  const sourceEpisodeId = backup.episode.id;
  if (!sourceDoc.episodes[sourceEpisodeId]) {
    throw new Error("Sikkerhedskopien kunne ikke læses");
  }

  const ts = nowIso();
  const prepared: ScriptDocument = {
    ...destDoc,
    production: {
      ...destDoc.production,
      noteColumns: mergeNoteColumnDefs(
        destDoc.production.noteColumns,
        backup.noteColumns,
      ),
      castMembers: mergeCastMembers(
        destDoc.production.castMembers,
        backup.castMembers,
      ),
      statusSteps: mergeStatusStepDefs(
        destDoc.production.statusSteps,
        backup.statusSteps,
      ),
      crewJobTitles: mergeCrewJobTitles(
        destDoc.production.crewJobTitles,
        backup.crewJobTitles,
      ),
      updatedAt: ts,
    },
  };

  const { doc, episodeId } = copyEpisodeToDocument(
    sourceDoc,
    sourceEpisodeId,
    prepared,
  );
  const episode = doc.episodes[episodeId];
  if (!episode) return { doc, episodeId };

  return {
    episodeId,
    doc: {
      ...doc,
      episodes: {
        ...doc.episodes,
        [episodeId]: {
          ...episode,
          title: unusedRestoredTitle(prepared, backup.episode.title),
        },
      },
    },
  };
}

/** Suggested download name for an episode backup file. */
export function suggestedEpisodeBackupFilename(
  doc: ScriptDocument,
  episodeId: Id,
): string {
  const episode = doc.episodes[episodeId];
  const parts = [
    doc.production.title,
    episode ? formatEpisodeDisplayTitle(episode, true) : "episode",
  ]
    .map((s) => s.trim())
    .filter(Boolean)
    .join(" – ")
    .replace(/[\\/:*?"<>|]+/g, "-")
    .trim();
  return `${parts || "episode"}.smepisode.json`;
}

export function addScene(
  doc: ScriptDocument,
  episodeId: Id,
  title = "Ny scene",
): { doc: ScriptDocument; sceneId: Id } {
  const episode = doc.episodes[episodeId];
  if (!episode) throw new Error(`Episode ${episodeId} not found`);

  const sceneId = createId();
  const ts = nowIso();
  const number = nextSceneNumber(doc, episode);
  const { cues, cueIds } = createStarterCues(sceneId, ts);

  const scene: Scene = {
    id: sceneId,
    episodeId,
    number,
    title,
    description: "",
    durationSeconds: 60,
    sortOrder: episode.sceneIds.length,
    cueIds,
    updatedAt: ts,
  };

  const cuesRecord = { ...doc.cues };
  for (const cue of cues) cuesRecord[cue.id] = cue;

  return {
    sceneId,
    doc: {
      ...doc,
      production: { ...doc.production, updatedAt: ts },
      episodes: {
        ...doc.episodes,
        [episodeId]: {
          ...episode,
          sceneIds: [...episode.sceneIds, sceneId],
          updatedAt: ts,
        },
      },
      scenes: { ...doc.scenes, [sceneId]: scene },
      cues: cuesRecord,
    },
  };
}

/**
 * Remove a scene and its cues/comments from an episode.
 * Keeps at least one scene. When `renumber` is true, remaining scenes become 1…n
 * — but never if the episode uses custom numbers, or if numbers already
 * deviate from 1…n (manual edits must not be wiped by a delete).
 */
export function removeScene(
  doc: ScriptDocument,
  episodeId: Id,
  sceneId: Id,
  options: { renumber?: boolean } = {},
): ScriptDocument {
  const episode = doc.episodes[episodeId];
  if (!episode) throw new Error(`Episode ${episodeId} not found`);
  if (!episode.sceneIds.includes(sceneId)) {
    throw new Error(`Scene ${sceneId} not in episode`);
  }
  if (episode.sceneIds.length <= 1) return doc;

  const scene = doc.scenes[sceneId];
  const ts = nowIso();
  const nextSceneIds = episode.sceneIds.filter((id) => id !== sceneId);

  const cues = { ...doc.cues };
  const removedCueIds = new Set(scene?.cueIds ?? []);
  for (const cueId of removedCueIds) {
    delete cues[cueId];
  }

  const scenes = { ...doc.scenes };
  delete scenes[sceneId];

  const isCustom = episode.sceneNumberingMode === "custom";
  const numbersFollowOrder = episodeSceneNumbersFollowOrder(doc, episode);
  const renumber =
    Boolean(options.renumber) && !isCustom && numbersFollowOrder;
  const nextMode: SceneNumberingMode =
    isCustom || (!numbersFollowOrder && !isCustom) ? "custom" : "sequential";

  nextSceneIds.forEach((id, i) => {
    const s = scenes[id];
    if (!s) return;
    scenes[id] = {
      ...s,
      sortOrder: i,
      ...(renumber ? { number: i + 1 } : {}),
      updatedAt: ts,
    };
  });

  const comments = Object.fromEntries(
    Object.entries(doc.comments ?? {}).filter(
      ([, comment]) => !removedCueIds.has(comment.cueId),
    ),
  );

  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    episodes: {
      ...doc.episodes,
      [episodeId]: {
        ...episode,
        sceneIds: nextSceneIds,
        sceneNumberingMode: nextMode,
        updatedAt: ts,
      },
    },
    scenes,
    cues,
    comments,
  };
}

/**
 * Move a scene so it sits immediately before `beforeSceneId`,
 * or at the end when `beforeSceneId` is null.
 * When `renumber` is true, scene numbers become 1…n in the new order.
 */
export function moveScene(
  doc: ScriptDocument,
  episodeId: Id,
  sceneId: Id,
  beforeSceneId: Id | null,
  options: { renumber?: boolean } = {},
): ScriptDocument {
  const episode = doc.episodes[episodeId];
  if (!episode) throw new Error(`Episode ${episodeId} not found`);
  const fromIndex = episode.sceneIds.indexOf(sceneId);
  if (fromIndex < 0) throw new Error(`Scene ${sceneId} not in episode`);

  if (beforeSceneId === sceneId) return doc;

  const remaining = episode.sceneIds.filter((id) => id !== sceneId);
  let insertAt =
    beforeSceneId === null
      ? remaining.length
      : remaining.indexOf(beforeSceneId);
  if (insertAt < 0) insertAt = remaining.length;

  const nextIds = [
    ...remaining.slice(0, insertAt),
    sceneId,
    ...remaining.slice(insertAt),
  ];

  if (
    nextIds.length === episode.sceneIds.length &&
    nextIds.every((id, i) => id === episode.sceneIds[i])
  ) {
    return doc;
  }

  const renumber = Boolean(options.renumber);
  const ts = nowIso();
  const scenes = { ...doc.scenes };
  nextIds.forEach((id, i) => {
    const scene = scenes[id];
    if (scene) {
      scenes[id] = {
        ...scene,
        sortOrder: i,
        ...(renumber ? { number: i + 1 } : {}),
        updatedAt: ts,
      };
    }
  });

  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    episodes: {
      ...doc.episodes,
      [episodeId]: { ...episode, sceneIds: nextIds, updatedAt: ts },
    },
    scenes,
  };
}

/** Duplicate a scene and insert the copy immediately after the original. */
export function duplicateScene(
  doc: ScriptDocument,
  sceneId: Id,
): { doc: ScriptDocument; sceneId: Id } {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);
  const episode = doc.episodes[scene.episodeId];
  if (!episode) throw new Error(`Episode ${scene.episodeId} not found`);
  return insertClonedScene(doc, scene, episode.id, {
    afterSceneId: sceneId,
    title: unusedSceneCopyTitle(doc, episode.id, scene.title),
  });
}

/** Copy a scene (cues, notes, credits, comments) onto the end of another episode. */
export function copySceneToEpisode(
  doc: ScriptDocument,
  sceneId: Id,
  destEpisodeId: Id,
): { doc: ScriptDocument; sceneId: Id } {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);
  const dest = doc.episodes[destEpisodeId];
  if (!dest) throw new Error(`Episode ${destEpisodeId} not found`);
  return insertClonedScene(doc, scene, destEpisodeId, {
    afterSceneId: dest.sceneIds[dest.sceneIds.length - 1] ?? null,
    title: unusedSceneCopyTitle(doc, destEpisodeId, scene.title),
  });
}

/**
 * Copy a scene into an episode in another production document.
 * Note-column defs from the source production are merged in so notes stay visible.
 */
export function copySceneToDocument(
  sourceDoc: ScriptDocument,
  sceneId: Id,
  destDoc: ScriptDocument,
  destEpisodeId: Id,
): { doc: ScriptDocument; sceneId: Id } {
  const scene = sourceDoc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);
  const dest = destDoc.episodes[destEpisodeId];
  if (!dest) throw new Error(`Episode ${destEpisodeId} not found`);

  const ts = nowIso();
  const newSceneId = createId();
  const cloned = cloneSceneContent(
    sourceDoc,
    scene,
    newSceneId,
    destEpisodeId,
    ts,
    unusedSceneCopyTitle(destDoc, destEpisodeId, scene.title),
    nextSceneNumber(destDoc, dest),
  );

  const scenes = { ...destDoc.scenes, [newSceneId]: cloned.scene };
  const cues = { ...destDoc.cues, ...cloned.cues };
  const mappedComments = commentsFromCueMap(
    sourceDoc.comments ?? {},
    cloned.cueIdMap,
  );
  const comments = { ...(destDoc.comments ?? {}), ...mappedComments };
  const nextIds = [...dest.sceneIds, newSceneId];
  applySceneOrder(
    scenes,
    dest.id,
    nextIds,
    ts,
    dest.sceneNumberingMode !== "custom",
  );

  return {
    sceneId: newSceneId,
    doc: {
      ...destDoc,
      production: {
        ...destDoc.production,
        noteColumns: mergeNoteColumnDefs(
          destDoc.production.noteColumns,
          sourceDoc.production.noteColumns,
        ),
        updatedAt: ts,
      },
      episodes: {
        ...destDoc.episodes,
        [dest.id]: { ...dest, sceneIds: nextIds, updatedAt: ts },
      },
      scenes,
      cues,
      comments,
    },
  };
}

function commentsFromCueMap(
  comments: Record<Id, Comment>,
  cueIdMap: Map<Id, Id>,
): Record<Id, Comment> {
  const next: Record<Id, Comment> = {};
  for (const comment of Object.values(comments)) {
    const newCueId = cueIdMap.get(comment.cueId);
    if (!newCueId) continue;
    const newId = createId();
    next[newId] = { ...comment, id: newId, cueId: newCueId };
  }
  return next;
}

/**
 * Move a scene to the end of another episode.
 * No-op when the source episode would be left without scenes.
 */
export function moveSceneToEpisode(
  doc: ScriptDocument,
  sceneId: Id,
  destEpisodeId: Id,
): ScriptDocument {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);
  const source = doc.episodes[scene.episodeId];
  const dest = doc.episodes[destEpisodeId];
  if (!source || !dest) throw new Error("Episode not found");
  if (source.id === dest.id) return doc;
  if (source.sceneIds.length <= 1) return doc;
  if (!source.sceneIds.includes(sceneId)) {
    throw new Error(`Scene ${sceneId} not in episode`);
  }

  const ts = nowIso();
  const scenes = { ...doc.scenes };
  const sourceIds = source.sceneIds.filter((id) => id !== sceneId);
  const destIds = [...dest.sceneIds, sceneId];
  const destRenumber = dest.sceneNumberingMode !== "custom";
  scenes[sceneId] = {
    ...scene,
    episodeId: dest.id,
    number: destRenumber ? destIds.length : nextSceneNumber(doc, dest),
    updatedAt: ts,
  };
  applySceneOrder(
    scenes,
    source.id,
    sourceIds,
    ts,
    source.sceneNumberingMode !== "custom",
  );
  applySceneOrder(scenes, dest.id, destIds, ts, destRenumber);

  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    episodes: {
      ...doc.episodes,
      [source.id]: { ...source, sceneIds: sourceIds, updatedAt: ts },
      [dest.id]: { ...dest, sceneIds: destIds, updatedAt: ts },
    },
    scenes,
  };
}

/**
 * Split a scene so `atCueId` (and everything after it) becomes a new scene.
 * `atCueId` must be a cue in the scene other than the first row.
 */
export function splitScene(
  doc: ScriptDocument,
  sceneId: Id,
  atCueId: Id,
): { doc: ScriptDocument; sceneId: Id } {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);
  const episode = doc.episodes[scene.episodeId];
  if (!episode) throw new Error(`Episode ${scene.episodeId} not found`);
  const cueIds = scene.cueIds.filter((id) => doc.cues[id]);
  const splitAt = cueIds.indexOf(atCueId);
  if (splitAt < 1) return { doc, sceneId };

  const keepCueIds = cueIds.slice(0, splitAt);
  const moveCueIds = cueIds.slice(splitAt);
  const ts = nowIso();
  const newSceneId = createId();
  const keepShare = keepCueIds.length / cueIds.length;
  const keepDuration = Math.max(
    0,
    Math.round(scene.durationSeconds * keepShare),
  );
  const moveDuration = Math.max(0, scene.durationSeconds - keepDuration);
  const cues = { ...doc.cues };
  for (const id of moveCueIds) {
    const cue = cues[id];
    if (cue) cues[id] = { ...cue, sceneId: newSceneId, updatedAt: ts };
  }
  clampCueSpans(cues, keepCueIds, ts);
  clampCueSpans(cues, moveCueIds, ts);

  const baseTitle = scene.title.trim() || "Unavngiven scene";
  const scenes = {
    ...doc.scenes,
    [sceneId]: {
      ...scene,
      cueIds: keepCueIds,
      durationSeconds: keepDuration,
      updatedAt: ts,
    },
    [newSceneId]: {
      ...scene,
      id: newSceneId,
      title: unusedSceneTitle(doc, episode.id, `${baseTitle} (fortsat)`),
      durationSeconds: moveDuration,
      cueIds: moveCueIds,
      number: nextSceneNumber(doc, episode),
      crewRoles: copyCrewRoles(scene.crewRoles),
      statusSteps: scene.statusSteps?.map((step) => ({ ...step })),
      statusCompletedIds: undefined,
      updatedAt: ts,
    },
  };
  const nextIds = insertIdAfter(episode.sceneIds, sceneId, newSceneId);
  applySceneOrder(
    scenes,
    episode.id,
    nextIds,
    ts,
    episode.sceneNumberingMode !== "custom",
  );

  return {
    sceneId: newSceneId,
    doc: {
      ...doc,
      production: { ...doc.production, updatedAt: ts },
      episodes: {
        ...doc.episodes,
        [episode.id]: { ...episode, sceneIds: nextIds, updatedAt: ts },
      },
      scenes,
      cues,
    },
  };
}

/**
 * Fold `otherSceneId` into `keepSceneId` (same episode). Cues from the
 * earlier scene come first when the other scene sits above the kept one.
 * `identity` sets the surviving title (and custom number).
 */
export type MergeScenesIdentity = {
  title: string;
  number?: ItemNumber;
};

export function mergeScenes(
  doc: ScriptDocument,
  keepSceneId: Id,
  otherSceneId: Id,
  identity?: MergeScenesIdentity,
): ScriptDocument {
  if (keepSceneId === otherSceneId) return doc;
  const keep = doc.scenes[keepSceneId];
  const other = doc.scenes[otherSceneId];
  if (!keep || !other) throw new Error("Scene not found");
  if (keep.episodeId !== other.episodeId) {
    throw new Error("Scenes must be in the same episode");
  }
  const episode = doc.episodes[keep.episodeId];
  if (!episode) throw new Error(`Episode ${keep.episodeId} not found`);
  if (
    !episode.sceneIds.includes(keepSceneId) ||
    !episode.sceneIds.includes(otherSceneId)
  ) {
    throw new Error("Scene not in episode");
  }

  const ts = nowIso();
  const keepIndex = episode.sceneIds.indexOf(keepSceneId);
  const otherIndex = episode.sceneIds.indexOf(otherSceneId);
  const otherFirst = otherIndex < keepIndex;
  const keepCues = keep.cueIds.filter((id) => doc.cues[id]);
  const otherCues = other.cueIds.filter((id) => doc.cues[id]);
  const nextCueIds = otherFirst
    ? [...otherCues, ...keepCues]
    : [...keepCues, ...otherCues];

  const cues = { ...doc.cues };
  for (const id of otherCues) {
    const cue = cues[id];
    if (cue) cues[id] = { ...cue, sceneId: keepSceneId, updatedAt: ts };
  }
  nextCueIds.forEach((id, i) => {
    const cue = cues[id];
    if (cue) cues[id] = { ...cue, sortOrder: i, updatedAt: ts };
  });
  clampCueSpans(cues, nextCueIds, ts);

  const descKeep = keep.description.trim();
  const descOther = other.description.trim();
  const description = (
    otherFirst ? [descOther, descKeep] : [descKeep, descOther]
  )
    .filter(Boolean)
    .join("\n\n");

  const crewKeep = keep.crewRoles ?? [];
  const crewOther = copyCrewRoles(other.crewRoles) ?? [];
  const crewRoles = otherFirst
    ? [...crewOther, ...crewKeep]
    : [...crewKeep, ...crewOther];

  const completed = [
    ...new Set([
      ...(keep.statusCompletedIds ?? []),
      ...(other.statusCompletedIds ?? []),
    ]),
  ];

  const scenes = { ...doc.scenes };
  scenes[keepSceneId] = {
    ...keep,
    cueIds: nextCueIds,
    durationSeconds: keep.durationSeconds + other.durationSeconds,
    description,
    notes: mergeCueNotes(keep.notes, other.notes),
    crewRoles: crewRoles.length ? crewRoles : undefined,
    statusCompletedIds: completed.length ? completed : undefined,
    updatedAt: ts,
  };
  delete scenes[otherSceneId];

  const nextIds = episode.sceneIds.filter((id) => id !== otherSceneId);
  applySceneOrder(
    scenes,
    episode.id,
    nextIds,
    ts,
    episode.sceneNumberingMode !== "custom",
  );

  if (identity) {
    const kept = scenes[keepSceneId];
    if (kept) {
      const title = identity.title.trim() || kept.title;
      const custom = episode.sceneNumberingMode === "custom";
      const parsed =
        custom && identity.number != null
          ? parseItemNumber(identity.number)
          : null;
      scenes[keepSceneId] = {
        ...kept,
        title,
        ...(parsed ? { number: parsed } : {}),
        updatedAt: ts,
      };
    }
  }

  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    episodes: {
      ...doc.episodes,
      [episode.id]: { ...episode, sceneIds: nextIds, updatedAt: ts },
    },
    scenes,
    cues,
  };
}

export type SceneHeadingKind = "int" | "ext";

export type SceneHeadingFields = Pick<
  Scene,
  "headingKind" | "headingLocation" | "headingTime" | "headingNote"
>;

/** Join selected heading parts with " - " (empty parts omitted). */
export function formatSceneHeading(parts: SceneHeadingFields): string {
  const bits: string[] = [];
  if (parts.headingKind === "int") bits.push("INT");
  if (parts.headingKind === "ext") bits.push("EXT");
  const loc = parts.headingLocation?.trim();
  const time = parts.headingTime?.trim();
  const note = parts.headingNote?.trim();
  if (loc) bits.push(loc);
  if (time) bits.push(time);
  if (note) bits.push(note);
  return bits.join(" - ");
}

/**
 * Parse a stored heading string into parts.
 * Supports "INT - LOC - TIME - NOTE" and legacy "INT. LOC — TIME".
 */
export function parseSceneHeading(raw: string | undefined): SceneHeadingFields {
  const t = (raw ?? "").trim();
  if (!t) {
    return {
      headingKind: undefined,
      headingLocation: undefined,
      headingTime: undefined,
      headingNote: undefined,
    };
  }

  const spaced = t.split(/\s+-\s+/).map((s) => s.trim()).filter(Boolean);
  if (spaced.length >= 1) {
    const first = spaced[0]!.replace(/\.$/, "").toUpperCase();
    if (first === "INT" || first === "EXT") {
      return {
        headingKind: first.toLowerCase() as SceneHeadingKind,
        headingLocation: spaced[1] || undefined,
        headingTime: spaced[2] || undefined,
        headingNote: spaced.slice(3).join(" - ") || undefined,
      };
    }
    // No INT/EXT — treat whole string as note, or split location/time/note
    if (spaced.length === 1) {
      return { headingNote: spaced[0] };
    }
    return {
      headingLocation: spaced[0],
      headingTime: spaced[1],
      headingNote: spaced.slice(2).join(" - ") || undefined,
    };
  }

  const legacy = /^(INT|EXT)\.?\s*(.*?)(?:\s+[—–-]\s*(.+))?$/i.exec(t);
  if (legacy) {
    return {
      headingKind: legacy[1]!.toUpperCase() as SceneHeadingKind,
      headingLocation: legacy[2]?.trim() || undefined,
      headingTime: legacy[3]?.trim() || undefined,
    };
  }

  return { headingNote: t };
}

function applyHeadingFields(scene: Scene, fields: SceneHeadingFields): Scene {
  const next: Scene = {
    ...scene,
    headingKind: fields.headingKind,
    headingLocation: fields.headingLocation?.trim() || undefined,
    headingTime: fields.headingTime?.trim() || undefined,
    headingNote: fields.headingNote?.trim() || undefined,
  };
  const joined = formatSceneHeading(next);
  next.heading = joined || undefined;
  return next;
}

function episodeSceneNumbersFollowOrder(
  doc: ScriptDocument,
  episode: Episode,
): boolean {
  return episode.sceneIds.every((id, i) =>
    itemNumberFollowsIndex(doc.scenes[id]?.number, i),
  );
}

export function updateSceneMeta(
  doc: ScriptDocument,
  sceneId: Id,
  patch: Partial<
    Pick<
      Scene,
      | "title"
      | "description"
      | "durationSeconds"
      | "number"
      | "headerBackground"
      | "heading"
      | "headingKind"
      | "headingLocation"
      | "headingTime"
      | "headingNote"
    >
  >,
): ScriptDocument {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);
  const ts = nowIso();
  let next: Scene = { ...scene, ...patch, updatedAt: ts };
  if (patch.number !== undefined) {
    const parsed = parseItemNumber(patch.number);
    next.number = parsed ?? scene.number;
  }
  if (patch.headerBackground !== undefined) {
    next.headerBackground = patch.headerBackground.trim() || undefined;
  }

  const touchesHeadingParts =
    "headingKind" in patch ||
    "headingLocation" in patch ||
    "headingTime" in patch ||
    "headingNote" in patch;

  if (touchesHeadingParts) {
    next = applyHeadingFields(next, {
      headingKind:
        "headingKind" in patch ? patch.headingKind : next.headingKind,
      headingLocation:
        "headingLocation" in patch
          ? patch.headingLocation
          : next.headingLocation,
      headingTime:
        "headingTime" in patch ? patch.headingTime : next.headingTime,
      headingNote:
        "headingNote" in patch ? patch.headingNote : next.headingNote,
    });
  } else if (patch.heading !== undefined) {
    // Legacy single-string write — parse into parts
    next = applyHeadingFields(next, parseSceneHeading(patch.heading));
  }

  const numberChanged =
    patch.number !== undefined &&
    String(next.number) !== String(scene.number);
  const episode = doc.episodes[scene.episodeId];
  const episodes =
    numberChanged && episode && episode.sceneNumberingMode !== "custom"
      ? {
          ...doc.episodes,
          [scene.episodeId]: {
            ...episode,
            sceneNumberingMode: "custom" as const,
            updatedAt: ts,
          },
        }
      : doc.episodes;

  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    episodes,
    scenes: {
      ...doc.scenes,
      [sceneId]: next,
    },
  };
}

export function updateProductionMeta(
  doc: ScriptDocument,
  patch: Partial<
    Pick<Production, "title" | "useSeasons" | "episodeNumberingMode">
  >,
): ScriptDocument {
  const ts = nowIso();
  return {
    ...doc,
    production: { ...doc.production, ...patch, updatedAt: ts },
  };
}

export function updateSeasonMeta(
  doc: ScriptDocument,
  seasonId: Id,
  patch: Partial<Pick<Season, "title" | "number">>,
): ScriptDocument {
  const season = doc.seasons[seasonId];
  if (!season) throw new Error(`Season ${seasonId} not found`);
  const ts = nowIso();
  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    seasons: {
      ...doc.seasons,
      [seasonId]: { ...season, ...patch, updatedAt: ts },
    },
  };
}

/**
 * Whether season labels should appear in UI (sidebar/print head).
 * On when `useSeasons` is enabled, or when the production already has a
 * non-default season structure (not just implicit “Sæson 1”).
 */
export function productionShowsSeasonChrome(
  production: Pick<Production, "useSeasons" | "seasonIds">,
  season: Season | null | undefined,
): boolean {
  if (production.useSeasons) return Boolean(season);
  if (!season) return false;
  if (production.seasonIds.length > 1) return true;
  return season.number !== 1;
}

/**
 * Enable/disable seasons for a production.
 * Enabling with no seasons yet creates “Sæson 1” and moves loose episodes into it.
 * Disabling only clears the flag — existing season data is kept.
 */
export function setProductionUseSeasons(
  doc: ScriptDocument,
  useSeasons: boolean,
): ScriptDocument {
  const ts = nowIso();
  if (!useSeasons) {
    return {
      ...doc,
      production: {
        ...doc.production,
        useSeasons: false,
        updatedAt: ts,
      },
    };
  }

  let next = {
    ...doc,
    production: {
      ...doc.production,
      useSeasons: true,
      updatedAt: ts,
    },
  };

  if (next.production.seasonIds.length > 0) return next;

  const seasonId = createId();
  const looseEpisodeIds = [...next.production.episodeIds];
  const season: Season = {
    id: seasonId,
    productionId: next.production.id,
    number: 1,
    title: "Sæson 1",
    sortOrder: 0,
    episodeIds: looseEpisodeIds,
    updatedAt: ts,
  };

  const nextEpisodes = { ...next.episodes };
  for (const epId of looseEpisodeIds) {
    const ep = nextEpisodes[epId];
    if (!ep) continue;
    nextEpisodes[epId] = { ...ep, seasonId, updatedAt: ts };
  }

  return {
    ...next,
    production: {
      ...next.production,
      seasonIds: [seasonId],
      episodeIds: [],
      updatedAt: ts,
    },
    seasons: { ...next.seasons, [seasonId]: season },
    episodes: nextEpisodes,
  };
}

export function findCastMember(
  doc: ScriptDocument,
  characterName: string | undefined | null,
): CastMember | undefined {
  const needle = characterName?.trim().toLowerCase();
  if (!needle) return undefined;
  return (doc.production.castMembers ?? []).find(
    (m) => m.name.trim().toLowerCase() === needle,
  );
}

export function addCastMember(
  doc: ScriptDocument,
  name: string,
  opts: { color?: string; showNameAboveLines?: boolean } = {},
): { doc: ScriptDocument; memberId: Id } {
  const trimmed = name.trim();
  if (!trimmed) throw new Error("Cast member name required");
  const existing = findCastMember(doc, trimmed);
  if (existing) return { doc, memberId: existing.id };

  const memberId = createId();
  const ts = nowIso();
  const members = [...(doc.production.castMembers ?? [])];
  const member: CastMember = {
    id: memberId,
    name: trimmed,
    color: opts.color,
    showNameAboveLines: opts.showNameAboveLines !== false,
    sortOrder: members.length,
  };
  members.push(member);
  return {
    memberId,
    doc: {
      ...doc,
      production: {
        ...doc.production,
        castMembers: members,
        updatedAt: ts,
      },
    },
  };
}

export function updateCastMember(
  doc: ScriptDocument,
  memberId: Id,
  patch: Partial<
    Pick<CastMember, "name" | "color" | "showNameAboveLines" | "sortOrder">
  >,
): ScriptDocument {
  const members = [...(doc.production.castMembers ?? [])];
  const index = members.findIndex((m) => m.id === memberId);
  if (index < 0) throw new Error(`Cast member ${memberId} not found`);
  const ts = nowIso();
  const prev = members[index]!;
  members[index] = {
    ...prev,
    ...patch,
    name: patch.name !== undefined ? patch.name.trim() || prev.name : prev.name,
    color:
      patch.color !== undefined
        ? patch.color.trim() || undefined
        : prev.color,
  };
  return {
    ...doc,
    production: {
      ...doc.production,
      castMembers: members,
      updatedAt: ts,
    },
  };
}

export function removeCastMember(
  doc: ScriptDocument,
  memberId: Id,
): ScriptDocument {
  const ts = nowIso();
  return {
    ...doc,
    production: {
      ...doc.production,
      castMembers: (doc.production.castMembers ?? []).filter(
        (m) => m.id !== memberId,
      ),
      updatedAt: ts,
    },
  };
}

export function listCommentsForCue(
  doc: ScriptDocument,
  cueId: Id,
  column?: ScriptColumn,
): Comment[] {
  return Object.values(doc.comments ?? {})
    .filter(
      (c) =>
        c.cueId === cueId &&
        !c.resolved &&
        (column === undefined || c.column === column),
    )
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export function listCommentsForScene(
  doc: ScriptDocument,
  sceneId: Id,
): Comment[] {
  const cueIds = new Set(doc.scenes[sceneId]?.cueIds ?? []);
  return Object.values(doc.comments ?? {})
    .filter((c) => cueIds.has(c.cueId) && !c.resolved)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** Resolved comments newest-first (kept in the document until deleted). */
export function listResolvedComments(doc: ScriptDocument): Comment[] {
  return Object.values(doc.comments ?? {})
    .filter((c) => c.resolved)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function addComment(
  doc: ScriptDocument,
  input: {
    cueId: Id;
    column: ScriptColumn;
    body: string;
    authorId: Id;
    authorName: string;
    from?: number;
    to?: number;
    quote?: string;
  },
): { doc: ScriptDocument; commentId: Id } {
  const body = input.body.trim();
  if (!body) throw new Error("Comment body required");
  if (!doc.cues[input.cueId]) throw new Error(`Cue ${input.cueId} not found`);

  const commentId = createId();
  const comment: Comment = {
    id: commentId,
    cueId: input.cueId,
    column: input.column,
    authorId: input.authorId,
    authorName: input.authorName,
    body,
    createdAt: nowIso(),
    resolved: false,
  };

  if (
    input.from !== undefined &&
    input.to !== undefined &&
    input.to > input.from
  ) {
    comment.from = Math.max(0, Math.floor(input.from));
    comment.to = Math.floor(input.to);
    comment.quote = input.quote?.trim() || undefined;
  }

  return {
    commentId,
    doc: {
      ...doc,
      comments: { ...(doc.comments ?? {}), [commentId]: comment },
      production: { ...doc.production, updatedAt: nowIso() },
    },
  };
}

export function updateComment(
  doc: ScriptDocument,
  commentId: Id,
  patch: Partial<Pick<Comment, "body" | "resolved">>,
): ScriptDocument {
  const prev = doc.comments?.[commentId];
  if (!prev) throw new Error(`Comment ${commentId} not found`);
  const next: Comment = { ...prev, ...patch };
  if (patch.body !== undefined) {
    const body = patch.body.trim();
    if (!body) throw new Error("Comment body required");
    next.body = body;
  }
  return {
    ...doc,
    comments: { ...(doc.comments ?? {}), [commentId]: next },
    production: { ...doc.production, updatedAt: nowIso() },
  };
}

export function removeComment(
  doc: ScriptDocument,
  commentId: Id,
): ScriptDocument {
  const comments = { ...(doc.comments ?? {}) };
  delete comments[commentId];
  return {
    ...doc,
    comments,
    production: { ...doc.production, updatedAt: nowIso() },
  };
}

export function updateEpisodeMeta(
  doc: ScriptDocument,
  episodeId: Id,
  patch: Partial<Pick<Episode, "title" | "number" | "sceneNumberingMode">>,
): ScriptDocument {
  const episode = doc.episodes[episodeId];
  if (!episode) throw new Error(`Episode ${episodeId} not found`);
  const ts = nowIso();
  const nextPatch = { ...patch };
  if (
    nextPatch.sceneNumberingMode != null &&
    nextPatch.sceneNumberingMode !== "sequential" &&
    nextPatch.sceneNumberingMode !== "custom"
  ) {
    delete nextPatch.sceneNumberingMode;
  }
  let nextNumber = episode.number;
  if (nextPatch.number !== undefined) {
    const parsed = parseItemNumber(nextPatch.number);
    if (parsed) nextNumber = parsed;
    delete nextPatch.number;
  }
  const numberChanged = String(nextNumber) !== String(episode.number);
  const nextEpisode: Episode = {
    ...episode,
    ...nextPatch,
    number: nextNumber,
    updatedAt: ts,
  };
  const nextProduction =
    numberChanged && !episodeNumberingIsCustom(doc)
      ? {
          ...doc.production,
          episodeNumberingMode: "custom" as const,
          updatedAt: ts,
        }
      : { ...doc.production, updatedAt: ts };
  return {
    ...doc,
    production: nextProduction,
    episodes: {
      ...doc.episodes,
      [episodeId]: nextEpisode,
    },
  };
}

export function addCue(
  doc: ScriptDocument,
  sceneId: Id,
  opts: {
    visual?: string;
    audio?: string;
    characterName?: string;
  } = {},
): { doc: ScriptDocument; cueId: Id } {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);

  const cueId = createId();
  const ts = nowIso();
  const cue: Cue = {
    id: cueId,
    sceneId,
    sortOrder: scene.cueIds.length,
    visual: {
      content: opts.visual ?? "",
      revision: 1,
      ...emptyLock(),
    },
    audio: {
      content: opts.audio ?? "",
      revision: 1,
      characterName: opts.characterName,
      ...emptyLock(),
    },
    visualRowSpan: 1,
    audioRowSpan: 1,
    updatedAt: ts,
  };

  return {
    cueId,
    doc: withoutCueDeleted(
      {
        ...doc,
        production: { ...doc.production, updatedAt: ts },
        scenes: {
          ...doc.scenes,
          [sceneId]: {
            ...scene,
            cueIds: [...scene.cueIds, cueId],
            updatedAt: ts,
          },
        },
        cues: { ...doc.cues, [cueId]: cue },
      },
      cueId,
    ),
  };
}

/**
 * Insert a new empty cue pair after `afterCueId` (used when pressing Return twice).
 */
export function insertCueAfter(
  doc: ScriptDocument,
  sceneId: Id,
  afterCueId: Id,
): { doc: ScriptDocument; cueId: Id } {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);
  const index = scene.cueIds.indexOf(afterCueId);
  if (index < 0) throw new Error(`Cue ${afterCueId} not in scene`);

  const cueId = createId();
  const ts = nowIso();
  const cue: Cue = {
    id: cueId,
    sceneId,
    sortOrder: index + 1,
    visual: emptyColumn(),
    audio: emptyColumn(),
    visualRowSpan: 1,
    audioRowSpan: 1,
    updatedAt: ts,
  };

  const nextIds = [...scene.cueIds];
  nextIds.splice(index + 1, 0, cueId);

  const cues = { ...doc.cues, [cueId]: cue };
  nextIds.forEach((id, sortOrder) => {
    const existing = cues[id];
    if (existing) cues[id] = { ...existing, sortOrder };
  });

  return {
    cueId,
    doc: withoutCueDeleted(
      {
        ...doc,
        production: { ...doc.production, updatedAt: ts },
        scenes: {
          ...doc.scenes,
          [sceneId]: { ...scene, cueIds: nextIds, updatedAt: ts },
        },
        cues,
      },
      cueId,
    ),
  };
}

/** Insert a new empty cue pair before `beforeCueId`. */
export function insertCueBefore(
  doc: ScriptDocument,
  sceneId: Id,
  beforeCueId: Id,
): { doc: ScriptDocument; cueId: Id } {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);
  const index = scene.cueIds.indexOf(beforeCueId);
  if (index < 0) throw new Error(`Cue ${beforeCueId} not in scene`);

  const cueId = createId();
  const ts = nowIso();
  const cue: Cue = {
    id: cueId,
    sceneId,
    sortOrder: index,
    visual: emptyColumn(),
    audio: emptyColumn(),
    visualRowSpan: 1,
    audioRowSpan: 1,
    updatedAt: ts,
  };

  const nextIds = [...scene.cueIds];
  nextIds.splice(index, 0, cueId);

  const cues = { ...doc.cues, [cueId]: cue };
  nextIds.forEach((id, sortOrder) => {
    const existing = cues[id];
    if (existing) cues[id] = { ...existing, sortOrder };
  });

  return {
    cueId,
    doc: withoutCueDeleted(
      {
        ...doc,
        production: { ...doc.production, updatedAt: ts },
        scenes: {
          ...doc.scenes,
          [sceneId]: { ...scene, cueIds: nextIds, updatedAt: ts },
        },
        cues,
      },
      cueId,
    ),
  };
}

function mergeCueSideContents(base: string, extras: string[]): string {
  const parts = [base, ...extras].filter((c) => !isCueSideContentEmpty(c));
  return parts
    .map((c) =>
      /^\s*</.test(c)
        ? c.replace(/(?:<p>(?:\s|&nbsp;)*<\/p>)+$/i, "").trim()
        : c
            .split("\n")
            .map(
              (line) =>
                `<p>${line
                  .replace(/&/g, "&amp;")
                  .replace(/</g, "&lt;")
                  .replace(/>/g, "&gt;")}</p>`,
            )
            .join(""),
    )
    .join("");
}

/** True when cue cell content has no visible text (plain or TipTap HTML). */
export function isCueSideContentEmpty(content: string): boolean {
  if (!content || !content.trim()) return true;
  if (!/^\s*</.test(content)) return !content.trim();
  const plain = content
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .trim();
  return !plain;
}

/** Plain text from a cue side (strips TipTap HTML marks/tags). */
export function cueSidePlainText(content: string): string {
  if (!content) return "";
  if (!/^\s*</.test(content)) return content;
  return content
    .replace(/\r\n?/g, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/\n+$/u, "")
    .replace(/^\n+/u, "")
    .replace(/\n{3,}/g, "\n\n");
}

/** True when both visual and audio sides have no text (and no character cue). */
export function isCuePairEmpty(cue: Cue): boolean {
  return (
    isCueSideContentEmpty(cue.visual.content) &&
    isCueSideContentEmpty(cue.audio.content) &&
    !cue.visual.characterName?.trim() &&
    !cue.audio.characterName?.trim()
  );
}

/**
 * Remove a cue pair from its scene. Keeps at least one cue.
 * Shrinks covering visualRowSpan / audioRowSpan when a covered row is removed.
 */
export function removeCue(
  doc: ScriptDocument,
  sceneId: Id,
  cueId: Id,
): ScriptDocument {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);
  const index = scene.cueIds.indexOf(cueId);
  if (index < 0) throw new Error(`Cue ${cueId} not in scene`);
  if (scene.cueIds.length <= 1) return doc;

  const ts = nowIso();
  const nextIds = scene.cueIds.filter((id) => id !== cueId);
  const cues = { ...doc.cues };
  delete cues[cueId];

  // Shrink any owner whose visual or audio span covered this index
  for (let i = 0; i < index; i++) {
    const ownerId = scene.cueIds[i]!;
    const owner = cues[ownerId] ?? doc.cues[ownerId];
    if (!owner || !cues[ownerId]) continue;
    let next = cues[ownerId]!;
    const vSpan = Math.max(1, owner.visualRowSpan ?? 1);
    if (i + vSpan > index) {
      next = {
        ...next,
        visualRowSpan: Math.max(1, vSpan - 1),
        updatedAt: ts,
      };
    }
    const aSpan = Math.max(1, owner.audioRowSpan ?? 1);
    if (i + aSpan > index) {
      next = {
        ...next,
        audioRowSpan: Math.max(1, aSpan - 1),
        updatedAt: ts,
      };
    }
    if (next !== cues[ownerId]) cues[ownerId] = next;
  }

  nextIds.forEach((id, sortOrder) => {
    const cue = cues[id];
    if (cue) cues[id] = { ...cue, sortOrder, updatedAt: ts };
  });

  return withCueDeleted(
    {
      ...doc,
      production: { ...doc.production, updatedAt: ts },
      scenes: {
        ...doc.scenes,
        [sceneId]: { ...scene, cueIds: nextIds, updatedAt: ts },
      },
      cues,
      comments: Object.fromEntries(
        Object.entries(doc.comments ?? {}).filter(
          ([, comment]) => comment.cueId !== cueId,
        ),
      ),
    },
    cueId,
    ts,
  );
}

export type DualColumnRowSpan = {
  /** 0 = covered by an earlier visual cell. */
  visualSpan: number;
  /** 0 = covered by an earlier audio cell. */
  audioSpan: number;
};

/**
 * Table rowspans for billedside/lydside. 0 means a previous cell covers this
 * row. Every row is guaranteed at least one of the two spans is > 0, so a
 * row can never render with both cells missing.
 */
export function layoutDualColumnSpans(
  cues: readonly Pick<Cue, "visualRowSpan" | "audioRowSpan">[],
): DualColumnRowSpan[] {
  const rows: DualColumnRowSpan[] = [];
  let visualCoveredUntil = -1;
  let audioCoveredUntil = -1;

  for (let i = 0; i < cues.length; i++) {
    const cue = cues[i]!;
    let visualSpan = 0;
    let audioSpan = 0;

    if (i > visualCoveredUntil) {
      const v = Math.max(1, cue.visualRowSpan ?? 1);
      visualSpan = Math.min(v, cues.length - i);
      visualCoveredUntil = i + visualSpan - 1;
    }

    if (i > audioCoveredUntil) {
      let a = Math.max(1, cue.audioRowSpan ?? 1);
      if (visualSpan > 1 || (a > 1 && Math.max(1, cue.visualRowSpan ?? 1) > 1)) {
        a = 1;
      }
      if (visualSpan === 0) a = 1;
      audioSpan = Math.min(a, cues.length - i);
      audioCoveredUntil = i + audioSpan - 1;
    } else if (visualSpan === 0) {
      for (let k = rows.length - 1; k >= 0; k--) {
        const owner = rows[k]!;
        if (owner.audioSpan > 0 && k + owner.audioSpan - 1 >= i) {
          rows[k] = { ...owner, audioSpan: Math.max(1, i - k) };
          break;
        }
      }
      audioSpan = 1;
      audioCoveredUntil = i;
    }

    rows.push({ visualSpan, audioSpan });
  }
  return rows;
}

function clipOpposingSpans(
  sceneCueIds: Id[],
  cues: Record<Id, Cue>,
  ownerIndex: number,
  coverSpan: number,
  covering: "visual" | "audio",
  ts?: string,
): Record<Id, Cue> {
  if (coverSpan <= 1) return cues;
  const coverEnd = ownerIndex + coverSpan - 1;
  const opposing: "visualRowSpan" | "audioRowSpan" =
    covering === "visual" ? "audioRowSpan" : "visualRowSpan";
  let next = cues;

  const write = (id: Id, cue: Cue) => {
    if (next === cues) next = { ...cues };
    next[id] = ts ? { ...cue, updatedAt: ts } : cue;
  };

  for (let j = 0; j < sceneCueIds.length; j++) {
    const id = sceneCueIds[j]!;
    const other = next[id];
    if (!other) continue;
    const span = Math.max(1, other[opposing] ?? 1);
    if (j > ownerIndex && j <= coverEnd) {
      if (span !== 1) {
        write(id, { ...other, [opposing]: 1 });
      }
    } else if (j <= ownerIndex && j + span - 1 > ownerIndex) {
      const clipped = ownerIndex - j + 1;
      if (clipped < span) {
        write(id, { ...other, [opposing]: Math.max(1, clipped) });
      }
    }
  }
  return next;
}

function repairSceneRowSpans(
  sceneCueIds: Id[],
  cues: Record<Id, Cue>,
): Record<Id, Cue> {
  let next = cues;
  for (let i = 0; i < sceneCueIds.length; i++) {
    const cue = next[sceneCueIds[i]!];
    if (!cue) continue;
    const v = Math.max(1, cue.visualRowSpan ?? 1);
    const a = Math.max(1, cue.audioRowSpan ?? 1);
    if (v > 1 && a > 1) {
      if (next === cues) next = { ...cues };
      next[cue.id] = { ...cue, audioRowSpan: 1 };
    }
  }
  for (let i = 0; i < sceneCueIds.length; i++) {
    const cue = next[sceneCueIds[i]!];
    if (!cue) continue;
    const v = Math.max(1, cue.visualRowSpan ?? 1);
    if (v > 1) {
      next = clipOpposingSpans(sceneCueIds, next, i, v, "visual");
    }
  }
  for (let i = 0; i < sceneCueIds.length; i++) {
    const cue = next[sceneCueIds[i]!];
    if (!cue) continue;
    const a = Math.max(1, cue.audioRowSpan ?? 1);
    if (a > 1) {
      next = clipOpposingSpans(sceneCueIds, next, i, a, "audio");
    }
  }
  return next;
}

/**
 * Let billedside on this cue cover `span` consecutive rows (including itself).
 * Following rows keep their own audio cells until the span ends.
 *
 * When extending over cues that have billedside text, pass `coveredVisual`:
 * - `"clear"` — empty those visual sides
 * - `"merge"` — append their text onto this cue's visual, then clear them
 *
 * Expanding visual (span > 1) resets this cue's audioRowSpan to 1.
 */
export function setVisualRowSpan(
  doc: ScriptDocument,
  cueId: Id,
  span: number,
  options?: { coveredVisual?: "clear" | "merge" },
): ScriptDocument {
  const cue = doc.cues[cueId];
  if (!cue) throw new Error(`Cue ${cueId} not found`);
  const scene = doc.scenes[cue.sceneId];
  if (!scene) throw new Error(`Scene ${cue.sceneId} not found`);
  const index = scene.cueIds.indexOf(cueId);
  if (index < 0) throw new Error(`Cue not in scene`);

  const maxSpan = scene.cueIds.length - index;
  const nextSpan = Math.max(1, Math.min(Math.floor(span), maxSpan));
  const prevSpan = Math.max(1, cue.visualRowSpan ?? 1);
  const ts = nowIso();

  let cues = { ...doc.cues };

  if (nextSpan > prevSpan && options?.coveredVisual) {
    const extras: string[] = [];
    for (let i = index + prevSpan; i < index + nextSpan; i++) {
      const id = scene.cueIds[i]!;
      const other = cues[id];
      if (!other) continue;
      const text = cueSidePlainText(other.visual.content).trim();
      if (text && options.coveredVisual === "merge") {
        extras.push(other.visual.content);
      }
      if (text || options.coveredVisual === "clear") {
        const side = other.visual;
        cues[id] = {
          ...other,
          visual: {
            ...side,
            content: "",
            revision: side.revision + 1,
          },
          updatedAt: ts,
        };
      }
    }
    if (options.coveredVisual === "merge" && extras.length > 0) {
      const owner = cues[cueId]!;
      const merged = mergeCueSideContents(owner.visual.content, extras);
      cues[cueId] = {
        ...owner,
        visual: {
          ...owner.visual,
          content: merged,
          revision: owner.visual.revision + 1,
        },
        updatedAt: ts,
      };
    }
  }

  // Reset visualRowSpan on cues that would overlap this new span
  for (let i = 0; i < scene.cueIds.length; i++) {
    const id = scene.cueIds[i]!;
    const other = cues[id];
    if (!other) continue;
    if (id === cueId) {
      cues[id] = {
        ...other,
        visualRowSpan: nextSpan,
        // Mutual exclusion: expanding visual clears audio span on owner
        audioRowSpan: nextSpan > 1 ? 1 : Math.max(1, other.audioRowSpan ?? 1),
        updatedAt: ts,
      };
      continue;
    }
    const otherSpan = Math.max(1, other.visualRowSpan ?? 1);
    const otherEnd = i + otherSpan - 1;
    const thisEnd = index + nextSpan - 1;
    const overlaps =
      (i <= index && otherEnd >= index) || (i > index && i <= thisEnd);
    if (overlaps && otherSpan > 1) {
      cues[id] = { ...other, visualRowSpan: 1, updatedAt: ts };
    }
  }

  cues = clipOpposingSpans(scene.cueIds, cues, index, nextSpan, "visual", ts);

  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    cues,
  };
}

/**
 * Let lydside on this cue cover `span` consecutive rows (including itself).
 * Following rows keep their own visual cells until the span ends.
 *
 * When extending over cues that have lydside text, pass `coveredAudio`:
 * - `"clear"` — empty those audio sides
 * - `"merge"` — append their text onto this cue's audio, then clear them
 *
 * Expanding audio (span > 1) resets this cue's visualRowSpan to 1.
 */
export function setAudioRowSpan(
  doc: ScriptDocument,
  cueId: Id,
  span: number,
  options?: { coveredAudio?: "clear" | "merge" },
): ScriptDocument {
  const cue = doc.cues[cueId];
  if (!cue) throw new Error(`Cue ${cueId} not found`);
  const scene = doc.scenes[cue.sceneId];
  if (!scene) throw new Error(`Scene ${cue.sceneId} not found`);
  const index = scene.cueIds.indexOf(cueId);
  if (index < 0) throw new Error(`Cue not in scene`);

  const maxSpan = scene.cueIds.length - index;
  const nextSpan = Math.max(1, Math.min(Math.floor(span), maxSpan));
  const prevSpan = Math.max(1, cue.audioRowSpan ?? 1);
  const ts = nowIso();

  let cues = { ...doc.cues };

  if (nextSpan > prevSpan && options?.coveredAudio) {
    const extras: string[] = [];
    for (let i = index + prevSpan; i < index + nextSpan; i++) {
      const id = scene.cueIds[i]!;
      const other = cues[id];
      if (!other) continue;
      const text = cueSidePlainText(other.audio.content).trim();
      if (text && options.coveredAudio === "merge") {
        extras.push(other.audio.content);
      }
      if (text || options.coveredAudio === "clear") {
        const side = other.audio;
        cues[id] = {
          ...other,
          audio: {
            ...side,
            content: "",
            revision: side.revision + 1,
          },
          updatedAt: ts,
        };
      }
    }
    if (options.coveredAudio === "merge" && extras.length > 0) {
      const owner = cues[cueId]!;
      const merged = mergeCueSideContents(owner.audio.content, extras);
      cues[cueId] = {
        ...owner,
        audio: {
          ...owner.audio,
          content: merged,
          revision: owner.audio.revision + 1,
        },
        updatedAt: ts,
      };
    }
  }

  // Reset audioRowSpan on cues that would overlap this new span
  for (let i = 0; i < scene.cueIds.length; i++) {
    const id = scene.cueIds[i]!;
    const other = cues[id];
    if (!other) continue;
    if (id === cueId) {
      cues[id] = {
        ...other,
        audioRowSpan: nextSpan,
        // Mutual exclusion: expanding audio clears visual span on owner
        visualRowSpan: nextSpan > 1 ? 1 : Math.max(1, other.visualRowSpan ?? 1),
        updatedAt: ts,
      };
      continue;
    }
    const otherSpan = Math.max(1, other.audioRowSpan ?? 1);
    const otherEnd = i + otherSpan - 1;
    const thisEnd = index + nextSpan - 1;
    const overlaps =
      (i <= index && otherEnd >= index) || (i > index && i <= thisEnd);
    if (overlaps && otherSpan > 1) {
      cues[id] = { ...other, audioRowSpan: 1, updatedAt: ts };
    }
  }

  cues = clipOpposingSpans(scene.cueIds, cues, index, nextSpan, "audio", ts);

  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    cues,
  };
}

/** Non-empty billedside texts that would become covered by extending to `span`. */
export function listCoveredVisualConflicts(
  doc: ScriptDocument,
  cueId: Id,
  span: number,
): { cueId: Id; preview: string }[] {
  const cue = doc.cues[cueId];
  if (!cue) return [];
  const scene = doc.scenes[cue.sceneId];
  if (!scene) return [];
  const index = scene.cueIds.indexOf(cueId);
  if (index < 0) return [];
  const prevSpan = Math.max(1, cue.visualRowSpan ?? 1);
  const maxSpan = scene.cueIds.length - index;
  const nextSpan = Math.max(1, Math.min(Math.floor(span), maxSpan));
  if (nextSpan <= prevSpan) return [];

  const out: { cueId: Id; preview: string }[] = [];
  for (let i = index + prevSpan; i < index + nextSpan; i++) {
    const id = scene.cueIds[i]!;
    const other = doc.cues[id];
    if (!other) continue;
    const text = cueSidePlainText(other.visual.content).trim();
    if (!text) continue;
    out.push({
      cueId: id,
      preview: text.length > 120 ? `${text.slice(0, 120)}…` : text,
    });
  }
  return out;
}

/** Non-empty lydside texts that would become covered by extending to `span`. */
export function listCoveredAudioConflicts(
  doc: ScriptDocument,
  cueId: Id,
  span: number,
): { cueId: Id; preview: string }[] {
  const cue = doc.cues[cueId];
  if (!cue) return [];
  const scene = doc.scenes[cue.sceneId];
  if (!scene) return [];
  const index = scene.cueIds.indexOf(cueId);
  if (index < 0) return [];
  const prevSpan = Math.max(1, cue.audioRowSpan ?? 1);
  const maxSpan = scene.cueIds.length - index;
  const nextSpan = Math.max(1, Math.min(Math.floor(span), maxSpan));
  if (nextSpan <= prevSpan) return [];

  const out: { cueId: Id; preview: string }[] = [];
  for (let i = index + prevSpan; i < index + nextSpan; i++) {
    const id = scene.cueIds[i]!;
    const other = doc.cues[id];
    if (!other) continue;
    const text = cueSidePlainText(other.audio.content).trim();
    if (!text) continue;
    out.push({
      cueId: id,
      preview: text.length > 120 ? `${text.slice(0, 120)}…` : text,
    });
  }
  return out;
}

/** Toggle or set whether a horizontal rule prints below this cue row. */
export function setCuePrintBorderBelow(
  doc: ScriptDocument,
  cueId: Id,
  printBorderBelow: boolean,
): ScriptDocument {
  const cue = doc.cues[cueId];
  if (!cue) throw new Error(`Cue ${cueId} not found`);
  const ts = nowIso();
  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    cues: {
      ...doc.cues,
      [cueId]: { ...cue, printBorderBelow, updatedAt: ts },
    },
  };
}

export function setCueSourceNote(
  doc: ScriptDocument,
  cueId: Id,
  sourceNote: string,
): ScriptDocument {
  const columns = getNoteColumns(doc.production);
  const primaryId = columns[0]?.id;
  if (primaryId) {
    return setCueNote(doc, cueId, primaryId, sourceNote);
  }
  const cue = doc.cues[cueId];
  if (!cue) throw new Error(`Cue ${cueId} not found`);
  const ts = nowIso();
  const trimmed = sourceNote.trimEnd();
  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    cues: {
      ...doc.cues,
      [cueId]: {
        ...cue,
        sourceNote: trimmed.length > 0 ? trimmed : undefined,
        updatedAt: ts,
      },
    },
  };
}

export function setCueNote(
  doc: ScriptDocument,
  cueId: Id,
  columnId: Id,
  text: string,
): ScriptDocument {
  const cue = doc.cues[cueId];
  if (!cue) throw new Error(`Cue ${cueId} not found`);
  const ts = nowIso();
  const trimmed = text.trimEnd();
  const empty = isCueSideContentEmpty(trimmed);
  const nextNotes = { ...(cue.notes ?? {}) };
  if (!empty) nextNotes[columnId] = trimmed;
  else delete nextNotes[columnId];
  const primaryId = getNoteColumns(doc.production)[0]?.id;
  const sourceNote =
    columnId === primaryId
      ? empty
        ? undefined
        : trimmed
      : cue.sourceNote;
  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    cues: {
      ...doc.cues,
      [cueId]: {
        ...cue,
        notes: Object.keys(nextNotes).length > 0 ? nextNotes : undefined,
        sourceNote,
        updatedAt: ts,
      },
    },
  };
}

export function setSceneNote(
  doc: ScriptDocument,
  sceneId: Id,
  columnId: Id,
  text: string,
): ScriptDocument {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);
  const ts = nowIso();
  const trimmed = text.trimEnd();
  const empty = isCueSideContentEmpty(trimmed);
  const nextNotes = { ...(scene.notes ?? {}) };
  if (!empty) nextNotes[columnId] = trimmed;
  else delete nextNotes[columnId];
  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    scenes: {
      ...doc.scenes,
      [sceneId]: {
        ...scene,
        notes: Object.keys(nextNotes).length > 0 ? nextNotes : undefined,
        updatedAt: ts,
      },
    },
  };
}

export function setProductionNoteColumns(
  doc: ScriptDocument,
  noteColumns: NoteColumnDef[],
): ScriptDocument {
  const ts = nowIso();
  const normalized = normalizeNoteColumns(noteColumns);
  return {
    ...doc,
    production: {
      ...doc.production,
      noteColumns: normalized,
      updatedAt: ts,
    },
  };
}

function normalizeHiddenNoteColumnIds(raw: unknown): Id[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const ids = raw.filter((x): x is Id => typeof x === "string" && x.length > 0);
  const unique = [...new Set(ids)];
  return unique.length > 0 ? unique : undefined;
}

/** Production columns minus those hidden for the episode. Always ≥1 when production has any. */
export function getVisibleNoteColumns(
  doc: ScriptDocument,
  episodeId: Id | null | undefined,
): NoteColumnDef[] {
  const all = getNoteColumns(doc.production);
  if (all.length === 0) return createDefaultNoteColumns();
  if (!episodeId) return all;
  const episode = doc.episodes[episodeId];
  const hidden = new Set(episode?.hiddenNoteColumnIds ?? []);
  if (hidden.size === 0) return all;
  const visible = all.filter((c) => !hidden.has(c.id));
  return visible.length > 0 ? visible : all;
}

function cueHasNoteColumnContent(
  cue: Cue,
  columnId: Id,
  primaryColumnId: Id | null,
): boolean {
  return !isCueSideContentEmpty(
    getCueNote(cue, columnId, primaryColumnId),
  );
}

function episodeHasNoteColumnContent(
  doc: ScriptDocument,
  episodeId: Id,
  columnId: Id,
  primaryColumnId: Id | null,
): boolean {
  for (const scene of getEpisodeScenes(doc, episodeId)) {
    if (!isCueSideContentEmpty(getSceneNote(scene, columnId))) return true;
    for (const cue of getSceneCues(doc, scene.id)) {
      if (cueHasNoteColumnContent(cue, columnId, primaryColumnId)) return true;
    }
  }
  return false;
}

export type NoteColumnContentReport = {
  currentEpisodeEmpty: boolean;
  /** Other episodes (not current) that have non-empty notes in this column. */
  otherEpisodesWithContent: Episode[];
  /** True when no episode in the production has content in this column. */
  allEpisodesEmpty: boolean;
};

/** Where a note column still has text — for the remove-column confirm dialog. */
export function reportNoteColumnContent(
  doc: ScriptDocument,
  columnId: Id,
  currentEpisodeId: Id,
): NoteColumnContentReport {
  const primaryId = getNoteColumns(doc.production)[0]?.id ?? null;
  const episodes = listEpisodes(doc);
  const withContent = episodes.filter((ep) =>
    episodeHasNoteColumnContent(doc, ep.id, columnId, primaryId),
  );
  const currentEmpty = !withContent.some((ep) => ep.id === currentEpisodeId);
  const otherEpisodesWithContent = withContent.filter(
    (ep) => ep.id !== currentEpisodeId,
  );
  return {
    currentEpisodeEmpty: currentEmpty,
    otherEpisodesWithContent,
    allEpisodesEmpty: withContent.length === 0,
  };
}

function clearNoteColumnOnCue(
  cue: Cue,
  columnId: Id,
  primaryColumnId: Id | null,
  ts: string,
): Cue {
  const nextNotes = { ...(cue.notes ?? {}) };
  delete nextNotes[columnId];
  const sourceNote =
    primaryColumnId && columnId === primaryColumnId
      ? undefined
      : cue.sourceNote;
  return {
    ...cue,
    notes: Object.keys(nextNotes).length > 0 ? nextNotes : undefined,
    sourceNote,
    updatedAt: ts,
  };
}

function clearNoteColumnOnScene(scene: Scene, columnId: Id, ts: string): Scene {
  if (!scene.notes?.[columnId]) return scene;
  const nextNotes = { ...scene.notes };
  delete nextNotes[columnId];
  return {
    ...scene,
    notes: Object.keys(nextNotes).length > 0 ? nextNotes : undefined,
    updatedAt: ts,
  };
}

function clearNoteColumnInEpisodeCues(
  doc: ScriptDocument,
  episodeId: Id,
  columnId: Id,
  primaryColumnId: Id | null,
  ts: string,
): { cues: Record<Id, Cue>; scenes: Record<Id, Scene> } {
  const cues = { ...doc.cues };
  const scenes = { ...doc.scenes };
  for (const scene of getEpisodeScenes(doc, episodeId)) {
    scenes[scene.id] = clearNoteColumnOnScene(scene, columnId, ts);
    for (const cueId of scene.cueIds) {
      const cue = cues[cueId];
      if (!cue) continue;
      cues[cueId] = clearNoteColumnOnCue(cue, columnId, primaryColumnId, ts);
    }
  }
  return { cues, scenes };
}

/** Hide a production note column in one episode and clear its notes there. */
export function hideNoteColumnInEpisode(
  doc: ScriptDocument,
  episodeId: Id,
  columnId: Id,
): ScriptDocument {
  const episode = doc.episodes[episodeId];
  if (!episode) throw new Error(`Episode ${episodeId} not found`);
  const visible = getVisibleNoteColumns(doc, episodeId);
  if (visible.length <= 1) {
    throw new Error("Cannot hide the last visible note column");
  }
  if (!visible.some((c) => c.id === columnId)) return doc;

  const ts = nowIso();
  const primaryId = getNoteColumns(doc.production)[0]?.id ?? null;
  const hidden = new Set(episode.hiddenNoteColumnIds ?? []);
  hidden.add(columnId);
  const { cues, scenes } = clearNoteColumnInEpisodeCues(
    doc,
    episodeId,
    columnId,
    primaryId,
    ts,
  );

  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    episodes: {
      ...doc.episodes,
      [episodeId]: {
        ...episode,
        hiddenNoteColumnIds: [...hidden],
        updatedAt: ts,
      },
    },
    scenes,
    cues,
  };
}

/**
 * Remove a note column from the production (all episodes), clear its notes,
 * and drop it from per-episode hidden lists.
 */
export function removeNoteColumnEverywhere(
  doc: ScriptDocument,
  columnId: Id,
): ScriptDocument {
  const columns = getNoteColumns(doc.production);
  if (columns.length <= 1) {
    throw new Error("Cannot remove the last note column");
  }
  if (!columns.some((c) => c.id === columnId)) return doc;

  const ts = nowIso();
  const primaryId = columns[0]?.id ?? null;
  const nextColumns = columns
    .filter((c) => c.id !== columnId)
    .map((c, i) => ({ ...c, sortOrder: i }));

  const cues = { ...doc.cues };
  for (const [cueId, cue] of Object.entries(cues)) {
    cues[cueId] = clearNoteColumnOnCue(cue, columnId, primaryId, ts);
  }

  const scenes: Record<Id, Scene> = {};
  for (const [id, scene] of Object.entries(doc.scenes)) {
    scenes[id] = clearNoteColumnOnScene(scene, columnId, ts);
  }

  const episodes: Record<Id, Episode> = {};
  for (const [id, ep] of Object.entries(doc.episodes)) {
    const hidden = (ep.hiddenNoteColumnIds ?? []).filter((x) => x !== columnId);
    episodes[id] = {
      ...ep,
      hiddenNoteColumnIds: hidden.length > 0 ? hidden : undefined,
      updatedAt: ts,
    };
  }

  return {
    ...doc,
    production: {
      ...doc.production,
      noteColumns: nextColumns,
      updatedAt: ts,
    },
    episodes,
    scenes,
    cues,
  };
}

/** Update setup meta: camera number/note and/or position note. */
export function setCueCameraMeta(
  doc: ScriptDocument,
  cueId: Id,
  patch: {
    cameraNumber?: number | null;
    cameraNote?: string | null;
    positionNote?: string | null;
  },
): ScriptDocument {
  const cue = doc.cues[cueId];
  if (!cue) throw new Error(`Cue ${cueId} not found`);
  const ts = nowIso();
  let cameraNumber = cue.cameraNumber;
  if ("cameraNumber" in patch) {
    if (patch.cameraNumber == null) {
      cameraNumber = undefined;
    } else {
      cameraNumber = normalizeCameraNumber(patch.cameraNumber);
    }
  }
  let cameraNote = cue.cameraNote;
  if ("cameraNote" in patch) {
    if (patch.cameraNote == null) {
      cameraNote = undefined;
    } else {
      // Keep spaces while typing; only clear when the field is empty.
      cameraNote = patch.cameraNote.length > 0 ? patch.cameraNote : undefined;
    }
  }
  let positionNote = cue.positionNote;
  if ("positionNote" in patch) {
    if (patch.positionNote == null) {
      positionNote = undefined;
    } else {
      positionNote =
        patch.positionNote.length > 0 ? patch.positionNote : undefined;
    }
  }
  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    cues: {
      ...doc.cues,
      [cueId]: {
        ...cue,
        cameraNumber,
        cameraNote,
        positionNote,
        updatedAt: ts,
      },
    },
  };
}

function normalizeCameraNumber(raw: unknown): number | undefined {
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n) || n < 1) return undefined;
  return Math.min(n, 99);
}

export function createDefaultStatusSteps(): StatusStepDef[] {
  return DEFAULT_STATUS_STEPS.map((step, i) => ({
    id: createId(),
    label: step.label,
    sortOrder: i,
  }));
}

/** Default + production-specific job titles, A–Å, for credit chips. */
export function getCrewJobTitleOptions(
  production: Pick<Production, "crewJobTitles"> | null | undefined,
): string[] {
  return [
    ...DEFAULT_CREW_JOB_TITLES,
    ...normalizeCrewJobTitles(production?.crewJobTitles),
  ].sort((a, b) => a.localeCompare(b, "da", { sensitivity: "base" }));
}

export function isDefaultCrewJobTitle(title: string): boolean {
  const key = title.trim().toLowerCase();
  if (!key) return false;
  return DEFAULT_CREW_JOB_TITLES.some((t) => t.toLowerCase() === key);
}

/** Remember a finished custom job title (call on blur, not per keystroke). */
export function addCrewJobTitle(
  doc: ScriptDocument,
  title: string,
): ScriptDocument {
  const trimmed = title.trim();
  if (!trimmed || isDefaultCrewJobTitle(trimmed)) return doc;
  return rememberCrewJobTitlesFromRoles(
    doc,
    [{ id: "", jobTitle: trimmed, people: [], sortOrder: 0 }],
    nowIso(),
  );
}

/** Remove a custom job-title chip (defaults cannot be removed). */
export function removeCrewJobTitle(
  doc: ScriptDocument,
  title: string,
): ScriptDocument {
  const key = title.trim().toLowerCase();
  if (!key || isDefaultCrewJobTitle(title)) return doc;
  const extras = normalizeCrewJobTitles(doc.production.crewJobTitles).filter(
    (t) => t.toLowerCase() !== key,
  );
  if (extras.length === (doc.production.crewJobTitles?.length ?? 0)) {
    return doc;
  }
  const ts = nowIso();
  return {
    ...doc,
    production: {
      ...doc.production,
      crewJobTitles: extras.length ? extras : undefined,
      updatedAt: ts,
    },
  };
}

export function createDefaultNoteColumns(): NoteColumnDef[] {
  return [{ id: createId(), title: "Noter", sortOrder: 0 }];
}

export function getNoteColumns(
  production: Pick<Production, "noteColumns"> | null | undefined,
): NoteColumnDef[] {
  return [...(production?.noteColumns ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );
}

export function getCueNote(
  cue: Pick<Cue, "notes" | "sourceNote">,
  columnId: Id | null | undefined,
  primaryColumnId?: Id | null,
): string {
  if (!columnId) return "";
  const mapped = cue.notes?.[columnId];
  if (typeof mapped === "string") return mapped;
  if (primaryColumnId && columnId === primaryColumnId) {
    return cue.sourceNote ?? "";
  }
  return "";
}

export function getSceneNote(
  scene: Pick<Scene, "notes">,
  columnId: Id | null | undefined,
): string {
  if (!columnId) return "";
  const mapped = scene.notes?.[columnId];
  return typeof mapped === "string" ? mapped : "";
}

export function cueHasNotes(
  cue: Pick<Cue, "notes" | "sourceNote">,
): boolean {
  if (cue.sourceNote && !isCueSideContentEmpty(cue.sourceNote)) return true;
  return Object.values(cue.notes ?? {}).some(
    (v) => !isCueSideContentEmpty(v),
  );
}

export function sceneHasNotes(scene: Pick<Scene, "notes">): boolean {
  return Object.values(scene.notes ?? {}).some(
    (v) => !isCueSideContentEmpty(v),
  );
}

function nonEmptyNoteText(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trimEnd();
  if (isCueSideContentEmpty(trimmed)) return undefined;
  return trimmed;
}

/**
 * Union of note-column texts. A remote/incoming cue must never drop a
 * non-empty local note just because that key is missing or blank.
 */
export function mergeCueNotes(
  local?: Record<Id, string>,
  remote?: Record<Id, string>,
): Record<Id, string> | undefined {
  const out: Record<Id, string> = {};
  const keys = new Set([
    ...Object.keys(local ?? {}),
    ...Object.keys(remote ?? {}),
  ]);
  for (const key of keys) {
    const picked =
      nonEmptyNoteText(remote?.[key]) ?? nonEmptyNoteText(local?.[key]);
    if (picked) out[key] = picked;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Apply a remote cue but keep local notes / newer column text that would otherwise vanish. */
export function mergeRemoteCuePreservingNotes(local: Cue, remote: Cue): Cue {
  const notes = mergeCueNotes(local.notes, remote.notes);
  const sourceNote =
    nonEmptyNoteText(remote.sourceNote) ?? nonEmptyNoteText(local.sourceNote);
  return {
    ...remote,
    visual: pickColumnContent(local.visual, remote.visual),
    audio: pickColumnContent(local.audio, remote.audio),
    notes,
    sourceNote,
  };
}

/** Prefer higher revision; at equal revision keep non-empty local over empty remote. */
function pickColumnContent(
  local: ColumnContent,
  remote: ColumnContent,
): ColumnContent {
  if (local.revision > remote.revision) return local;
  if (remote.revision > local.revision) return remote;
  if (
    !isCueSideContentEmpty(local.content) &&
    isCueSideContentEmpty(remote.content)
  ) {
    return local;
  }
  return remote;
}

/**
 * Merge cue id lists for one scene without resurrecting deletes or
 * re-attaching cues that now belong to another scene (after split/move).
 *
 * Critical: a cue may only appear in `scenes[cue.sceneId]`. Using the
 * post-merge cue.updatedAt for "is this a new insert?" used to re-list
 * split-away rows on the original scene (same id → shared body).
 */
function mergeCueIdLists(
  sceneId: Id,
  localIds: Id[],
  remoteIds: Id[],
  cues: Record<Id, Cue>,
  localCues: Record<Id, Cue>,
  deletedCueIds: Record<Id, string> | undefined,
  localSceneUpdatedAt: string,
  remoteSceneUpdatedAt: string,
): Id[] {
  const remoteIdSet = new Set(remoteIds);
  const localIdSet = new Set(localIds);
  const localNewerOrEqual = localSceneUpdatedAt >= remoteSceneUpdatedAt;
  const remoteStrictlyNewer = remoteSceneUpdatedAt > localSceneUpdatedAt;
  const sameStamp = localSceneUpdatedAt === remoteSceneUpdatedAt;

  const belongsHere = (id: Id): boolean => {
    const cue = cues[id];
    if (!cue || cue.sceneId !== sceneId) return false;
    const delAt = deletedCueIds?.[id];
    if (delAt && delAt >= cue.updatedAt) return false;
    return true;
  };

  const keepLocalOnly = (id: Id): boolean => {
    if (!belongsHere(id) || remoteIdSet.has(id)) return false;
    if (localNewerOrEqual) return true;
    // Concurrent insert: use the *local* cue stamp, not the merged body
    // (merged updatedAt can be the split/move time and falsely look new).
    const localCue = localCues[id];
    return Boolean(localCue && localCue.updatedAt >= remoteSceneUpdatedAt);
  };

  const keepRemoteOnly = (id: Id): boolean => {
    if (!belongsHere(id) || localIdSet.has(id)) return false;
    // Tombstone on either side wins over a stale remote membership.
    if (deletedCueIds?.[id]) return false;
    if (remoteStrictlyNewer) return true;
    if (!sameStamp) return false;
    const cue = cues[id];
    return Boolean(cue && cue.updatedAt >= localSceneUpdatedAt);
  };

  const primary = localNewerOrEqual ? localIds : remoteIds;
  const secondary = primary === localIds ? remoteIds : localIds;
  const out: Id[] = [];
  const seen = new Set<Id>();
  for (const id of primary) {
    if (!belongsHere(id) || seen.has(id)) continue;
    if (primary === localIds) {
      if (!remoteIdSet.has(id) && !keepLocalOnly(id)) continue;
    } else if (!localIdSet.has(id) && !keepRemoteOnly(id)) {
      continue;
    }
    out.push(id);
    seen.add(id);
  }
  for (const id of secondary) {
    if (!belongsHere(id) || seen.has(id)) continue;
    if (secondary === localIds) {
      if (!keepLocalOnly(id)) continue;
    } else if (!keepRemoteOnly(id)) {
      continue;
    }
    out.push(id);
    seen.add(id);
  }
  return out;
}

/**
 * Merge `local` (live editor doc) onto `remote` (structural persist base).
 * Must not drop local-only cues, newer typed content, or structural moves
 * (e.g. scene → papirkurv): episode.sceneIds and scene.episodeId must agree.
 */
export function mergeDocumentCueNotes(
  local: ScriptDocument,
  remote: ScriptDocument,
): ScriptDocument {
  let deletedCueIds = mergeDeletedCueMaps(
    local.deletedCueIds,
    remote.deletedCueIds,
  );

  const takeCue = (cue: Cue): Cue | null => {
    const delAt = deletedCueIds?.[cue.id];
    if (delAt && delAt >= cue.updatedAt) return null;
    if (delAt && cue.updatedAt > delAt) {
      const next = { ...(deletedCueIds ?? {}) };
      delete next[cue.id];
      deletedCueIds = Object.keys(next).length > 0 ? next : undefined;
    }
    return cue;
  };

  const cues: Record<Id, Cue> = {};
  for (const remoteCue of Object.values(remote.cues)) {
    const kept = takeCue(remoteCue);
    if (kept) cues[kept.id] = kept;
  }
  for (const localCue of Object.values(local.cues)) {
    const remoteCue = cues[localCue.id];
    if (remoteCue) {
      const merged = mergeRemoteCuePreservingNotes(localCue, remoteCue);
      const kept = takeCue(merged);
      if (kept) cues[kept.id] = kept;
      else delete cues[localCue.id];
    } else {
      const kept = takeCue(localCue);
      if (kept) cues[kept.id] = kept;
    }
  }

  const scenes: ScriptDocument["scenes"] = { ...remote.scenes };
  const sceneIds = new Set([
    ...Object.keys(remote.scenes),
    ...Object.keys(local.scenes),
  ]);
  for (const sceneId of sceneIds) {
    const remoteScene = remote.scenes[sceneId];
    const localScene = local.scenes[sceneId];
    if (!remoteScene && localScene) {
      scenes[sceneId] = {
        ...localScene,
        cueIds: localScene.cueIds.filter(
          (id) => cues[id]?.sceneId === sceneId,
        ),
      };
      continue;
    }
    if (!remoteScene) continue;
    if (!localScene) {
      scenes[sceneId] = {
        ...remoteScene,
        cueIds: remoteScene.cueIds.filter(
          (id) => cues[id]?.sceneId === sceneId && !deletedCueIds?.[id],
        ),
      };
      continue;
    }
    const newerMeta =
      localScene.updatedAt >= remoteScene.updatedAt ? localScene : remoteScene;
    const cueIds = mergeCueIdLists(
      sceneId,
      localScene.cueIds,
      remoteScene.cueIds,
      cues,
      local.cues,
      deletedCueIds,
      localScene.updatedAt,
      remoteScene.updatedAt,
    );
    scenes[sceneId] = {
      ...newerMeta,
      cueIds,
      updatedAt:
        localScene.updatedAt >= remoteScene.updatedAt
          ? localScene.updatedAt
          : remoteScene.updatedAt,
    };
  }

  const localNewer =
    local.production.updatedAt >= remote.production.updatedAt;
  const baseProduction = localNewer ? local.production : remote.production;
  const otherProduction = localNewer ? remote.production : local.production;

  const seasons = mergeSeasonRecords(local, remote);
  let episodes = reconcileEpisodeSceneMembership(
    mergeEpisodeRecords(local, remote),
    scenes,
  );

  // Keep special-season episode lists in sync with shell episodes we retained.
  for (const field of [
    "trashSeasonId",
    "archiveSeasonId",
    "submittedSeasonId",
  ] as const) {
    const seasonId =
      baseProduction[field] ?? otherProduction[field] ?? null;
    if (!seasonId || !seasons[seasonId]) continue;
    const shellIds = Object.values(episodes)
      .filter((ep) => ep.seasonId === seasonId)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((ep) => ep.id);
    const season = seasons[seasonId]!;
    if (
      shellIds.length !== season.episodeIds.length ||
      shellIds.some((id, i) => id !== season.episodeIds[i])
    ) {
      seasons[seasonId] = { ...season, episodeIds: shellIds };
    }
  }

  const comments = { ...(remote.comments ?? {}) };
  for (const [id, comment] of Object.entries(local.comments ?? {})) {
    if (!comments[id]) comments[id] = comment;
  }

  const merged: ScriptDocument = {
    ...remote,
    production: {
      ...baseProduction,
      noteColumns: mergeNoteColumnDefs(
        local.production.noteColumns,
        remote.production.noteColumns,
      ),
      castMembers:
        (localNewer
          ? local.production.castMembers
          : remote.production.castMembers) ?? [],
      trashSeasonId:
        baseProduction.trashSeasonId ?? otherProduction.trashSeasonId ?? null,
      archiveSeasonId:
        baseProduction.archiveSeasonId ??
        otherProduction.archiveSeasonId ??
        null,
      submittedSeasonId:
        baseProduction.submittedSeasonId ??
        otherProduction.submittedSeasonId ??
        null,
      updatedAt: localNewer
        ? local.production.updatedAt
        : remote.production.updatedAt,
    },
    seasons,
    episodes,
    scenes,
    cues,
    comments,
    deletedCueIds,
  };

  return reconcileCueSceneMembership(merged);
}

/** Keep every note-column id from both sides so custom columns cannot vanish. */
export function mergeNoteColumnDefs(
  local?: NoteColumnDef[],
  remote?: NoteColumnDef[],
): NoteColumnDef[] {
  const byId = new Map<Id, NoteColumnDef>();
  for (const col of remote ?? []) {
    if (col?.id) byId.set(col.id, { ...col });
  }
  for (const col of local ?? []) {
    if (col?.id && !byId.has(col.id)) byId.set(col.id, { ...col });
  }
  const cols = [...byId.values()].map((col, i) => ({ ...col, sortOrder: i }));
  return cols.length > 0 ? cols : createDefaultNoteColumns();
}

function normalizeStepList(statusSteps: StatusStepDef[]): StatusStepDef[] {
  return statusSteps.map((s, i) => ({
    id: s.id || createId(),
    label: typeof s.label === "string" ? s.label : "",
    sortOrder: i,
  }));
}

/** Empty / whitespace-only labels are visual spacers, not checklist items. */
export function isStatusStepSpacer(
  step: Pick<StatusStepDef, "label">,
): boolean {
  return step.label.trim() === "";
}

/** Effective status steps for a scene (scene override or production default). */
export function getSceneStatusSteps(
  scene: Pick<Scene, "statusSteps"> | null | undefined,
  productionSteps: StatusStepDef[] | null | undefined,
): StatusStepDef[] {
  if (scene?.statusSteps && scene.statusSteps.length > 0) {
    return [...scene.statusSteps].sort((a, b) => a.sortOrder - b.sortOrder);
  }
  return [...(productionSteps ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
}

export function setProductionStatusSteps(
  doc: ScriptDocument,
  statusSteps: StatusStepDef[],
  options?: { syncSceneOverrides?: boolean },
): ScriptDocument {
  const ts = nowIso();
  const normalized = normalizeStepList(statusSteps);
  const prevSteps = doc.production.statusSteps ?? [];
  const prevIds = new Set(prevSteps.map((s) => s.id));
  const nextIds = new Set(normalized.map((s) => s.id));
  const added = normalized.filter((s) => !prevIds.has(s.id));
  const removedIds = new Set(
    prevSteps.filter((s) => !nextIds.has(s.id)).map((s) => s.id),
  );
  const prevById = new Map(prevSteps.map((s) => [s.id, s]));
  const nextById = new Map(normalized.map((s) => [s.id, s]));
  let scenes = doc.scenes;
  if (options?.syncSceneOverrides) {
    scenes = { ...doc.scenes };
    for (const [id, scene] of Object.entries(scenes)) {
      if (scene.statusSteps && scene.statusSteps.length > 0) {
        scenes[id] = {
          ...scene,
          statusSteps: normalized.map((s) => ({ ...s })),
          updatedAt: ts,
        };
      }
    }
  } else {
    const nextScenes = { ...doc.scenes };
    let scenesChanged = false;
    for (const [id, scene] of Object.entries(nextScenes)) {
      const custom = scene.statusSteps;
      if (!custom || custom.length === 0) continue;
      let next = custom.filter((s) => !removedIds.has(s.id));
      const have = new Set(next.map((s) => s.id));
      for (const step of added) {
        if (!have.has(step.id)) {
          next.push({ ...step });
          have.add(step.id);
        }
      }
      next = next.map((s, i) => {
        const prod = nextById.get(s.id);
        const prev = prevById.get(s.id);
        const label =
          prod && prev && s.label === prev.label ? prod.label : s.label;
        return { ...s, label, sortOrder: i };
      });
      const unchanged =
        next.length === custom.length &&
        next.every(
          (s, i) => s.id === custom[i]?.id && s.label === custom[i]?.label,
        );
      if (unchanged) continue;
      scenesChanged = true;
      nextScenes[id] = {
        ...scene,
        statusSteps: next.length > 0 ? next : undefined,
        updatedAt: ts,
      };
    }
    if (scenesChanged) scenes = nextScenes;
  }
  return {
    ...doc,
    production: {
      ...doc.production,
      statusSteps: normalized,
      updatedAt: ts,
    },
    scenes,
  };
}

/** Set or clear a scene-specific status step list (`null` → inherit production). */
export function setSceneStatusSteps(
  doc: ScriptDocument,
  sceneId: Id,
  statusSteps: StatusStepDef[] | null,
): ScriptDocument {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);
  const ts = nowIso();
  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    scenes: {
      ...doc.scenes,
      [sceneId]: {
        ...scene,
        statusSteps:
          statusSteps == null ? undefined : normalizeStepList(statusSteps),
        updatedAt: ts,
      },
    },
  };
}

export function setSceneStatusCompleted(
  doc: ScriptDocument,
  sceneId: Id,
  statusCompletedIds: Id[],
): ScriptDocument {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);
  const ts = nowIso();
  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    scenes: {
      ...doc.scenes,
      [sceneId]: {
        ...scene,
        statusCompletedIds: [...statusCompletedIds],
        updatedAt: ts,
      },
    },
  };
}

export function toggleSceneStatusStep(
  doc: ScriptDocument,
  sceneId: Id,
  stepId: Id,
): ScriptDocument {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);
  const prev = new Set(scene.statusCompletedIds ?? []);
  if (prev.has(stepId)) prev.delete(stepId);
  else prev.add(stepId);
  return setSceneStatusCompleted(doc, sceneId, [...prev]);
}

/** Episode-level status steps (whole episode, not per scene). */
export function getEpisodeStatusSteps(
  episode: Pick<Episode, "statusSteps"> | null | undefined,
): StatusStepDef[] {
  if (!episode?.statusSteps || episode.statusSteps.length === 0) return [];
  return [...episode.statusSteps].sort((a, b) => a.sortOrder - b.sortOrder);
}

export function setEpisodeStatusSteps(
  doc: ScriptDocument,
  episodeId: Id,
  statusSteps: StatusStepDef[] | null,
): ScriptDocument {
  const episode = doc.episodes[episodeId];
  if (!episode) throw new Error(`Episode ${episodeId} not found`);
  const ts = nowIso();
  const nextSteps =
    statusSteps == null || statusSteps.length === 0
      ? undefined
      : normalizeStepList(statusSteps);
  const keepIds = new Set((nextSteps ?? []).map((s) => s.id));
  const completed = (episode.statusCompletedIds ?? []).filter((id) =>
    keepIds.has(id),
  );
  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    episodes: {
      ...doc.episodes,
      [episodeId]: {
        ...episode,
        statusSteps: nextSteps,
        statusCompletedIds: completed.length ? completed : undefined,
        updatedAt: ts,
      },
    },
  };
}

export function setEpisodeStatusCompleted(
  doc: ScriptDocument,
  episodeId: Id,
  statusCompletedIds: Id[],
): ScriptDocument {
  const episode = doc.episodes[episodeId];
  if (!episode) throw new Error(`Episode ${episodeId} not found`);
  const ts = nowIso();
  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    episodes: {
      ...doc.episodes,
      [episodeId]: {
        ...episode,
        statusCompletedIds: [...statusCompletedIds],
        updatedAt: ts,
      },
    },
  };
}

export function toggleEpisodeStatusStep(
  doc: ScriptDocument,
  episodeId: Id,
  stepId: Id,
): ScriptDocument {
  const episode = doc.episodes[episodeId];
  if (!episode) throw new Error(`Episode ${episodeId} not found`);
  const prev = new Set(episode.statusCompletedIds ?? []);
  if (prev.has(stepId)) prev.delete(stepId);
  else prev.add(stepId);
  return setEpisodeStatusCompleted(doc, episodeId, [...prev]);
}

export type StatusProgress = {
  completed: number;
  total: number;
  pct: number;
};

function progressFromCounts(completed: number, total: number): StatusProgress {
  return {
    completed,
    total,
    pct: total === 0 ? 0 : Math.round((completed / total) * 100),
  };
}

/** Completed / total status steps for one scene. */
export function sceneStatusProgress(
  scene: Pick<Scene, "statusSteps" | "statusCompletedIds">,
  productionSteps: StatusStepDef[] | null | undefined,
): StatusProgress {
  const steps = getSceneStatusSteps(scene, productionSteps).filter(
    (s) => !isStatusStepSpacer(s),
  );
  const done = new Set(scene.statusCompletedIds ?? []);
  const completed = steps.filter((s) => done.has(s.id)).length;
  return progressFromCounts(completed, steps.length);
}

/** Episode-level checklist progress (0 when no episode steps). */
export function episodeLevelStatusProgress(
  episode: Pick<Episode, "statusSteps" | "statusCompletedIds"> | null | undefined,
): StatusProgress {
  const steps = getEpisodeStatusSteps(episode).filter(
    (s) => !isStatusStepSpacer(s),
  );
  const done = new Set(episode?.statusCompletedIds ?? []);
  const completed = steps.filter((s) => done.has(s.id)).length;
  return progressFromCounts(completed, steps.length);
}

/**
 * Weighted rollup: all scene points in the episode plus any episode-level
 * points (each counts the same as one scene point).
 */
export function episodeStatusProgress(
  doc: ScriptDocument,
  episodeId: Id,
): StatusProgress {
  const episode = doc.episodes[episodeId];
  const scenes = getEpisodeScenes(doc, episodeId);
  let completed = 0;
  let total = 0;
  for (const scene of scenes) {
    const p = sceneStatusProgress(scene, doc.production.statusSteps);
    completed += p.completed;
    total += p.total;
  }
  const ep = episodeLevelStatusProgress(episode);
  completed += ep.completed;
  total += ep.total;
  return progressFromCounts(completed, total);
}

/** Weighted rollup of all episodes in the production. */
export function productionStatusProgress(doc: ScriptDocument): StatusProgress {
  let completed = 0;
  let total = 0;
  for (const episode of listEpisodes(doc)) {
    const p = episodeStatusProgress(doc, episode.id);
    completed += p.completed;
    total += p.total;
  }
  return progressFromCounts(completed, total);
}

export function countProductionScenes(doc: ScriptDocument): number {
  return listEpisodes(doc).reduce(
    (n, episode) => n + getEpisodeScenes(doc, episode.id).length,
    0,
  );
}

export function productionTotalDuration(doc: ScriptDocument): number {
  return listEpisodes(doc).reduce(
    (n, episode) =>
      n + totalSceneDuration(getEpisodeScenes(doc, episode.id)),
    0,
  );
}

export function setSceneCrewRoles(
  doc: ScriptDocument,
  sceneId: Id,
  crewRoles: SceneCrewRole[],
): ScriptDocument {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);
  const ts = nowIso();
  const normalized = normalizeCrewRolesInput(crewRoles);
  // Do not remember job titles here — typing each letter would create chips.
  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    scenes: {
      ...doc.scenes,
      [sceneId]: {
        ...scene,
        crewRoles: normalized,
        updatedAt: ts,
      },
    },
  };
}

export function setEpisodeCrewRoles(
  doc: ScriptDocument,
  episodeId: Id,
  crewRoles: SceneCrewRole[],
): ScriptDocument {
  const episode = doc.episodes[episodeId];
  if (!episode) throw new Error(`Episode ${episodeId} not found`);
  const ts = nowIso();
  const normalized = normalizeCrewRolesInput(crewRoles);
  // Do not remember job titles here — typing each letter would create chips.
  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    episodes: {
      ...doc.episodes,
      [episodeId]: {
        ...episode,
        crewRoles: normalized,
        updatedAt: ts,
      },
    },
  };
}

export function setSeasonCrewRoles(
  doc: ScriptDocument,
  seasonId: Id,
  crewRoles: SceneCrewRole[],
): ScriptDocument {
  const season = doc.seasons[seasonId];
  if (!season) throw new Error(`Season ${seasonId} not found`);
  const ts = nowIso();
  const normalized = normalizeCrewRolesInput(crewRoles);
  return rememberCrewJobTitlesFromRoles(
    {
      ...doc,
      production: { ...doc.production, updatedAt: ts },
      seasons: {
        ...doc.seasons,
        [seasonId]: {
          ...season,
          crewRoles: normalized.length ? normalized : undefined,
          updatedAt: ts,
        },
      },
    },
    normalized,
    ts,
  );
}

function crewPersonMatchKey(name: string, detail?: string): string {
  return `${name.trim().toLowerCase()}|${(detail ?? "").trim().toLowerCase()}`;
}

function rolesHaveCrewPerson(
  roles: SceneCrewRole[] | undefined,
  jobTitle: string,
  name: string,
  detail?: string,
): boolean {
  const titleKey = jobTitle.trim().toLowerCase();
  const personKey = crewPersonMatchKey(name, detail);
  if (!titleKey || !name.trim()) return false;
  for (const role of roles ?? []) {
    if (role.jobTitle.trim().toLowerCase() !== titleKey) continue;
    for (const person of role.people) {
      if (crewPersonMatchKey(person.name, person.detail) === personKey) {
        return true;
      }
    }
  }
  return false;
}

function upsertCrewPersonInRoles(
  roles: SceneCrewRole[] | undefined,
  jobTitle: string,
  person: { name: string; detail?: string },
): SceneCrewRole[] {
  const title = jobTitle.trim();
  const name = person.name.trim();
  if (!title || !name) return roles ? [...roles] : [];
  const detail =
    typeof person.detail === "string" && person.detail.trim()
      ? person.detail.trimEnd()
      : undefined;
  const personKey = crewPersonMatchKey(name, detail);
  const titleKey = title.toLowerCase();
  const next = (roles ?? []).map((role) => ({
    ...role,
    people: [...role.people],
  }));
  let role = next.find((r) => r.jobTitle.trim().toLowerCase() === titleKey);
  if (!role) {
    role = {
      id: createId(),
      jobTitle: title,
      people: [],
      sortOrder: next.length,
    };
    next.push(role);
  }
  if (
    !role.people.some(
      (p) => crewPersonMatchKey(p.name, p.detail) === personKey,
    )
  ) {
    role.people.push({ id: createId(), name, detail });
  }
  return next.map((r, i) => ({ ...r, sortOrder: i }));
}

function removeCrewPersonFromRoles(
  roles: SceneCrewRole[] | undefined,
  jobTitle: string,
  person: { name: string; detail?: string },
): SceneCrewRole[] {
  const titleKey = jobTitle.trim().toLowerCase();
  const personKey = crewPersonMatchKey(person.name, person.detail);
  if (!titleKey || !person.name.trim()) return roles ? [...roles] : [];
  return (roles ?? [])
    .map((role) => {
      if (role.jobTitle.trim().toLowerCase() !== titleKey) return role;
      return {
        ...role,
        people: role.people.filter(
          (p) => crewPersonMatchKey(p.name, p.detail) !== personKey,
        ),
      };
    })
    .filter((role) => role.people.length > 0)
    .map((r, i) => ({ ...r, sortOrder: i }));
}

/** Whether this person+job is marked as season-wide on the season. */
export function seasonHasCrewPerson(
  doc: ScriptDocument,
  seasonId: Id,
  jobTitle: string,
  person: { name: string; detail?: string },
): boolean {
  return rolesHaveCrewPerson(
    doc.seasons[seasonId]?.crewRoles,
    jobTitle,
    person.name,
    person.detail,
  );
}

/**
 * Episodes that share the same credit peer group as `episodeId`:
 * - seasons off → every episode in the production (implicit “sæson 1”)
 * - seasons on + episode in a season → that season’s episodes
 * - otherwise → all listed episodes
 */
export function listCrewPeerEpisodeIds(
  doc: ScriptDocument,
  episodeId: Id,
): Id[] {
  const episode = doc.episodes[episodeId];
  if (!episode) return [];

  // Seasons disabled: treat the whole production as one invisible season.
  if (!doc.production.useSeasons) {
    const ids = new Set<Id>(listEpisodes(doc).map((ep) => ep.id));
    for (const id of Object.keys(doc.episodes)) ids.add(id);
    return [...ids];
  }

  if (episode.seasonId && doc.seasons[episode.seasonId]) {
    const season = doc.seasons[episode.seasonId]!;
    const ids = new Set<Id>(season.episodeIds);
    for (const [id, ep] of Object.entries(doc.episodes)) {
      if (ep.seasonId === episode.seasonId) ids.add(id);
    }
    ids.add(episodeId);
    return [...ids];
  }

  const ids = new Set<Id>(listEpisodes(doc).map((ep) => ep.id));
  for (const id of Object.keys(doc.episodes)) ids.add(id);
  return [...ids];
}

function storesPeerCrewOnSeason(
  doc: ScriptDocument,
  episodeId: Id,
): boolean {
  if (!doc.production.useSeasons) return false;
  const episode = doc.episodes[episodeId];
  return Boolean(episode?.seasonId && doc.seasons[episode.seasonId]);
}

/** Season- or production-level roles shared across peer episodes. */
export function getEpisodePeerCrewRoles(
  doc: ScriptDocument,
  episodeId: Id,
): SceneCrewRole[] | undefined {
  if (storesPeerCrewOnSeason(doc, episodeId)) {
    const seasonId = doc.episodes[episodeId]!.seasonId!;
    return doc.seasons[seasonId]?.crewRoles;
  }
  return doc.production.crewRoles;
}

/** Whether this person+job is marked peer-wide for the episode’s group. */
export function episodePeerHasCrewPerson(
  doc: ScriptDocument,
  episodeId: Id,
  jobTitle: string,
  person: { name: string; detail?: string },
): boolean {
  return rolesHaveCrewPerson(
    getEpisodePeerCrewRoles(doc, episodeId),
    jobTitle,
    person.name,
    person.detail,
  );
}

function writePeerCrewRoles(
  doc: ScriptDocument,
  episodeId: Id,
  roles: SceneCrewRole[],
  ts: string,
): ScriptDocument {
  if (storesPeerCrewOnSeason(doc, episodeId)) {
    const seasonId = doc.episodes[episodeId]!.seasonId!;
    const season = doc.seasons[seasonId]!;
    return {
      ...doc,
      production: { ...doc.production, updatedAt: ts },
      seasons: {
        ...doc.seasons,
        [seasonId]: {
          ...season,
          crewRoles: roles.length ? roles : undefined,
          updatedAt: ts,
        },
      },
    };
  }
  return {
    ...doc,
    production: {
      ...doc.production,
      crewRoles: roles.length ? roles : undefined,
      updatedAt: ts,
    },
  };
}

/**
 * Mark (or unmark) a credited person across the episode’s peer group
 * (season, or all production episodes when seasons are off).
 */
export function setCrewPersonOnEpisodePeers(
  doc: ScriptDocument,
  episodeId: Id,
  jobTitle: string,
  person: { name: string; detail?: string },
  onPeers: boolean,
): ScriptDocument {
  const episode = doc.episodes[episodeId];
  if (!episode) throw new Error(`Episode ${episodeId} not found`);
  const name = person.name.trim();
  if (!jobTitle.trim() || !name) return doc;

  const ts = nowIso();
  const currentPeerRoles = getEpisodePeerCrewRoles(doc, episodeId);
  const nextPeerRoles = onPeers
    ? upsertCrewPersonInRoles(currentPeerRoles, jobTitle, person)
    : removeCrewPersonFromRoles(currentPeerRoles, jobTitle, person);

  let next = writePeerCrewRoles(doc, episodeId, nextPeerRoles, ts);

  if (onPeers) {
    const episodes = { ...next.episodes };
    for (const peerId of listCrewPeerEpisodeIds(next, episodeId)) {
      const peer = episodes[peerId];
      if (!peer) continue;
      episodes[peerId] = {
        ...peer,
        crewRoles: upsertCrewPersonInRoles(peer.crewRoles, jobTitle, person),
        updatedAt: ts,
      };
    }
    next = { ...next, episodes };
  }

  return rememberCrewJobTitlesFromRoles(next, nextPeerRoles, ts);
}

/** @deprecated Prefer setCrewPersonOnEpisodePeers */
export function setCrewPersonOnSeason(
  doc: ScriptDocument,
  seasonId: Id,
  jobTitle: string,
  person: { name: string; detail?: string },
  onSeason: boolean,
): ScriptDocument {
  const episodeId =
    doc.seasons[seasonId]?.episodeIds[0] ??
    Object.values(doc.episodes).find((ep) => ep.seasonId === seasonId)?.id;
  if (!episodeId) {
    // No episodes yet — only update season store.
    const season = doc.seasons[seasonId];
    if (!season) throw new Error(`Season ${seasonId} not found`);
    const ts = nowIso();
    const nextRoles = onSeason
      ? upsertCrewPersonInRoles(season.crewRoles, jobTitle, person)
      : removeCrewPersonFromRoles(season.crewRoles, jobTitle, person);
    return rememberCrewJobTitlesFromRoles(
      {
        ...doc,
        production: { ...doc.production, updatedAt: ts },
        seasons: {
          ...doc.seasons,
          [seasonId]: {
            ...season,
            crewRoles: nextRoles.length ? nextRoles : undefined,
            updatedAt: ts,
          },
        },
      },
      nextRoles,
      ts,
    );
  }
  return setCrewPersonOnEpisodePeers(
    doc,
    episodeId,
    jobTitle,
    person,
    onSeason,
  );
}

/**
 * Remove a person from peer-wide credits and from every peer episode.
 */
export function removeCrewPersonFromEpisodePeers(
  doc: ScriptDocument,
  episodeId: Id,
  jobTitle: string,
  person: { name: string; detail?: string },
): ScriptDocument {
  const episode = doc.episodes[episodeId];
  if (!episode) throw new Error(`Episode ${episodeId} not found`);
  const ts = nowIso();
  const nextPeerRoles = removeCrewPersonFromRoles(
    getEpisodePeerCrewRoles(doc, episodeId),
    jobTitle,
    person,
  );
  let next = writePeerCrewRoles(doc, episodeId, nextPeerRoles, ts);
  const episodes = { ...next.episodes };
  for (const peerId of listCrewPeerEpisodeIds(next, episodeId)) {
    const peer = episodes[peerId];
    if (!peer) continue;
    episodes[peerId] = {
      ...peer,
      crewRoles: removeCrewPersonFromRoles(peer.crewRoles, jobTitle, person),
      updatedAt: ts,
    };
  }
  return { ...next, episodes };
}

/** How far to clear episode copies when turning off a season-wide credit. */
export type CrewSeasonDeactivateMode = "all" | "others" | "this";

/**
 * Turn off the season-wide mark and optionally remove the person from
 * episode “hele episoden” lists:
 * - all: every peer episode
 * - others: every peer except the current episode
 * - this: only the current episode (other episodes keep their copies)
 */
export function deactivateCrewPersonSeason(
  doc: ScriptDocument,
  episodeId: Id,
  jobTitle: string,
  person: { name: string; detail?: string },
  mode: CrewSeasonDeactivateMode,
): ScriptDocument {
  const episode = doc.episodes[episodeId];
  if (!episode) throw new Error(`Episode ${episodeId} not found`);
  const name = person.name.trim();
  if (!jobTitle.trim() || !name) return doc;

  const ts = nowIso();
  const nextPeerRoles = removeCrewPersonFromRoles(
    getEpisodePeerCrewRoles(doc, episodeId),
    jobTitle,
    person,
  );
  let next = writePeerCrewRoles(doc, episodeId, nextPeerRoles, ts);
  const episodes = { ...next.episodes };
  const peers = listCrewPeerEpisodeIds(next, episodeId);

  const clearOn = (peerId: Id) => {
    const peer = episodes[peerId];
    if (!peer) return;
    episodes[peerId] = {
      ...peer,
      crewRoles: removeCrewPersonFromRoles(peer.crewRoles, jobTitle, person),
      updatedAt: ts,
    };
  };

  if (mode === "all") {
    for (const peerId of peers) clearOn(peerId);
  } else if (mode === "others") {
    for (const peerId of peers) {
      if (peerId === episodeId) continue;
      clearOn(peerId);
    }
  } else {
    clearOn(episodeId);
  }

  return { ...next, episodes };
}

/** @deprecated Prefer removeCrewPersonFromEpisodePeers */
export function removeCrewPersonFromSeasonEpisodes(
  doc: ScriptDocument,
  seasonId: Id,
  jobTitle: string,
  person: { name: string; detail?: string },
): ScriptDocument {
  const episodeId =
    doc.seasons[seasonId]?.episodeIds[0] ??
    Object.values(doc.episodes).find((ep) => ep.seasonId === seasonId)?.id;
  if (!episodeId) {
    const season = doc.seasons[seasonId];
    if (!season) throw new Error(`Season ${seasonId} not found`);
    const ts = nowIso();
    const nextSeasonRoles = removeCrewPersonFromRoles(
      season.crewRoles,
      jobTitle,
      person,
    );
    return {
      ...doc,
      production: { ...doc.production, updatedAt: ts },
      seasons: {
        ...doc.seasons,
        [seasonId]: {
          ...season,
          crewRoles: nextSeasonRoles.length ? nextSeasonRoles : undefined,
          updatedAt: ts,
        },
      },
    };
  }
  return removeCrewPersonFromEpisodePeers(doc, episodeId, jobTitle, person);
}

/** Ensure peer-wide people appear in an episode’s “hele episoden” role list. */
export function mergeSeasonCrewIntoEpisodeRoles(
  episodeRoles: SceneCrewRole[] | undefined,
  seasonRoles: SceneCrewRole[] | undefined,
): SceneCrewRole[] {
  let next = episodeRoles ? [...episodeRoles] : [];
  for (const role of seasonRoles ?? []) {
    const title = role.jobTitle.trim();
    if (!title) continue;
    for (const person of role.people) {
      const name = person.name.trim();
      if (!name) continue;
      if (rolesHaveCrewPerson(next, title, name, person.detail)) continue;
      next = upsertCrewPersonInRoles(next, title, {
        name,
        detail: person.detail,
      });
    }
  }
  return next;
}

export function setEpisodeProgramTexts(
  doc: ScriptDocument,
  episodeId: Id,
  programTexts: ProgramText[],
): ScriptDocument {
  const episode = doc.episodes[episodeId];
  if (!episode) throw new Error(`Episode ${episodeId} not found`);
  const ts = nowIso();
  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    episodes: {
      ...doc.episodes,
      [episodeId]: {
        ...episode,
        programTexts: normalizeProgramTexts(programTexts),
        updatedAt: ts,
      },
    },
  };
}

export function getEpisodeProgramTexts(
  episode: Episode | null | undefined,
): ProgramText[] {
  return normalizeProgramTexts(episode?.programTexts);
}

function normalizeCrewRolesInput(crewRoles: SceneCrewRole[]): SceneCrewRole[] {
  return crewRoles.map((role, i) => ({
    id: role.id || createId(),
    jobTitle: typeof role.jobTitle === "string" ? role.jobTitle.trim() : "",
    people: (role.people ?? []).map((p) => ({
      id: p.id || createId(),
      // Keep blank rows so “+ Person” can show an empty field to fill in.
      name: typeof p.name === "string" ? p.name : "",
      detail:
        typeof p.detail === "string" && p.detail.trim()
          ? p.detail.trimEnd()
          : undefined,
    })),
    sortOrder: i,
  }));
}

function normalizeCrewJobTitles(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set(
    DEFAULT_CREW_JOB_TITLES.map((title) => title.toLowerCase()),
  );
  const extras: string[] = [];
  for (const item of raw) {
    if (typeof item !== "string") continue;
    const title = item.trim();
    if (!title) continue;
    const key = title.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    extras.push(title);
  }
  return prunePrefixCrewJobTitles(extras);
}

function rememberCrewJobTitlesFromRoles(
  doc: ScriptDocument,
  roles: SceneCrewRole[],
  ts: string,
): ScriptDocument {
  const extras = [...normalizeCrewJobTitles(doc.production.crewJobTitles)];
  const seen = new Set([
    ...DEFAULT_CREW_JOB_TITLES.map((title) => title.toLowerCase()),
    ...extras.map((title) => title.toLowerCase()),
  ]);
  let added = false;
  for (const role of roles) {
    const title = role.jobTitle.trim();
    if (!title) continue;
    const key = title.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    extras.push(title);
    added = true;
  }
  if (!added) return doc;
  const pruned = prunePrefixCrewJobTitles(extras);
  return {
    ...doc,
    production: {
      ...doc.production,
      crewJobTitles: pruned.length ? pruned : undefined,
      updatedAt: ts,
    },
  };
}

/**
 * Drop extras that are proper prefixes of a longer title (typing artifacts
 * like "I", "In", "Ins" when "Instruktør" exists).
 */
function prunePrefixCrewJobTitles(titles: string[]): string[] {
  const keys = titles.map((t) => t.toLowerCase());
  return titles.filter((_title, i) => {
    const key = keys[i]!;
    return !keys.some(
      (other, j) => i !== j && other !== key && other.startsWith(key),
    );
  });
}

function collectCrewJobTitleExtras(doc: ScriptDocument): string[] {
  const extras = [...normalizeCrewJobTitles(doc.production.crewJobTitles)];
  const seen = new Set([
    ...DEFAULT_CREW_JOB_TITLES.map((title) => title.toLowerCase()),
    ...extras.map((title) => title.toLowerCase()),
  ]);
  const visit = (roles?: SceneCrewRole[]) => {
    for (const role of roles ?? []) {
      const title = role.jobTitle.trim();
      if (!title) continue;
      const key = title.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      extras.push(title);
    }
  };
  for (const episode of Object.values(doc.episodes)) visit(episode.crewRoles);
  for (const season of Object.values(doc.seasons)) visit(season.crewRoles);
  visit(doc.production.crewRoles);
  for (const scene of Object.values(doc.scenes)) visit(scene.crewRoles);
  return prunePrefixCrewJobTitles(extras);
}

/**
 * Unique person names per job title across an episode (for autocomplete).
 * Keys are lowercased trimmed job titles.
 */
export function listEpisodeCrewNamesByJob(
  doc: ScriptDocument,
  episodeId: Id,
): Record<string, string[]> {
  const byJob = new Map<string, Set<string>>();
  const episode = doc.episodes[episodeId];
  const season = episode?.seasonId ? doc.seasons[episode.seasonId] : null;
  const roleLists = [
    ...(season?.crewRoles ? [season.crewRoles] : []),
    ...(episode?.crewRoles ? [episode.crewRoles] : []),
    ...getEpisodeScenes(doc, episodeId).map((scene) => scene.crewRoles ?? []),
  ];
  for (const roles of roleLists) {
    for (const role of roles) {
      const titleKey = role.jobTitle.trim().toLowerCase();
      if (!titleKey) continue;
      let names = byJob.get(titleKey);
      if (!names) {
        names = new Set();
        byJob.set(titleKey, names);
      }
      for (const person of role.people) {
        const n = person.name.trim();
        if (n) names.add(n);
      }
    }
  }
  const out: Record<string, string[]> = {};
  for (const [key, names] of byJob) {
    out[key] = [...names].sort((a, b) =>
      a.localeCompare(b, "da", { sensitivity: "base" }),
    );
  }
  return out;
}

/** @deprecated Prefer listEpisodeCrewNamesByJob — kept for flat lists if needed. */
export function listEpisodeCrewNames(
  doc: ScriptDocument,
  episodeId: Id,
  jobTitle?: string,
): string[] {
  const byJob = listEpisodeCrewNamesByJob(doc, episodeId);
  if (jobTitle != null) {
    return byJob[jobTitle.trim().toLowerCase()] ?? [];
  }
  const names = new Set<string>();
  for (const list of Object.values(byJob)) {
    for (const n of list) names.add(n);
  }
  return [...names].sort((a, b) =>
    a.localeCompare(b, "da", { sensitivity: "base" }),
  );
}

export interface AggregatedCredit {
  jobTitle: string;
  people: {
    name: string;
    detail?: string;
    sceneNumbers: string[];
    episodeWide?: boolean;
  }[];
}

function addCreditPeople(
  byTitle: Map<
    string,
    Map<
      string,
      {
        name: string;
        detail?: string;
        sceneNumbers: Set<string>;
        episodeWide: boolean;
      }
    >
  >,
  roles: SceneCrewRole[],
  sceneNumber: ItemNumber | null,
) {
  for (const role of roles) {
    const title = role.jobTitle.trim();
    if (!title) continue;
    let peopleMap = byTitle.get(title);
    if (!peopleMap) {
      peopleMap = new Map();
      byTitle.set(title, peopleMap);
    }
    for (const person of role.people) {
      const name = person.name.trim();
      if (!name) continue;
      const key = `${name.toLowerCase()}|${(person.detail ?? "").toLowerCase()}`;
      let entry = peopleMap.get(key);
      if (!entry) {
        entry = {
          name,
          detail: person.detail?.trim() || undefined,
          sceneNumbers: new Set(),
          episodeWide: false,
        };
        peopleMap.set(key, entry);
      }
      if (sceneNumber == null) entry.episodeWide = true;
      else entry.sceneNumbers.add(String(sceneNumber));
    }
  }
}

/** Merge crew credits for all scenes in an episode, grouped by job title. */
export function aggregateEpisodeCredits(
  doc: ScriptDocument,
  episodeId: Id,
): AggregatedCredit[] {
  const byTitle = new Map<
    string,
    Map<
      string,
      {
        name: string;
        detail?: string;
        sceneNumbers: Set<string>;
        episodeWide: boolean;
      }
    >
  >();

  const episode = doc.episodes[episodeId];
  if (episode?.crewRoles?.length) {
    addCreditPeople(byTitle, episode.crewRoles, null);
  }
  const peerRoles = getEpisodePeerCrewRoles(doc, episodeId);
  if (peerRoles?.length) {
    addCreditPeople(byTitle, peerRoles, null);
  }
  for (const scene of getEpisodeScenes(doc, episodeId)) {
    addCreditPeople(byTitle, scene.crewRoles ?? [], scene.number);
  }

  const titles = [...byTitle.keys()].sort((a, b) =>
    a.localeCompare(b, "da", { sensitivity: "base" }),
  );
  return titles
    .map((jobTitle) => {
      const people = [...(byTitle.get(jobTitle)?.values() ?? [])]
        .map((p) => ({
          name: p.name,
          detail: p.detail,
          sceneNumbers: [...p.sceneNumbers].sort((a, b) =>
            a.localeCompare(b, "da", { numeric: true }),
          ),
          episodeWide: p.episodeWide || undefined,
        }))
        .sort((a, b) =>
          a.name.localeCompare(b.name, "da", { sensitivity: "base" }),
        );
      return { jobTitle, people };
    })
    .filter((block) => block.people.length > 0);
}

/** Insert a dialogue cue (character name on the cue pair).
 *  UI is gated by `SHOW_REPLIK_UI` in `packages/editor/src/replikFeature.ts`.
 *  @deprecated Prefer insertCueAfter / addCue */
export function addDialogueCue(
  doc: ScriptDocument,
  sceneId: Id,
  characterName: string,
): { doc: ScriptDocument; cueId: Id } {
  return addCue(doc, sceneId, { characterName, audio: "", visual: "" });
}

/**
 * Reorder a cue within its scene. Visual + audio stay linked (move as one row).
 */
export function moveCue(
  doc: ScriptDocument,
  sceneId: Id,
  cueId: Id,
  toIndex: number,
): ScriptDocument {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);
  const fromIndex = scene.cueIds.indexOf(cueId);
  if (fromIndex < 0) throw new Error(`Cue ${cueId} not in scene`);

  const nextIds = [...scene.cueIds];
  nextIds.splice(fromIndex, 1);
  const clamped = Math.max(0, Math.min(toIndex, nextIds.length));
  nextIds.splice(clamped, 0, cueId);

  const ts = nowIso();
  const cues = { ...doc.cues };
  nextIds.forEach((id, sortOrder) => {
    const cue = cues[id];
    if (cue) cues[id] = { ...cue, sortOrder, updatedAt: ts };
  });

  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    scenes: {
      ...doc.scenes,
      [sceneId]: { ...scene, cueIds: nextIds, updatedAt: ts },
    },
    cues,
  };
}

/**
 * Move a layout block (cue + rows covered by max(visualRowSpan, audioRowSpan))
 * so it starts immediately before `beforeCueId`, or at the end when `beforeCueId` is null.
 */
export function moveCueBlock(
  doc: ScriptDocument,
  sceneId: Id,
  cueId: Id,
  beforeCueId: Id | null,
): ScriptDocument {
  const scene = doc.scenes[sceneId];
  if (!scene) throw new Error(`Scene ${sceneId} not found`);
  const fromIndex = scene.cueIds.indexOf(cueId);
  if (fromIndex < 0) throw new Error(`Cue ${cueId} not in scene`);

  const owner = doc.cues[cueId];
  if (!owner) throw new Error(`Cue ${cueId} not found`);
  const span = Math.max(
    1,
    owner.visualRowSpan ?? 1,
    owner.audioRowSpan ?? 1,
  );
  const blockIds = scene.cueIds.slice(fromIndex, fromIndex + span);

  if (beforeCueId && blockIds.includes(beforeCueId)) {
    return doc;
  }

  const remaining = [
    ...scene.cueIds.slice(0, fromIndex),
    ...scene.cueIds.slice(fromIndex + span),
  ];
  let insertAt =
    beforeCueId === null ? remaining.length : remaining.indexOf(beforeCueId);
  if (insertAt < 0) insertAt = remaining.length;

  const nextIds = [
    ...remaining.slice(0, insertAt),
    ...blockIds,
    ...remaining.slice(insertAt),
  ];

  const ts = nowIso();
  const cues = { ...doc.cues };
  nextIds.forEach((id, sortOrder) => {
    const cue = cues[id];
    if (cue) cues[id] = { ...cue, sortOrder, updatedAt: ts };
  });

  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    scenes: {
      ...doc.scenes,
      [sceneId]: { ...scene, cueIds: nextIds, updatedAt: ts },
    },
    cues,
  };
}

export function updateCueSide(
  doc: ScriptDocument,
  cueId: Id,
  column: ScriptColumn,
  content: string,
  userId?: Id,
  extra?: { characterName?: string },
): ScriptDocument {
  const cue = doc.cues[cueId];
  if (!cue) throw new Error(`Cue ${cueId} not found`);
  const ts = nowIso();
  const side = getCueSide(cue, column);
  const nextSide: ColumnContent = {
    ...side,
    content,
    revision: side.revision + 1,
    ...(extra?.characterName !== undefined
      ? { characterName: extra.characterName.trim() || undefined }
      : {}),
  };
  const nextCue: Cue = {
    ...cue,
    [column]: nextSide,
    updatedAt: ts,
    updatedById: userId,
  };
  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    cues: { ...doc.cues, [cueId]: nextCue },
  };
}

/** Assign or clear the cast/character name on one cue cell. */
export function setCueCharacter(
  doc: ScriptDocument,
  cueId: Id,
  column: ScriptColumn,
  characterName: string | null | undefined,
  userId?: Id,
): ScriptDocument {
  const cue = doc.cues[cueId];
  if (!cue) throw new Error(`Cue ${cueId} not found`);
  const ts = nowIso();
  const side = getCueSide(cue, column);
  const nextName = characterName?.trim() || undefined;
  if ((side.characterName || undefined) === nextName) return doc;
  const nextSide: ColumnContent = {
    ...side,
    characterName: nextName,
    revision: side.revision + 1,
  };
  return {
    ...doc,
    production: { ...doc.production, updatedAt: ts },
    cues: {
      ...doc.cues,
      [cueId]: {
        ...cue,
        [column]: nextSide,
        updatedAt: ts,
        updatedById: userId,
      },
    },
  };
}

export function acquireLock(
  doc: ScriptDocument,
  cueId: Id,
  column: ScriptColumn,
  userId: Id,
  displayName: string,
  ttlMs = LOCK_TTL_MS,
): ScriptDocument {
  const cue = doc.cues[cueId];
  if (!cue) throw new Error(`Cue ${cueId} not found`);
  const side = getCueSide(cue, column);
  if (!isLockAvailable(side, userId)) {
    throw new Error("Cell is locked by another user");
  }
  const expires = new Date(Date.now() + ttlMs).toISOString();
  const nextSide: ColumnContent = {
    ...side,
    lockOwnerId: userId,
    lockOwnerName: displayName,
    lockExpiresAt: expires,
  };
  return {
    ...doc,
    cues: {
      ...doc.cues,
      [cueId]: { ...cue, [column]: nextSide },
    },
  };
}

export function releaseLock(
  doc: ScriptDocument,
  cueId: Id,
  column: ScriptColumn,
  userId: Id,
): ScriptDocument {
  const cue = doc.cues[cueId];
  if (!cue) throw new Error(`Cue ${cueId} not found`);
  const side = getCueSide(cue, column);
  if (side.lockOwnerId && side.lockOwnerId !== userId) {
    throw new Error("Cannot release another user's lock");
  }
  return {
    ...doc,
    cues: {
      ...doc.cues,
      [cueId]: { ...cue, [column]: { ...side, ...emptyLock() } },
    },
  };
}

export function renewLock(
  doc: ScriptDocument,
  cueId: Id,
  column: ScriptColumn,
  userId: Id,
  ttlMs = LOCK_TTL_MS,
): ScriptDocument {
  const cue = doc.cues[cueId];
  if (!cue) throw new Error(`Cue ${cueId} not found`);
  const side = getCueSide(cue, column);
  if (!isLockHeldBy(side, userId)) {
    throw new Error("Lock not held");
  }
  return {
    ...doc,
    cues: {
      ...doc.cues,
      [cueId]: {
        ...cue,
        [column]: {
          ...side,
          lockExpiresAt: new Date(Date.now() + ttlMs).toISOString(),
        },
      },
    },
  };
}

type LegacyPart = {
  id: Id;
  sceneId: Id;
  kind?: string;
  characterName?: string;
  content: string;
  sortOrder: number;
};

function migratePartsToCues(
  scenes: Record<Id, Scene & { partIds?: Id[]; cueIds?: Id[] }>,
  parts: Record<Id, LegacyPart>,
  existingCues: Record<Id, Cue>,
): { scenes: Record<Id, Scene>; cues: Record<Id, Cue> } {
  if (Object.keys(existingCues).length > 0) {
    const nextScenes: Record<Id, Scene> = {};
    for (const [id, scene] of Object.entries(scenes)) {
      nextScenes[id] = {
        ...scene,
        cueIds: scene.cueIds ?? [],
        description: scene.description ?? "",
        durationSeconds: scene.durationSeconds ?? 60,
      };
    }
    const cues: Record<Id, Cue> = {};
    for (const [id, cue] of Object.entries(existingCues)) {
      cues[id] = {
        ...cue,
        visualRowSpan: Math.max(1, cue.visualRowSpan ?? 1),
        audioRowSpan: Math.max(1, cue.audioRowSpan ?? 1),
        printBorderBelow: Boolean(cue.printBorderBelow),
        sourceNote:
          typeof cue.sourceNote === "string" && cue.sourceNote.trim()
            ? cue.sourceNote
            : undefined,
        notes: normalizeCueNotes(cue.notes),
        cameraNumber: normalizeCameraNumber(cue.cameraNumber),
        cameraNote:
          typeof cue.cameraNote === "string" && cue.cameraNote.trim()
            ? cue.cameraNote.trimEnd()
            : undefined,
        positionNote:
          typeof cue.positionNote === "string" && cue.positionNote.trim()
            ? cue.positionNote.trimEnd()
            : undefined,
      };
    }
    return { scenes: nextScenes, cues };
  }

  const cues: Record<Id, Cue> = {};
  const nextScenes: Record<Id, Scene> = {};
  const ts = nowIso();

  for (const [sceneId, scene] of Object.entries(scenes)) {
    const partIds = scene.partIds ?? [];
    const cueIds: Id[] = [];
    partIds.forEach((partId, index) => {
      const part = parts[partId];
      if (!part) return;
      const cueId = createId();
      const isDialogue = part.kind === "dialogue" || Boolean(part.characterName);
      const cue: Cue = {
        id: cueId,
        sceneId,
        sortOrder: index,
        visual: {
          content: isDialogue ? "" : part.content,
          revision: 1,
          ...emptyLock(),
        },
        audio: {
          content: isDialogue ? part.content : "",
          revision: 1,
          characterName: part.characterName,
          ...emptyLock(),
        },
        visualRowSpan: 1,
        audioRowSpan: 1,
        updatedAt: ts,
      };
      cues[cueId] = cue;
      cueIds.push(cueId);
    });
    if (cueIds.length === 0) {
      const starter = createStarterCues(sceneId, ts);
      for (const c of starter.cues) cues[c.id] = c;
      cueIds.push(...starter.cueIds);
    }
    nextScenes[sceneId] = {
      id: scene.id,
      episodeId: scene.episodeId,
      number: scene.number,
      title: scene.title,
      description: scene.description ?? "",
      durationSeconds: scene.durationSeconds ?? 60,
      sortOrder: scene.sortOrder,
      cueIds,
      updatedAt: scene.updatedAt ?? ts,
    };
  }

  return { scenes: nextScenes, cues };
}

/** Migrate legacy documents. Safe on every load. */
export function normalizeDocument(raw: unknown): ScriptDocument {
  const doc = raw as ScriptDocument & {
    project?: Production & { episodeIds?: Id[] };
    parts?: Record<Id, LegacyPart>;
  };

  let production = doc.production;
  let seasons = doc.seasons ?? {};
  let episodes = doc.episodes ?? {};

  if (!production && doc.project) {
    const legacy = doc.project;
    production = {
      id: legacy.id,
      title: legacy.title,
      createdAt: legacy.createdAt,
      updatedAt: legacy.updatedAt,
      seasonIds: [],
      episodeIds: legacy.episodeIds ?? Object.keys(episodes),
      castMembers: [],
    };
    const nextEpisodes: Record<Id, Episode> = {};
    for (const [id, ep] of Object.entries(episodes)) {
      const old = ep as Episode & { projectId?: Id };
      nextEpisodes[id] = {
        ...old,
        productionId: old.productionId ?? old.projectId ?? production.id,
        seasonId: old.seasonId ?? null,
      };
    }
    episodes = nextEpisodes;
  }

  if (!production) {
    throw new Error("Invalid document: missing production");
  }

  production = {
    ...production,
    useSeasons: Boolean(production.useSeasons),
    episodeNumberingMode:
      production.episodeNumberingMode === "custom" ? "custom" : "sequential",
    castMembers: Array.isArray(production.castMembers)
      ? production.castMembers.map((m, i) => ({
          id: m.id || createId(),
          name: m.name || "Unavngiven",
          color: m.color?.trim() || undefined,
          showNameAboveLines: m.showNameAboveLines !== false,
          sortOrder: m.sortOrder ?? i,
        }))
      : [],
    statusSteps: normalizeStatusSteps(production.statusSteps),
    crewJobTitles: normalizeCrewJobTitles(production.crewJobTitles),
    crewRoles: (() => {
      const roles = normalizeCrewRoles(production.crewRoles);
      return roles.length ? roles : undefined;
    })(),
    noteColumns: normalizeNoteColumns(production.noteColumns),
    trashSeasonId:
      typeof production.trashSeasonId === "string"
        ? production.trashSeasonId
        : null,
    archiveSeasonId:
      typeof production.archiveSeasonId === "string"
        ? production.archiveSeasonId
        : null,
    submittedSeasonId:
      typeof production.submittedSeasonId === "string"
        ? production.submittedSeasonId
        : null,
  };

  const { scenes, cues: rawCues } = migratePartsToCues(
    (doc.scenes ?? {}) as Record<Id, Scene & { partIds?: Id[] }>,
    doc.parts ?? {},
    doc.cues ?? {},
  );

  const cues: Record<Id, Cue> = {};
  for (const [id, cue] of Object.entries(rawCues)) {
    cues[id] = {
      ...cue,
      notes: normalizeCueNotes(cue.notes),
      cameraNumber: normalizeCameraNumber(cue.cameraNumber),
      cameraNote:
        typeof cue.cameraNote === "string" && cue.cameraNote.trim()
          ? cue.cameraNote.trimEnd()
          : undefined,
      positionNote:
        typeof cue.positionNote === "string" && cue.positionNote.trim()
          ? cue.positionNote.trimEnd()
          : undefined,
    };
  }

  const normalizedScenes: Record<Id, Scene> = {};
  for (const [id, scene] of Object.entries(scenes)) {
    const hasParts =
      scene.headingKind ||
      scene.headingLocation ||
      scene.headingTime ||
      scene.headingNote;
    const parts = hasParts
      ? {
          headingKind: scene.headingKind,
          headingLocation: scene.headingLocation?.trim() || undefined,
          headingTime: scene.headingTime?.trim() || undefined,
          headingNote: scene.headingNote?.trim() || undefined,
        }
      : parseSceneHeading(scene.heading);
    const withParts = applyHeadingFields(
      {
        ...scene,
        number: parseItemNumber(scene.number) ?? String(scene.number ?? 1),
        headerBackground: scene.headerBackground || undefined,
        crewRoles: normalizeCrewRoles(scene.crewRoles),
        notes: normalizeCueNotes(scene.notes),
        statusSteps: normalizeOptionalStatusSteps(scene.statusSteps),
        statusCompletedIds: Array.isArray(scene.statusCompletedIds)
          ? scene.statusCompletedIds.filter((x) => typeof x === "string")
          : [],
        archiveOfSceneId:
          typeof scene.archiveOfSceneId === "string"
            ? scene.archiveOfSceneId
            : undefined,
        archivedAt:
          typeof scene.archivedAt === "string" ? scene.archivedAt : undefined,
        archiveLabel:
          typeof scene.archiveLabel === "string" && scene.archiveLabel.trim()
            ? scene.archiveLabel.trim()
            : undefined,
        submittedOfSceneId:
          typeof scene.submittedOfSceneId === "string"
            ? scene.submittedOfSceneId
            : undefined,
        submittedAt:
          typeof scene.submittedAt === "string" ? scene.submittedAt : undefined,
        submittedByUserId:
          typeof scene.submittedByUserId === "string"
            ? scene.submittedByUserId
            : undefined,
        submittedByName:
          typeof scene.submittedByName === "string" &&
          scene.submittedByName.trim()
            ? scene.submittedByName.trim()
            : undefined,
        trashedAt:
          typeof scene.trashedAt === "string" ? scene.trashedAt : undefined,
      },
      parts,
    );
    normalizedScenes[id] = withParts;
  }

  let repairedCues = cues;
  for (const scene of Object.values(normalizedScenes)) {
    repairedCues = repairSceneRowSpans(scene.cueIds, repairedCues);
  }

  const normalizedEpisodes: Record<Id, Episode> = {};
  for (const [id, ep] of Object.entries(episodes)) {
    normalizedEpisodes[id] = {
      ...ep,
      number: parseItemNumber(ep.number) ?? String(ep.number ?? 1),
      sceneNumberingMode:
        ep.sceneNumberingMode === "custom" ? "custom" : "sequential",
      crewRoles: normalizeCrewRoles(ep.crewRoles),
      programTexts: normalizeProgramTexts(ep.programTexts),
      hiddenNoteColumnIds: normalizeHiddenNoteColumnIds(ep.hiddenNoteColumnIds),
      statusSteps: normalizeOptionalStatusSteps(ep.statusSteps),
      statusCompletedIds: Array.isArray(ep.statusCompletedIds)
        ? ep.statusCompletedIds.filter((x) => typeof x === "string")
        : undefined,
      originEpisodeId:
        typeof ep.originEpisodeId === "string" ? ep.originEpisodeId : undefined,
      originEpisodeTitle:
        typeof ep.originEpisodeTitle === "string"
          ? ep.originEpisodeTitle
          : undefined,
    };
  }

  const normalizedSeasons: Record<Id, Season> = {};
  for (const [id, season] of Object.entries(seasons)) {
    const roles = normalizeCrewRoles(season.crewRoles);
    const role: SeasonRole | undefined =
      season.role === "trash" ||
      season.role === "archive" ||
      season.role === "submitted"
        ? season.role
        : id === production.trashSeasonId
          ? "trash"
          : id === production.archiveSeasonId
            ? "archive"
            : id === production.submittedSeasonId
              ? "submitted"
              : undefined;
    normalizedSeasons[id] = {
      ...season,
      crewRoles: roles.length ? roles : undefined,
      role,
    };
  }

  const extras = collectCrewJobTitleExtras({
    production,
    seasons: normalizedSeasons,
    episodes: normalizedEpisodes,
    scenes: normalizedScenes,
    cues: repairedCues,
    comments: {},
  });
  if (
    extras.length !== (production.crewJobTitles?.length ?? 0) ||
    extras.some((t, i) => t !== production.crewJobTitles?.[i])
  ) {
    production = { ...production, crewJobTitles: extras };
  }

  const rawDeleted = (doc as ScriptDocument).deletedCueIds;
  const deletedCueIds =
    rawDeleted && typeof rawDeleted === "object"
      ? Object.fromEntries(
          Object.entries(rawDeleted).filter(
            (entry): entry is [string, string] =>
              typeof entry[0] === "string" &&
              typeof entry[1] === "string" &&
              entry[1].length > 0,
          ),
        )
      : undefined;

  // Drop cue bodies that are still tombstoned (stale payloads).
  let cuesAfterTombstones = repairedCues;
  if (deletedCueIds && Object.keys(deletedCueIds).length > 0) {
    cuesAfterTombstones = { ...repairedCues };
    for (const [id, delAt] of Object.entries(deletedCueIds)) {
      const cue = cuesAfterTombstones[id];
      if (cue && delAt >= cue.updatedAt) delete cuesAfterTombstones[id];
    }
  }

  let normalized: ScriptDocument = reconcileCueSceneMembership({
    production,
    seasons: normalizedSeasons,
    episodes: normalizedEpisodes,
    scenes: normalizedScenes,
    cues: cuesAfterTombstones,
    comments: normalizeComments(doc.comments),
    deletedCueIds:
      deletedCueIds && Object.keys(deletedCueIds).length > 0
        ? deletedCueIds
        : undefined,
  });

  // Align episode.sceneIds with scene.episodeId (repairs trash/sync zombies).
  const reconciledEpisodes = reconcileEpisodeSceneMembership(
    normalized.episodes,
    normalized.scenes,
  );
  if (reconciledEpisodes !== normalized.episodes) {
    normalized = { ...normalized, episodes: reconciledEpisodes };
  }

  // Backfill vault timestamps so retention still applies to older items.
  {
    let scenes = normalized.scenes;
    let stamped = false;
    for (const ep of listTrashEpisodes(normalized)) {
      for (const sceneId of ep.sceneIds) {
        const scene = scenes[sceneId];
        if (!scene || scene.trashedAt) continue;
        if (!stamped) {
          scenes = { ...scenes };
          stamped = true;
        }
        scenes[sceneId] = {
          ...scene,
          trashedAt: scene.updatedAt || nowIso(),
        };
      }
    }
    for (const ep of listArchiveEpisodes(normalized)) {
      for (const sceneId of ep.sceneIds) {
        const scene = scenes[sceneId];
        if (!scene || scene.archivedAt) continue;
        if (!stamped) {
          scenes = { ...scenes };
          stamped = true;
        }
        scenes[sceneId] = {
          ...scene,
          archivedAt: scene.updatedAt || nowIso(),
        };
      }
    }
    if (stamped) {
      normalized = { ...normalized, scenes };
    }
  }

  return purgeExpiredVaultItems(normalized);
}

function normalizeStatusSteps(raw: unknown): StatusStepDef[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return createDefaultStatusSteps();
  }
  const mapped = raw
    .map((s, i) => {
      const step = s as StatusStepDef;
      return {
        id: typeof step?.id === "string" && step.id ? step.id : createId(),
        label: typeof step?.label === "string" ? step.label : "",
        sortOrder: i,
      };
    })
    .filter((s) => s.label !== "Færdig")
    .map((s, i) => ({ ...s, sortOrder: i }));
  return migrateLegacyDefaultStatusSteps(mapped) ?? mapped;
}

function migrateLegacyDefaultStatusSteps(
  steps: StatusStepDef[],
): StatusStepDef[] | null {
  if (steps.length !== LEGACY_DEFAULT_STATUS_STEP_LABELS.length) return null;
  if (
    !steps.every(
      (step, i) => step.label === LEGACY_DEFAULT_STATUS_STEP_LABELS[i],
    )
  ) {
    return null;
  }
  const [
    research,
    manus,
    optagelse,
    nedklip,
    postmanus,
    intern,
    ekstern,
    lyd,
    grade,
  ] = steps;
  return [
    { ...research!, label: "Research", sortOrder: 0 },
    { ...manus!, label: "Manus", sortOrder: 1 },
    { ...optagelse!, label: "Optagelse", sortOrder: 2 },
    { ...nedklip!, label: "Nedklip", sortOrder: 3 },
    { ...postmanus!, label: "Sceneklip", sortOrder: 4 },
    { ...intern!, label: "Internt gennemsyn", sortOrder: 5 },
    { id: createId(), label: "Rettelser", sortOrder: 6 },
    { ...ekstern!, label: "Eksternt gennemsyn", sortOrder: 7 },
    { id: createId(), label: "Rettelser", sortOrder: 8 },
    { ...lyd!, label: "Lyd", sortOrder: 9 },
    { ...grade!, label: "Grade", sortOrder: 10 },
  ];
}

function normalizeProgramTexts(raw: unknown): ProgramText[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item, i) => {
    const t = item as ProgramText;
    return {
      id: typeof t?.id === "string" && t.id ? t.id : createId(),
      title: typeof t?.title === "string" ? t.title : "",
      body: typeof t?.body === "string" ? t.body : "",
      sortOrder: i,
    };
  });
}

function normalizeNoteColumns(raw: unknown): NoteColumnDef[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return createDefaultNoteColumns();
  }
  const cols = raw.map((c, i) => {
    const col = c as NoteColumnDef;
    return {
      id: typeof col?.id === "string" && col.id ? col.id : createId(),
      title:
        typeof col?.title === "string" && col.title.trim()
          ? col.title
          : "Noter",
      sortOrder: i,
    };
  });
  return cols.length > 0 ? cols : createDefaultNoteColumns();
}

function normalizeCueNotes(raw: unknown): Record<Id, string> | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const out: Record<Id, string> = {};
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === "string" && !isCueSideContentEmpty(value)) {
      out[id] = value;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Empty / missing → inherit production steps (undefined). */
function normalizeOptionalStatusSteps(
  raw: unknown,
): StatusStepDef[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  return normalizeStatusSteps(raw);
}

function normalizeCrewRoles(raw: unknown): SceneCrewRole[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((r, i) => {
    const role = r as SceneCrewRole;
    return {
      id: typeof role?.id === "string" && role.id ? role.id : createId(),
      jobTitle:
        typeof role?.jobTitle === "string" ? role.jobTitle.trim() : "",
      people: Array.isArray(role?.people)
        ? role.people.map((p) => ({
            id: typeof p?.id === "string" && p.id ? p.id : createId(),
            name: typeof p?.name === "string" ? p.name : "",
            detail:
              typeof p?.detail === "string" && p.detail.trim()
                ? p.detail.trimEnd()
                : undefined,
          }))
        : [],
      sortOrder: i,
    };
  });
}

function normalizeComments(raw: unknown): Record<Id, Comment> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<Id, Comment> = {};
  for (const [id, value] of Object.entries(raw as Record<string, Comment>)) {
    if (!value || typeof value !== "object") continue;
    const body = typeof value.body === "string" ? value.body.trim() : "";
    if (!body) continue;
    const cueId = value.cueId;
    if (!cueId) continue;
    const comment: Comment = {
      id: value.id || id,
      cueId,
      column:
        value.column === "visual" || value.column === "audio"
          ? value.column
          : undefined,
      authorId: value.authorId || "",
      authorName: value.authorName || "Ukendt",
      body,
      createdAt: value.createdAt || nowIso(),
      resolved: Boolean(value.resolved),
    };
    if (
      typeof value.from === "number" &&
      typeof value.to === "number" &&
      value.to > value.from
    ) {
      comment.from = value.from;
      comment.to = value.to;
      comment.quote =
        typeof value.quote === "string" && value.quote.trim()
          ? value.quote
          : undefined;
    }
    out[comment.id] = comment;
  }
  return out;
}
